import React, { useCallback, useState, useRef } from "react";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  useReactFlow,
  useUpdateNodeInternals,
  ReactFlowProvider,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useStyle } from "../../context/StyleContext";
import { Image, Video, Wand2, FolderOpen, Link, Upload, X, Trash2, Grid3x3, Film, LayoutGrid, ListVideo, Layers, Clapperboard, ScrollText, StickyNote, Frame, Bot, Loader2, Scissors } from "lucide-react";
import AtomNode from "./nodes/AtomNode";
import CanvasToolbar from "./CanvasToolbar";
import CanvasNodeListPanel from "./CanvasNodeListPanel";
import StoryboardEpisodeNode from "./nodes/StoryboardEpisodeNode";
import GroupFrameNode from "./nodes/GroupFrameNode";
import StickyNoteNode from "./nodes/StickyNoteNode";
import MotionStoryboardEditor from "./MotionStoryboardEditor";
import SketchEditorModal from "../common/SketchEditorModal";
import ResourceSelector from "../resource/ResourceSelector";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import StoryboardImportPanel from "./StoryboardImportPanel";
import useCanvasGeneration from "./useCanvasGeneration";
import { API_HOST } from "../../utils/constants";
import { authenticatedFetch } from "../../utils/api";
import { useToast } from "../../context/ToastContext";
import { submitTask, startTaskPolling, runImageTaskToUrl, runTaskToCompletion } from "../../services/taskService";
import { getNodeImageUrl } from "./canvasUtils";
import { getProjectDefaultVideoRatio, getProjectVideoProvider, getVideoAssetApiBase, supportsVideoAssetIngest } from "../../utils/projectRuntime";
import {
  applyAssetCreateResult,
  assetLibraryLabel,
  clearVideoAssetBinding,
  getVideoAssetFieldNames,
} from "../../utils/guaVideoAssetStatus";
import { pollVideoAssetUntilTerminal } from "../../utils/videoAssetPreflight";
import { normalizeNodeData } from "./utils/nodeNormalizer";
import { buildConnectedImageIndex, buildConnectedModelArkReferenceIndex, collectConnectedImages, needsConnectedImages, getNodeKind, isNodeType } from "./utils/nodeUtils";
import { getSeedance25ReferenceUrl } from '../../utils/seedance25Capabilities';
import useCanvasHistory, { snapshotGraph } from "./hooks/useCanvasHistory";
import { useSaveQueue } from "./utils/saveQueue";
import {
  backfillGenerationHistoryEntry,
  createSuccessfulGenerationPatch,
  getCurrentGenerationHistoryEntry,
  normalizeGenerationHistory,
  selectGenerationHistoryVersion,
} from "./utils/generationHistory";
import { getConnectionRejection, makeIsValidConnection } from "./utils/connectionRules";
import PaneContextMenu from "./menus/PaneContextMenu";
import NodeContextMenu from "./menus/NodeContextMenu";
import EdgeContextMenu from "./menus/EdgeContextMenu";
import { useDelayedUnmount } from "../../hooks/useDelayedUnmount";
import { useLastPresentValue } from "../../hooks/useLastPresentValue";
import { downloadFile } from "../../utils/fileUtils";
import {
  assertWanxImageInputLimit,
  getCurrentImageProvider,
  normalizeWanxImageQualityTier,
} from "../../utils/wanxImageCapabilities";

const nodeTypes = {
  atom: AtomNode,
  storyboardEpisode: StoryboardEpisodeNode,
  groupFrame: GroupFrameNode,
  stickyNote: StickyNoteNode,
};

// 边样式：reference = 实线（参考输入）；output = 实线 accent（产物输出），刷新后按 connectionType 恢复
// 自然曲线：贝塞尔（defaultEdgeOptions.type='default'），实线填充、圆角线帽，不用虚线/拐角
const EDGE_STYLE_UNIFIED = { stroke: 'var(--color-text-muted)', strokeWidth: 2, opacity: 0.6, strokeLinecap: 'round' };
const EDGE_STYLE_REFERENCE = EDGE_STYLE_UNIFIED;
const EDGE_STYLE_OUTPUT = { stroke: 'var(--color-accent-primary)', strokeWidth: 2, opacity: 0.8, strokeLinecap: 'round' };

// temp 节点 id 前缀（后端保存返回真实 id 前的本地占位）
const isTempNodeId = (id) => typeof id === 'string' && id.startsWith('node-');

// 智能匹配用：大小写/空白归一（"张 三" 与 "张三" 视为同名）
const normalizeMatchText = (s) => String(s || '').toLowerCase().replace(/\s+/g, '');
const STORYBOARD_ASSET_TYPES = new Set(['character', 'scene', 'prop']);
const STORYBOARD_GROUP_ROLE = 'storyboardSequence';
const STORYBOARD_GROUP_WIDTH = 380;
const STORYBOARD_GROUP_PADDING_X = 24;
const STORYBOARD_GROUP_PADDING_TOP = 48;
const STORYBOARD_GROUP_PADDING_BOTTOM = 24;
const STORYBOARD_SHOT_GAP = 18;
const STORYBOARD_SHOT_FALLBACK_HEIGHT = 340;

const uniqueNodesById = (nodes) => {
  const seen = new Set();
  return (nodes || []).filter((node) => {
    const id = node?.id != null ? String(node.id) : '';
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const sameStringArray = (left = [], right = []) => (
  left.length === right.length
  && left.every((value, index) => String(value) === String(right[index]))
);

const normalizeShotNameList = (value) => {
  const values = Array.isArray(value) ? value : (value ? [value] : []);
  return [...new Set(values.map((item) => String(item || '').trim()).filter(Boolean))];
};

const hasStructuredShotReferences = (shot = {}) => (
  ['characters', 'scenes', 'props', 'refCharacterNames', 'refSceneNames', 'refPropNames']
    .some((key) => Object.prototype.hasOwnProperty.call(shot, key))
);

const normalizeAssetIdentity = (value) => String(value || '')
  .normalize('NFKC')
  .trim()
  .toLocaleLowerCase()
  .replace(/\s+/g, ' ');

const escapeRegExp = (value) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// 英文资产名必须按完整词组命中，避免 of/off 等短词或单词片段误匹配。
const assetNameAppearsInText = (name, text) => {
  const assetName = String(name || '').trim();
  const sourceText = String(text || '');
  if (!assetName || !sourceText) return false;
  if (/^[\p{L}\p{N}\s._'-]+$/u.test(assetName) && /[A-Za-z]/.test(assetName)) {
    const pattern = assetName
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean)
      .map(escapeRegExp)
      .join('\\s+');
    return new RegExp(`(^|[^a-z0-9])${pattern}(?=$|[^a-z0-9])`, 'i').test(sourceText);
  }
  const normalizedName = normalizeMatchText(assetName);
  return normalizedName.length >= 2 && normalizeMatchText(sourceText).includes(normalizedName);
};

// 剧本转分镜任务结果解析：数组原样返回；{shots/masterShots/...} 取数组字段；字符串尝试 JSON.parse + 正则兜底
const parseShotArrayResult = (result) => {
  let value = result;
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch { value = result; }
  }
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    for (const key of ['shots', 'masterShots', 'connectingShots', 'storyboard', 'value', 'result', 'data']) {
      if (Array.isArray(value[key])) return value[key];
    }
    // 任务系统包装形态：result.resultPayload 是 JSON 字符串（{"taskType":"...","result":[...]}）
    if (typeof value.resultPayload === 'string') {
      const inner = parseShotArrayResult(value.resultPayload);
      if (inner.length) return inner;
    }
  }
  if (typeof result === 'string') {
    const match = result.match(/\[[\s\S]*\]/);
    if (match) {
      try {
        const arr = JSON.parse(match[0]);
        return Array.isArray(arr) ? arr : [];
      } catch { /* 解析失败按空处理，由调用方提示 */ }
    }
  }
  return [];
};

// 菜单视口钳位：防止在屏幕/画布边缘右键时菜单溢出（x/y 为视口坐标）
const clampMenuToViewport = (x, y, estW = 200, estH = 300) => ({
  x: Math.max(8, Math.min(x, window.innerWidth - estW - 8)),
  y: Math.max(8, Math.min(y, window.innerHeight - estH - 8)),
});

// 从节点 data 中剔除函数回调与派生属性，得到可持久化的数据
const NON_SERIALIZABLE_KEYS = [
  'normalized',
  'onImageClick', 'onToggleExpand', 'onUpdate', 'onGenerate', 'onRegeneratePrompts', 'onExportResult',
  'onSplitGrid', 'onOpenEditor', 'connectedImages', 'connectedModelArkReferences', 'expanded', 'regeneratingCells', 'upscalingCells',
  'onSmartMatch', 'onGenerateMasterShots', 'onGenerateConnectingShots', 'onCreateBlankStoryboardShot',
  'onSyncShotReferences', 'onRemoveReference', 'smartMatching', 'syncingShotReferences',
  'onArrangeStoryboard', 'onResumeStoryboardTask',
];
const stripNonSerializable = (data) =>
  Object.fromEntries(Object.entries(data).filter(([k]) => !NON_SERIALIZABLE_KEYS.includes(k)));

const GENERATION_BINDING_FIELDS = [
  'resourceId', 'originalVideoUrl', 'assetProvider',
  'guaVideoAssetId', 'guaVideoAssetStatus',
  'engGuaVideoAssetId', 'engGuaVideoAssetStatus',
  'guaOminiAssetId', 'guaOminiAssetStatus',
  'dreaminaProxyAssetId', 'dreaminaProxyAssetStatus',
];

// 画布撤销只切换当前展示版本，已经产生并付费的生成记录必须继续保留。
const mergeDurableGenerationHistory = (snapshotData = {}, currentData = {}) => {
  const entries = [
    ...normalizeGenerationHistory(currentData),
    ...normalizeGenerationHistory(snapshotData),
  ];
  if (entries.length === 0) return [];
  const seen = new Set();
  return entries.filter((entry) => {
    const identity = entry.taskId
      ? `task:${entry.taskId}`
      : `entry:${entry.id || `${entry.url}|${entry.createdAt}`}`;
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  }).slice(0, 20);
};

const buildStoryboardDraftData = (shot = {}) => ({
  draftStatus: shot.draftStatus || 'rough',
  storyPurpose: shot.storyPurpose || '',
  cameraAngle: shot.cameraAngle || '平视',
  cameraMovement: shot.cameraMovement || '固定',
  composition: shot.composition || '',
  subjectPositions: shot.subjectPositions || '',
  axisType: shot.axisType || '自由或静态',
  axisSide: shot.axisSide || '待定',
  screenDirection: shot.screenDirection || '不适用',
  lookDirection: shot.lookDirection || '',
  axisStatus: shot.axisStatus || '需复核',
  axisReason: shot.axisReason || '',
  informationControl: shot.informationControl || '',
  sound: shot.sound || '',
  continuity: shot.continuity || '',
  notes: shot.notes || '',
});

const NodeCanvasInner = ({ canvasId, projectId, episodeId, nodes: initialNodes, connections, onSaveNode, onDeleteNode, onSaveConnection }) => {
  const toast = useToast();
  const { currentStyle } = useStyle();
  const { screenToFlowPosition, fitView, getNodes } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();

  const [contextMenu, setContextMenu] = useState(null);
  const [hoveredSubmenu, setHoveredSubmenu] = useState(null);
  const reactFlowWrapper = useRef(null);
  const [nodeIdCounter, setNodeIdCounter] = useState(1);
  const [selectedNodes, setSelectedNodes] = useState([]); // 多选节点
  const [isSpacePressed, setIsSpacePressed] = useState(false); // 空格键状态
  const [showNodeList, setShowNodeList] = useState(false); // 左侧滑入的全节点导航
  const [showResourceLibrary, setShowResourceLibrary] = useState(false);
  const [showUrlDialog, setShowUrlDialog] = useState(false);
  const [importPanelSource, setImportPanelSource] = useState(null); // 导入面板来源：null | 'storyboard' | 'mini' | 'board'
  const [showGenerateTypeMenu, setShowGenerateTypeMenu] = useState(null); // { x, y, sourceNodeIds, position }
  const [sketchEditor, setSketchEditor] = useState(null); // { aspectRatio } - 草图编辑器
  const [motionEditor, setMotionEditor] = useState(null); // { nodeId, nodeData } - 动态分镜编辑器
  const [imagePreview, setImagePreview] = useState(null); // { url, name, mediaType } - 图片/视频预览
  const [nodeContextMenu, setNodeContextMenu] = useState(null); // { x, y, node } - 节点右键菜单
  const [roughCutConfirm, setRoughCutConfirm] = useState(null); // { images: [{url, name}] } - 批量加入粗剪台确认
  const [batchDeleteConfirm, setBatchDeleteConfirm] = useState(null); // { nodeIds: [] } - 批量删除确认
  const [edgeContextMenu, setEdgeContextMenu] = useState(null); // { x, y, edge } - 连线右键菜单
  const [splitGridConfirm, setSplitGridConfirm] = useState(null); // { node, rows, cols, busy } - 宫格拆分确认弹窗

  // 出场动画：生成类型菜单 / URL 对话框 / 导入面板 / 图片预览延迟卸载
  const genTypeMenuAnim = useDelayedUnmount(!!showGenerateTypeMenu, 150);
  const urlDialogAnim = useDelayedUnmount(showUrlDialog, 150);
  const importPanelAnim = useDelayedUnmount(!!importPanelSource, 150);
  const imagePreviewAnim = useDelayedUnmount(!!imagePreview, 150);
  const effectiveGenTypeMenu = useLastPresentValue(showGenerateTypeMenu);
  const effectiveImportPanelSource = useLastPresentValue(importPanelSource);
  const effectiveImagePreview = useLastPresentValue(imagePreview);
  const isVideoPreview = effectiveImagePreview?.mediaType === 'video'
    || /\.(mp4|mov|webm|m4v)(\?|#|$)/i.test(effectiveImagePreview?.url || '');
  const fileInputRef = useRef(null);
  const isUploadingRef = useRef(false); // 上传中 flag 守卫（禁止重复选择）
  const isInitializing = useRef(true); // 标记是否正在初始化
  const connectingFrom = useRef(null); // 记录正在连接的源节点
  const handleUpdateNodeRef = useRef(null); // 稳定引用，避免闭包过期
  const committedNodeIdsRef = useRef(new Map()); // temp id → 后端真实 id，供异步生成回调追踪
  const handleNodeGenerateRef = useRef(null);
  const handleRegeneratePromptsRef = useRef(null);
  const handleExportResultRef = useRef(null); // “导出为素材节点”回调（经 spawnGeneratedResource，带幂等查重）
  const handleSmartMatchRef = useRef(null); // 剧本转分镜：智能匹配参考资产
  const handleGenerateMasterShotsRef = useRef(null); // 剧本转分镜：生成主镜
  const handleGenerateConnectingShotsRef = useRef(null); // 剧本转分镜：生成连接镜
  const handleCreateBlankStoryboardShotRef = useRef(null); // 剧本转分镜：人工新建空白镜头
  const handleSyncShotReferencesRef = useRef(null); // 剧本转分镜：为已有主镜/连接镜同步参考资产
  const handleRemoveReferenceRef = useRef(null); // 从节点参考缩略图解除参考关系
  const handleArrangeStoryboardRef = useRef(null); // 剧本转分镜：纵向整理成组
  const handleResumeStoryboardTaskRef = useRef(null); // 剧本转分镜：恢复任务查询
  const storyboardPollingStopsRef = useRef(new Map());
  const storyboardSubmittingRef = useRef(new Set());
  const storyboardRecoveryDoneRef = useRef(false);
  const canvasGenerationRecoveryDoneRef = useRef(false);
  const removeReferenceFromNode = useCallback((targetNodeId, reference) => {
    handleRemoveReferenceRef.current?.(targetNodeId, reference);
  }, []);

  // 初始化完成后设置标志
  React.useEffect(() => {
    let timer;
    if (initialNodes.length > 0 || connections.length > 0) {
      // 延迟设置，确保 ReactFlow 完成初始化
      timer = setTimeout(() => {
        isInitializing.current = false;
      }, 500);
    } else {
      isInitializing.current = false;
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [initialNodes.length, connections.length]);

  // 转换节点格式为 React Flow 格式
  const convertToReactFlowNodes = useCallback((nodes) => {
    const converted = nodes.map((node) => {
      let nodeData;
      try {
        nodeData = JSON.parse(node.data || "{}");
      } catch (e) {
        console.warn(`[Canvas] 节点 ${node.id} 数据解析失败，已回退为空对象:`, e);
        nodeData = {};
      }

      // 根据 nodeType 判断节点类型
      // 优先使用后端的 nodeType 字段
      let type = node.nodeType;

      // 如果后端没有 nodeType，则根据数据特征判断
      if (!type || type === "resource") {
        // 优先检查 resourceType，有 resourceType 的一定是资源节点
        if (nodeData.resourceType) {
          type = "resource";
        }
        // 分镜导入节点
        else if (nodeData.nodeKind === "storyboardShot") {
          type = "storyboardShot";
        }
        // 分镜生成节点（有 prompts 数组）
        else if (nodeData.nodeKind === "storyboardGrid") {
          type = "storyboardGrid";
        }
        // Seedance 片段合并节点
        else if (nodeData.nodeKind === "seedanceMerge") {
          type = "seedanceMerge";
        }
        // 整集分镜列表节点
        else if (nodeData.nodeKind === "storyboardEpisode") {
          type = "storyboardEpisode";
        }
        // 剧本转分镜节点
        else if (nodeData.nodeKind === "scriptToStoryboard") {
          type = "scriptToStoryboard";
        }
        // AI 导演对话节点
        else if (nodeData.nodeKind === "aiDirector") {
          type = "aiDirector";
        }
        // 旧 prompt 节点 fallback 到 resource
        else if (nodeData.generateType) {
          type = "generate";
        } else {
          type = "resource";
        }
      }

      // resource / generate / storyboardShot / seedanceMerge / motionStoryboard / scriptToStoryboard / aiDirector 走 atom
      const ATOM_LEGACY = new Set(["resource", "generate", "storyboardShot", "seedanceMerge", "motionStoryboard", "storyboardGrid", "scriptToStoryboard", "aiDirector"]);
      const useAtom = ATOM_LEGACY.has(type);
      const normalized = useAtom ? normalizeNodeData(nodeData, type) : null;
      const renderType = useAtom ? "atom" : type;

      // 片4：分组父子关系持久化 — data.parentId 恢复为 ReactFlow parentId + extent:'parent'
      // （子节点 position 存的就是相对父节点的坐标，直接恢复即可）
      const parentId = nodeData.parentId || undefined;
      const isFrame = renderType === "groupFrame";

      return {
        id: node.id.toString(),
        type: renderType,
        position: { x: node.positionX, y: node.positionY },
        ...(parentId ? { parentId, extent: "parent" } : {}),
        // 分组框：固定置于底层（zIndex -1），尺寸持久化在 data.frameWidth/frameHeight
        ...(isFrame ? {
          zIndex: -1,
          style: { width: nodeData.frameWidth || 480, height: nodeData.frameHeight || 360 },
        } : {}),
        data: {
          ...nodeData,
          projectId,
          episodeId,
          normalized,
          legacyType: type,
          onImageClick: (url, name, mediaType) => {
            setImagePreview({ url, name, mediaType });
          },
          onToggleExpand: (nodeId) => toggleNodeExpandRef.current?.(nodeId),
          // 所有节点都需要 onUpdate/onGenerate（atom 编辑面板统一使用）
          onUpdate: (nodeId, newData) => handleUpdateNodeRef.current?.(nodeId, newData),
          onGenerate: (nodeId, formData) => handleNodeGenerateRef.current?.(nodeId, formData),
          onExportResult: (nodeId) => handleExportResultRef.current?.(nodeId),
          onRemoveReference: removeReferenceFromNode,
          // 剧本转分镜节点专用回调
          ...(type === "scriptToStoryboard" ? {
            onSmartMatch: (nodeId) => handleSmartMatchRef.current?.(nodeId),
            onGenerateMasterShots: (nodeId, opts) => handleGenerateMasterShotsRef.current?.(nodeId, opts),
            onGenerateConnectingShots: (nodeId) => handleGenerateConnectingShotsRef.current?.(nodeId),
            onCreateBlankStoryboardShot: (nodeId, opts) => handleCreateBlankStoryboardShotRef.current?.(nodeId, opts),
            onSyncShotReferences: (nodeId) => handleSyncShotReferencesRef.current?.(nodeId),
            onArrangeStoryboard: (nodeId) => handleArrangeStoryboardRef.current?.(nodeId),
            onResumeStoryboardTask: (nodeId) => handleResumeStoryboardTaskRef.current?.(nodeId),
          } : {}),
          // 容器节点专用回调
          ...(type === "storyboardGrid" || type === "storyboardShot" || type === "seedanceMerge" || type === "storyboardEpisode" ? {
            onRegeneratePrompts: (nodeId, formData) => handleRegeneratePromptsRef.current?.(nodeId, formData),
            onSplitGrid: (nodeId, params) => handleSplitGridRef.current?.(nodeId, params),
          } : {}),
          // motionStoryboard 节点回调
          ...(type === "motionStoryboard" ? {
            onOpenEditor: (nodeId) => {
              const node = getNodes().find(n => n.id === nodeId);
              if (node) setMotionEditor({ nodeId, nodeData: node.data });
            },
            onUpdate: (nodeId, newData) => handleUpdateNodeRef.current?.(nodeId, newData),
          } : {}),
        },
      };
    });
    // ReactFlow 父子机制要求父节点排在子节点之前（分组框提前）
    return [
      ...converted.filter((n) => n.type === "groupFrame"),
      ...converted.filter((n) => n.type !== "groupFrame"),
    ];
  }, [episodeId, getNodes, projectId, removeReferenceFromNode]);

  // 转换连接格式为 React Flow 格式（方向 source→target = 数据流向；output 边恢复虚线样式）
  const convertToReactFlowEdges = (connections) => {
    return connections.map((conn) => {
      const isOutput = (conn.connectionType || "reference") === "output";
      return {
        id: conn.id.toString(),
        source: conn.sourceNodeId.toString(),
        target: conn.targetNodeId.toString(),
        sourceHandle: conn.sourcePort === "default" ? null : conn.sourcePort,
        targetHandle: conn.targetPort === "default" ? null : conn.targetPort,
        data: { connectionType: conn.connectionType || "reference" },
        style: isOutput ? EDGE_STYLE_OUTPUT : EDGE_STYLE_REFERENCE,
        animated: false,
        deletable: true,
      };
    });
  };

  const [nodes, setNodes, onNodesChange] = useNodesState(
    convertToReactFlowNodes(initialNodes)
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    convertToReactFlowEdges(connections)
  );
  // 最新状态 refs（供历史快照、异步生成与保存队列读取，避免 stale closure）。
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  React.useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  React.useEffect(() => { edgesRef.current = edges; }, [edges]);

  // 同一时间只展开一个节点，只重算发生变化的节点尺寸。
  const setExpandedNode = useCallback((nodeId, mode = 'open') => {
    setNodes((nds) => {
      const targetId = nodeId == null ? null : String(nodeId);
      const target = targetId ? nds.find((node) => node.id === targetId) : null;
      const closeTarget = mode === 'toggle' && !!target?.data?.expanded;
      const changedIds = [];
      let changed = false;
      const next = nds.map((node) => {
        const nextExpanded = targetId
          ? (node.id === targetId ? !closeTarget : false)
          : false;
        if (!!node.data?.expanded === nextExpanded) return node;
        changed = true;
        changedIds.push(node.id);
        return { ...node, data: { ...node.data, expanded: nextExpanded } };
      });
      if (!changed) return nds;
      nodesRef.current = next;
      setTimeout(() => changedIds.forEach((id) => updateNodeInternals(id)), 50);
      return next;
    });
  }, [setNodes, updateNodeInternals]);

  const expandNode = useCallback((nodeId) => {
    setExpandedNode(nodeId, 'open');
  }, [setExpandedNode]);

  // 左侧节点列表导航：选中并平滑聚焦，不修改节点在画布中的真实位置。
  const locateNodeFromList = useCallback((nodeId) => {
    const targetId = String(nodeId);
    const targetNode = nodesRef.current.find((node) => String(node.id) === targetId);
    if (!targetNode) return;

    setNodes((current) => {
      let changed = false;
      const next = current.map((node) => {
        const selected = String(node.id) === targetId;
        if (!!node.selected === selected) return node;
        changed = true;
        return { ...node, selected };
      });
      if (!changed) return current;
      nodesRef.current = next;
      return next;
    });
    setSelectedNodes([targetNode.id]);
    setEdges((current) => {
      let changed = false;
      const next = current.map((edge) => {
        if (!edge.selected) return edge;
        changed = true;
        return { ...edge, selected: false };
      });
      if (!changed) return current;
      edgesRef.current = next;
      return next;
    });

    window.requestAnimationFrame(() => {
      fitView({
        nodes: [targetNode],
        padding: 0.7,
        duration: 320,
        minZoom: 0.35,
        maxZoom: 1.2,
      });
    });
  }, [fitView, setEdges, setNodes]);

  const openNodeFromList = useCallback((nodeId) => {
    locateNodeFromList(nodeId);
    setExpandedNode(nodeId, 'open');
    // 展开会改变节点尺寸；等 React Flow 重算 internals 后再聚焦，避免展开区被视口裁掉。
    window.setTimeout(() => {
      const targetNode = nodesRef.current.find((node) => String(node.id) === String(nodeId));
      if (!targetNode) return;
      fitView({
        nodes: [targetNode],
        padding: 0.7,
        duration: 320,
        minZoom: 0.35,
        maxZoom: 1.2,
      });
    }, 80);
  }, [fitView, locateNodeFromList, setExpandedNode]);

  const toggleNodeExpandRef = React.useRef(null);
  toggleNodeExpandRef.current = (nodeId) => {
    setExpandedNode(nodeId, 'toggle');
  };

  // 监听 initialNodes 变化，重新转换并添加 onImageClick 回调
  React.useEffect(() => {
    if (initialNodes.length === 0) return;

    const convertedNodes = convertToReactFlowNodes(initialNodes);
    nodesRef.current = convertedNodes;
    setNodes(convertedNodes);
  }, [convertToReactFlowNodes, initialNodes, setNodes]);

  // 收集某个节点通过连线关联的所有参考图（已抽离至 utils/nodeUtils.js）
  // collectConnectedImages 现从 utils 导入

  // 为 generate / storyboardGrid / storyboardShot 节点注入 connectedImages
  // 仅当连线或上游图片身份变化时更新，避免目标节点自身编辑触发全图重复扫描。
  const connectedSourceSignature = React.useMemo(() => {
    const sourceIds = new Set(
      edges
        .filter((edge) => edge.data?.connectionType === 'reference')
        .map((edge) => String(edge.source))
    );
    return nodes
      .filter((node) => sourceIds.has(String(node.id)))
      .map((node) => [
        String(node.id),
        getNodeImageUrl(node.data) || '',
        getSeedance25ReferenceUrl(node.data) || '',
        node.data?.resourceType || node.data?.generateType || '',
        node.data?.name || '',
        node.data?.assetType || '',
      ].join('\u0001'))
      .join('\u0002');
  }, [edges, nodes]);

  React.useEffect(() => {
    setNodes((nds) => {
      const connectedImageIndex = buildConnectedImageIndex(edges, nds);
      const modelArkReferenceIndex = buildConnectedModelArkReferenceIndex(edges, nds);
      let changed = false;
      const next = nds.map((n) => {
        if (!needsConnectedImages(getNodeKind(n))) return n;
        const connectedImages = connectedImageIndex.get(String(n.id)) || [];
        const connectedModelArkReferences = modelArkReferenceIndex.get(String(n.id)) || [];
        const prev = n.data.connectedImages || [];
        // 浅比较：同 URL 的不同资源也必须按来源节点/连线区分。
        const isSame = prev.length === connectedImages.length &&
          connectedImages.every((img, i) => (
            prev[i]?.url === img.url
            && prev[i]?.sourceNodeId === img.sourceNodeId
            && sameStringArray(prev[i]?.edgeIds || [], img.edgeIds || [])
          ));
        const hasRemoveCallback = n.data.onRemoveReference === removeReferenceFromNode;
        const sameLibraryReferences = JSON.stringify(n.data.connectedModelArkReferences || []) === JSON.stringify(connectedModelArkReferences);
        if (isSame && hasRemoveCallback && sameLibraryReferences) return n;
        changed = true;
        return {
          ...n,
          data: {
            ...n.data,
            connectedImages,
            connectedModelArkReferences,
            onRemoveReference: removeReferenceFromNode,
          },
        };
      });
      if (!changed) return nds;
      nodesRef.current = next;
      return next;
    });
  }, [connectedSourceSignature, edges, removeReferenceFromNode, setNodes]);

  // 为 scriptToStoryboard 节点同步其主镜节点 id 列表（驱动面板"生成连接镜"可用态与计数；
  // 主镜节点被删除后自动回收，避免面板计数过期）
  const storyboardTopologySignature = React.useMemo(() => nodes
    .filter((node) => (
      getNodeKind(node) === 'scriptToStoryboard'
      || (node.data?.sourceScriptNodeId && node.data?.masterType)
    ))
    .map((node) => [
      String(node.id),
      getNodeKind(node),
      node.data?.sourceScriptNodeId || '',
      node.data?.masterType || '',
      node.data?.shotNumber || '',
    ].join('\u0001'))
    .join('\u0002'), [nodes]);

  React.useEffect(() => {
    setNodes((nds) => {
      const idsByScript = new Map();
      nds.forEach((node) => {
        const scriptNodeId = node.data?.sourceScriptNodeId;
        if (!scriptNodeId || !node.data?.masterType) return;
        const shotNodes = idsByScript.get(String(scriptNodeId)) || [];
        shotNodes.push(node);
        idsByScript.set(String(scriptNodeId), shotNodes);
      });
      idsByScript.forEach((shotNodes) => {
        shotNodes.sort((a, b) => (a.data?.shotNumber || 0) - (b.data?.shotNumber || 0));
      });
      let changed = false;
      const next = nds.map((n) => {
        if (getNodeKind(n) !== 'scriptToStoryboard') return n;
        const ids = (idsByScript.get(String(n.id)) || []).map((m) => m.id);
        const prev = Array.isArray(n.data.masterShotNodeIds) ? n.data.masterShotNodeIds : [];
        const isSame = prev.length === ids.length && ids.every((v, i) => prev[i] === v);
        if (isSame) return n;
        changed = true;
        return { ...n, data: { ...n.data, masterShotNodeIds: ids } };
      });
      if (changed) nodesRef.current = next;
      return changed ? next : nds;
    });
  }, [storyboardTopologySignature, setNodes]);

  // ── P2/P3 基础设施 ─────────────────────────────────────────────
  // P2-3 防抖批量保存 + temp 连线队列
  const saveQueue = useSaveQueue({
    canvasId,
    onSaveNode,
    onSaveConnection,
    getNodes,
    stripData: stripNonSerializable,
  });

  // P2-2 撤销/重做
  const history = useCanvasHistory();
  // 离散动作发生“前”调用：压入当前快照
  const recordNow = useCallback(() => {
    history.record(snapshotGraph(nodesRef.current, edgesRef.current, stripNonSerializable));
  }, [history]);

  // P2-4 连线类型化校验（连接过程中合法目标 Handle 高亮）
  const isValidConnection = React.useMemo(() => makeIsValidConnection(() => nodesRef.current), []);

  // 拖拽历史记录标志（整段拖拽只记一步）
  const dragRecordedRef = useRef(false);

  // 统一连线持久化：任一端 temp → 入队等真实 id；否则立即 POST
  const persistConnection = useCallback(async ({ source, target, sourceHandle = null, targetHandle = null, connectionType = 'reference' }) => {
    if (isTempNodeId(source) || isTempNodeId(target)) {
      saveQueue.queueConnection({ source, target, sourceHandle, targetHandle, connectionType });
      return;
    }
    try {
      await onSaveConnection({
        canvasId,
        sourceNodeId: source,
        targetNodeId: target,
        sourcePort: sourceHandle || "default",
        targetPort: targetHandle || "default",
        connectionType,
      });
    } catch (error) {
      console.error("保存连接失败:", error);
    }
  }, [canvasId, onSaveConnection, saveQueue]);

  // temp 节点拿到真实 id：回写节点 id、重映射本地连线、补存排队连线
  // （若 temp 节点是分组框，其子节点的 parentId 一并重映射并标脏补存）
  const commitTempNodeId = useCallback((tempId, realId) => {
    if (!realId || realId === tempId) return;
    committedNodeIdsRef.current.set(String(tempId), String(realId));
    const childIds = nodesRef.current
      .filter((n) => n.parentId === tempId || n.data?.parentId === tempId)
      .map((n) => n.id);
    setNodes((nds) => {
      const next = nds.map((n) => {
        if (n.id === tempId) return { ...n, id: realId };
        if (n.parentId === tempId || n.data?.parentId === tempId) {
          return {
            ...n,
            ...(n.parentId === tempId ? { parentId: realId } : {}),
            data: { ...n.data, ...(n.data?.parentId === tempId ? { parentId: realId } : {}) },
          };
        }
        return n;
      });
      nodesRef.current = next;
      return next;
    });
    setEdges((eds) => {
      const nextEdges = eds.map((e) => {
      const next = { ...e };
      if (next.source === tempId) next.source = realId;
      if (next.target === tempId) next.target = realId;
      return next;
      });
      edgesRef.current = nextEdges;
      return nextEdges;
    });
    // 子节点 parentId 已变化：标脏走防抖队列补存（flush 时从最新状态构建 payload）
    // temp 阶段可能已经收到生成进度/结果；真实 id 到位后补标脏，确保这些数据不会只留在内存。
    saveQueue.markDirty(realId);
    childIds.forEach((cid) => saveQueue.markDirty(cid));
    saveQueue.flushConnectionsFor(tempId, realId);
  }, [setNodes, setEdges, saveQueue]);

  // 片4 分组框删除/移除时：子节点脱离分组（parentId 清除，相对坐标换算回绝对坐标），并标脏补存
  const orphanChildrenOf = useCallback((frameIds, { excludeIds = [] } = {}) => {
    const frames = nodesRef.current.filter((n) => frameIds.includes(n.id));
    const exclude = new Set(excludeIds);
    const affected = [];
    setNodes((nds) => nds.map((n) => {
      if (exclude.has(n.id)) return n;
      const fid = (n.parentId && frameIds.includes(n.parentId)) ? n.parentId
        : (n.data?.parentId && frameIds.includes(n.data.parentId)) ? n.data.parentId
        : null;
      if (!fid) return n;
      const frame = frames.find((f) => f.id === fid);
      const absPos = frame
        ? { x: frame.position.x + n.position.x, y: frame.position.y + n.position.y }
        : n.position;
      const data = { ...n.data };
      delete data.parentId;
      affected.push(n.id);
      return { ...n, parentId: undefined, extent: undefined, position: absPos, data };
    }));
    affected.forEach((nid) => saveQueue.markDirty(nid));
    if (affected.length > 0) saveQueue.flush();
  }, [setNodes, saveQueue]);

  // P2-1 生成器产出独立素材节点（身份不突变）+ P3-1 自动入资源中心
  const spawnGeneratedResource = useCallback(async (sourceNodeId, { resourceType, url, name, thumbnail, duration, extraData = {} }) => {
    if (!url) return;
    // spawn 幂等：同来源节点 + 同 URL 的产物节点已存在时直接返回
    // （杜绝双击穿透/重复回调导致的双节点；旧数据无 sourceNodeId 字段，不影响新数据查重）
    const alreadySpawned = nodesRef.current.some(
      (n) => n.data?.sourceNodeId === sourceNodeId && n.data?.url === url
    );
    if (alreadySpawned) return;
    recordNow(); // 整个 spawn 算一步

    const sourceNode = nodesRef.current.find((n) => n.id === sourceNodeId);
    const basePos = sourceNode?.position || { x: 100, y: 100 };
    // 生成节点右侧 x+320；与已有节点重叠则逐级下移避让
    let position = { x: basePos.x + 320, y: basePos.y };
    const collides = (pos) => nodesRef.current.some(
      (n) => Math.abs(n.position.x - pos.x) < 60 && Math.abs(n.position.y - pos.y) < 60
    );
    let guard = 0;
    while (collides(position) && guard < 20) {
      position = { x: position.x, y: position.y + 120 };
      guard++;
    }

    const nodeData = {
      resourceType,
      name,
      url,
      thumbnail: thumbnail || url,
      ...(duration != null ? { duration } : {}),
      source: 'canvas-generate',
      sourceNodeId, // 追溯来源生成节点（同时用于 spawn 幂等查重）
      ...extraData,
    };

    const tempId = `node-${nodeIdCounter}`;
    setNodeIdCounter((c) => c + 1);
    const newNode = {
      id: tempId,
      type: "atom",
      position,
      data: {
        ...nodeData,
        legacyType: "resource",
        normalized: normalizeNodeData(nodeData, "resource"),
        onImageClick: (u, n, mediaType) => setImagePreview({ url: u, name: n, mediaType }),
        onToggleExpand: (nid) => toggleNodeExpandRef.current?.(nid),
        onUpdate: (nid, nd) => handleUpdateNodeRef.current?.(nid, nd),
        onGenerate: (nid, fd) => handleNodeGenerateRef.current?.(nid, fd),
        onExportResult: (nid) => handleExportResultRef.current?.(nid),
      },
    };
    setNodes((nds) => {
      const next = [...nds, newNode];
      nodesRef.current = next;
      return next;
    });

    // output 虚线边（先本地，真实 id 到位后持久化）
    const edge = {
      id: `e-${sourceNodeId}-${tempId}`,
      source: sourceNodeId,
      target: tempId,
      data: { connectionType: 'output' },
      style: EDGE_STYLE_OUTPUT,
      animated: false,
      deletable: true,
    };
    setEdges((eds) => [...eds, edge]);

    let realId = tempId;
    try {
      const savedNode = await onSaveNode({
        canvasId,
        nodeType: "resource",
        positionX: position.x,
        positionY: position.y,
        data: JSON.stringify(nodeData),
      });
      if (savedNode?.id) {
        realId = savedNode.id.toString();
        commitTempNodeId(tempId, realId);
      }
    } catch (e) {
      console.error('[Canvas] 保存生成结果节点失败:', e);
    }

    // 连线持久化（temp 端走队列）
    await persistConnection({
      source: sourceNodeId,
      target: realId,
      sourceHandle: 'right',
      targetHandle: 'left',
      connectionType: 'output',
    });

    // P3-1：自动写入资源中心（失败不阻塞节点创建）
    try {
      const saved = await authenticatedFetch(`${API_HOST}/api/resource/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url,
          projectId: projectId || localStorage.getItem('currentProjectId'),
          episodeId: episodeId || null,
          name: name || 'AI生成',
          typeCode: resourceType === 'video' ? 'agent_video' : 'agent_image',
          source: 'canvas_generate',
        }),
      });
      if (saved?.id && !isTempNodeId(realId)) {
        handleUpdateNodeRef.current?.(realId, { resourceId: saved.id });
      }
    } catch (e) {
      console.warn('[Canvas] 生成结果写入资源中心失败:', e);
      toast.warning('已生成素材节点，但写入资源中心失败');
    }
  }, [recordNow, nodeIdCounter, setNodes, setEdges, onSaveNode, commitTempNodeId, persistConnection, canvasId, projectId, episodeId, toast]);

  // P3-1 手动补存：resource 且尚无 resourceId 的节点 → 保存到资源中心
  const handleSaveToResourceCenter = useCallback(async (node) => {
    const d = node.data || {};
    const targetHistoryEntry = getCurrentGenerationHistoryEntry(d);
    const fallbackUrl = getNodeImageUrl(d);
    const url = d.currentGenerationHistoryId && targetHistoryEntry?.url
      ? targetHistoryEntry.url
      : fallbackUrl;
    const targetHistoryEntryId = targetHistoryEntry?.url === url
      ? targetHistoryEntry.id
      : null;
    if (!url) { toast.warning('该节点没有可保存的内容'); return; }
    const rt = d.resourceType || 'image';
    const typeCode = rt === 'video' ? 'agent_video' : rt === 'audio' ? 'agent_audio' : 'agent_image';
    try {
      const saved = await authenticatedFetch(`${API_HOST}/api/resource/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url,
          projectId: projectId || localStorage.getItem('currentProjectId'),
          episodeId: episodeId || null,
          name: d.name || d.prompt || '画布素材',
          typeCode,
          source: 'canvas',
        }),
      });
      if (saved?.id) {
        handleUpdateNodeRef.current?.(node.id, (previousData) => {
          if (!targetHistoryEntryId) return { ...previousData, resourceId: saved.id };
          return {
            ...previousData,
            ...backfillGenerationHistoryEntry(
              previousData,
              targetHistoryEntryId,
              { resourceId: saved.id },
            ),
          };
        });
        toast.success('已保存到资源中心');
      } else {
        toast.warning('资源中心未返回资源 ID');
      }
    } catch (e) {
      console.warn('[Canvas] 保存到资源中心失败:', e);
      toast.warning('保存到资源中心失败，请稍后重试');
    }
  }, [projectId, episodeId, toast]);

  // 图片入库：素材库由项目绑定的视频服务唯一决定，各厂商素材 ID 互不复用。
  const handleIngestNodeImage = useCallback(async (node) => {
    const d = node.data || {};
    const targetHistoryEntry = getCurrentGenerationHistoryEntry(d);
    const fallbackUrl = getNodeImageUrl(d);
    const url = d.currentGenerationHistoryId && targetHistoryEntry?.url
      ? targetHistoryEntry.url
      : fallbackUrl;
    // 入库可能轮询数十秒，必须锁定发起时的版本；期间切换版本不能把 A 的 AssetId 写到 B。
    // 旧节点若展示 URL 与合成历史不一致，则只回填顶层，避免给错误历史项绑定 AssetId。
    const targetHistoryEntryId = targetHistoryEntry?.url === url
      ? targetHistoryEntry.id
      : null;
    const updateAssetState = (updates) => {
      handleUpdateNodeRef.current?.(node.id, (previousData) => {
        if (!targetHistoryEntryId) return { ...previousData, ...updates };
        return {
          ...previousData,
          ...backfillGenerationHistoryEntry(previousData, targetHistoryEntryId, updates),
        };
      });
    };
    if (!url) { toast.warning('该节点没有可入库的图片'); return; }
    const pid = projectId || localStorage.getItem('currentProjectId');
    if (!pid) { toast.warning('缺少项目 ID，无法入库'); return; }
    const provider = getProjectVideoProvider(pid);
    const apiBase = getVideoAssetApiBase(provider);
    const assetFields = getVideoAssetFieldNames(provider);
    if (!supportsVideoAssetIngest(provider) || !apiBase || !assetFields) {
      toast.error('当前项目的视频服务配置无效，无法入库');
      return;
    }
    const { idField: idKey, statusField: statusKey } = assetFields;
    const prevId = d[idKey] || '';
    const prevStatus = d[statusKey] || '';
    if (prevStatus === 'Active') { toast.success('图片已入库'); return; }
    updateAssetState({
      ...clearVideoAssetBinding({}),
      assetProvider: provider,
      [idKey]: prevId,
      [statusKey]: 'Processing',
    });
    const successText = `图片已入库 ${assetLibraryLabel(provider)}`;
    let assetId = prevId;
    try {
      let status = 'Processing';
      if (!assetId) {
        const result = await authenticatedFetch(`${API_HOST}${apiBase}/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            projectId: Number(pid),
            episodeId: episodeId ? Number(episodeId) : undefined,
            url,
            name: d.name || d.prompt || '画布图片',
            assetType: 'Image',
            typeCode: 'storyboard_image',
            source: 'canvas',
            sourceDetail: 'canvas_node_ingest',
          }),
        });
        const applied = applyAssetCreateResult({}, result, provider);
        if (!applied.accepted || applied.provider !== provider) {
          throw new Error('素材库返回与当前项目不匹配，已拒绝写入');
        }
        assetId = applied.target[idKey];
        status = applied.target[statusKey] || 'Processing';
        if (!assetId) throw new Error('素材库未返回素材 ID');
        updateAssetState({ ...applied.target, assetProvider: provider });
        if (status !== 'Active') toast.success(result?.reused ? '已同步素材入库任务' : '已提交素材入库');
      }
      if (status === 'Active') { toast.success(successText); return; }
      const terminal = await pollVideoAssetUntilTerminal({
        provider,
        projectId: pid,
        url,
        assetId,
        onStatus: ({ status: nextStatus }) => {
          status = nextStatus;
          updateAssetState({ [idKey]: assetId, [statusKey]: nextStatus, assetProvider: provider });
        },
      });
      if (terminal.status === 'Active') toast.success(successText);
      else toast.error('图片入库失败，可重试');
    } catch (e) {
      // 轮询超时/暂时网络错误不代表上游入库失败；有 ID 时保留 Processing。
      updateAssetState({ [statusKey]: assetId ? 'Processing' : prevStatus });
      toast.warning(e.message || '素材入库状态暂时无法确认');
    }
  }, [projectId, episodeId, toast]);

  // 宫格拆分：打开确认弹窗（行列数在弹窗里选，不再用 window.prompt）
  const openSplitGridConfirm = useCallback((targetNode) => {
    let rows = 3, cols = 3;
    const sourceId = targetNode.data?.sourceNodeId;
    if (sourceId) {
      const sourceNode = nodesRef.current.find(n => n.id === sourceId);
      if (sourceNode?.data) {
        rows = sourceNode.data.rows || 3;
        cols = sourceNode.data.cols || 3;
      }
    }
    setSplitGridConfirm({ node: targetNode, rows, cols, busy: false });
  }, []);

  // 宫格拆分：实际执行（确认弹窗点确认后调用）
  const executeSplitImage = useCallback(async (targetNode, splitRows, splitCols) => {
    recordNow(); // 拆分产出多节点算一步
    setSplitGridConfirm((current) => (current ? { ...current, busy: true } : current));
    toast.info(`正在拆分 ${splitRows}×${splitCols} 宫格图...`);
    try {
      const result = await authenticatedFetch(`${API_HOST}/api/video/split-image-only`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gridImageUrl: targetNode.data.url, rows: splitRows, cols: splitCols }),
      });
      const images = result?.images || result;
      if (!Array.isArray(images) || images.length === 0) { toast.error('拆分失败，未返回图片'); return; }
      const baseX = (targetNode.position?.x || 0) + 300;
      const baseY = (targetNode.position?.y || 0);
      const spacing = { x: 230, y: 180 };
      for (let i = 0; i < images.length; i++) {
        const row = Math.floor(i / splitCols);
        const col = i % splitCols;
        const pos = { x: baseX + col * spacing.x, y: baseY + row * spacing.y };
        const nodeData = { resourceType: 'image', name: `${targetNode.data.name || '分镜'}-${String(i + 1).padStart(2, '0')}`, url: images[i], thumbnail: images[i] };
        const tempId = `node-${nodeIdCounter + i}`;
        setNodes((nds) => [...nds, { id: tempId, type: 'atom', position: pos, data: { ...nodeData, legacyType: 'resource', normalized: normalizeNodeData(nodeData, 'resource'), onImageClick: (url, name, mediaType) => setImagePreview({ url, name, mediaType }), onToggleExpand: (nid) => toggleNodeExpandRef.current?.(nid), onUpdate: (nid, nd) => handleUpdateNodeRef.current?.(nid, nd), onGenerate: (nid, fd) => handleNodeGenerateRef.current?.(nid, fd), onExportResult: (nid) => handleExportResultRef.current?.(nid) } }]);
        try {
          const saved = await onSaveNode({ canvasId, nodeType: 'resource', positionX: pos.x, positionY: pos.y, data: JSON.stringify(nodeData) });
          if (saved?.id) { commitTempNodeId(tempId, saved.id.toString()); }
        } catch (e) { console.error('保存拆分节点失败:', e); }
      }
      setNodeIdCounter((c) => c + images.length);
      toast.success(`已拆分为 ${images.length} 张图片`);
      setSplitGridConfirm(null);
    } catch (error) {
      console.error('拆分宫格失败:', error);
      toast.error(`拆分失败: ${error.message || '请重试'}`);
      setSplitGridConfirm((current) => (current ? { ...current, busy: false } : current));
    }
  }, [recordNow, nodeIdCounter, setNodes, onSaveNode, commitTempNodeId, canvasId, toast]);

  // P2-2 应用快照：本地恢复 + 与后端 diff 同步
  const applySnapshot = useCallback(async (snapshot) => {
    const currentNodes = nodesRef.current;
    const currentEdges = edgesRef.current;
    const currentNodeById = new Map(currentNodes.map((node) => [String(node.id), node]));
    const durableSnapshotNodes = snapshot.nodes.map((snapshotNode) => {
      const currentNode = currentNodeById.get(String(snapshotNode.id));
      if (!currentNode) return snapshotNode;
      const generationHistory = mergeDurableGenerationHistory(snapshotNode.data, currentNode.data);
      if (generationHistory.length === 0) return snapshotNode;
      const mergedData = { ...snapshotNode.data, generationHistory };
      const snapshotCurrentEntry = snapshotNode.data?.currentGenerationHistoryId != null
        ? generationHistory.find((entry) => (
          String(entry.id) === String(snapshotNode.data.currentGenerationHistoryId)
        ))
        : generationHistory.find((entry) => entry.url === snapshotNode.data?.lastResultUrl);
      const bindingProjection = snapshotCurrentEntry
        ? selectGenerationHistoryVersion(mergedData, snapshotCurrentEntry.id)
        : null;
      return {
        ...snapshotNode,
        data: {
          ...mergedData,
          ...(bindingProjection ? Object.fromEntries(
            GENERATION_BINDING_FIELDS.map((field) => [field, bindingProjection[field] ?? null]),
          ) : {}),
        },
      };
    });
    const snapNodeIds = new Set(durableSnapshotNodes.map((n) => n.id));
    const curNodeIds = new Set(currentNodes.map((n) => n.id));

    // 1) 删除：当前有、快照没有
    for (const n of currentNodes) {
      if (!snapNodeIds.has(n.id) && !isTempNodeId(n.id)) {
        try { await onDeleteNode(n.id); } catch (e) { console.error('[Canvas] 撤销同步-删除节点失败:', e); }
      }
    }

    // 2) 重建：快照有、当前没有（后端已删，重新 POST 并记录 id 映射）
    const idMap = {};
    for (const sn of durableSnapshotNodes) {
      if (curNodeIds.has(sn.id)) continue;
      try {
        const saved = await onSaveNode({
          canvasId,
          nodeType: sn.data?.legacyType || sn.type,
          positionX: sn.position.x,
          positionY: sn.position.y,
          data: JSON.stringify(sn.data),
        });
        idMap[sn.id] = saved?.id?.toString() || sn.id;
      } catch (e) {
        idMap[sn.id] = sn.id;
        console.error('[Canvas] 撤销同步-重建节点失败:', e);
      }
    }
    const mapId = (id) => idMap[id] || id;

    // 3) 变更：两边都有但内容/位置不同 → 标脏走防抖队列（payload 取快照内容）
    const curById = new Map(currentNodes.map((n) => [n.id, n]));
    for (const sn of durableSnapshotNodes) {
      if (!curNodeIds.has(sn.id)) continue;
      const cur = curById.get(sn.id);
      const curData = stripNonSerializable(cur.data);
      delete curData.normalized;
      const changed = JSON.stringify(curData) !== JSON.stringify(sn.data)
        || cur.position.x !== sn.position.x
        || cur.position.y !== sn.position.y;
      if (changed) {
        saveQueue.markDirty(sn.id, {
          id: sn.id,
          canvasId,
          nodeType: sn.data?.legacyType || cur.type,
          positionX: sn.position.x,
          positionY: sn.position.y,
          data: JSON.stringify(sn.data),
        });
      }
    }

    // 4) 边 diff：按 source→target+handles+type 归一比较（id 可能已重映射）
    const edgeKey = (e) => `${mapId(e.source)}->${mapId(e.target)}|${e.sourceHandle || ''}|${e.targetHandle || ''}|${e.data?.connectionType || 'reference'}`;
    const curEdgeKeys = new Map(currentEdges.map((e) => [edgeKey(e), e]));
    const snapEdgeKeys = new Set(snapshot.edges.map((e) => edgeKey(e)));

    for (const e of currentEdges) {
      if (snapEdgeKeys.has(edgeKey(e))) continue;
      if (/^\d+$/.test(String(e.id))) {
        try {
          await authenticatedFetch(`${API_HOST}/api/canvas/connection/${e.id}`, { method: "DELETE" });
        } catch (err) { console.error('[Canvas] 撤销同步-删边失败:', err); }
      }
    }
    for (const e of snapshot.edges) {
      if (curEdgeKeys.has(edgeKey(e))) continue;
      const source = mapId(e.source);
      const target = mapId(e.target);
      if (isTempNodeId(source) || isTempNodeId(target)) continue; // 后端不存在的只保留本地
      try {
        await onSaveConnection({
          canvasId,
          sourceNodeId: source,
          targetNodeId: target,
          sourcePort: e.sourceHandle || 'default',
          targetPort: e.targetHandle || 'default',
          connectionType: e.data?.connectionType || 'reference',
        });
      } catch (err) { console.error('[Canvas] 撤销同步-补边失败:', err); }
    }

    // 5) 应用本地状态（重算 normalized + 重新挂载回调 + 恢复分组父子关系/框样式）
    const restoredNodes = durableSnapshotNodes.map((sn) => {
      const id = mapId(sn.id);
      const legacyType = sn.data?.legacyType;
      const useAtom = sn.type === 'atom' && legacyType;
      // 片4：分组父子关系 — parentId 存于 data（快照含 data.parentId），框尺寸存于 data.frameWidth/frameHeight
      const parentId = sn.data?.parentId ? (idMap[sn.data.parentId] || sn.data.parentId) : undefined;
      const isFrame = sn.type === 'groupFrame' || legacyType === 'groupFrame';
      return {
        id,
        type: sn.type,
        position: { ...sn.position },
        ...(parentId ? { parentId, extent: 'parent' } : {}),
        ...(isFrame ? {
          zIndex: -1,
          style: { width: sn.data?.frameWidth || 480, height: sn.data?.frameHeight || 360 },
        } : {}),
        data: {
          ...sn.data,
          // data.parentId 同步为（可能重映射后的）真实框 id，保持与 node.parentId 一致
          ...(sn.data?.parentId ? { parentId: idMap[sn.data.parentId] || sn.data.parentId } : {}),
          normalized: useAtom ? normalizeNodeData(sn.data, legacyType) : (sn.data?.normalized || null),
          onImageClick: (url, name, mediaType) => setImagePreview({ url, name, mediaType }),
          onToggleExpand: (nid) => toggleNodeExpandRef.current?.(nid),
          onUpdate: (nid, nd) => handleUpdateNodeRef.current?.(nid, nd),
          onGenerate: (nid, fd) => handleNodeGenerateRef.current?.(nid, fd),
        onExportResult: (nid) => handleExportResultRef.current?.(nid),
          ...(['storyboardGrid', 'storyboardShot', 'seedanceMerge', 'storyboardEpisode'].includes(legacyType) ? {
            onRegeneratePrompts: (nid, fd) => handleRegeneratePromptsRef.current?.(nid, fd),
            onSplitGrid: (nid, p) => handleSplitGridRef.current?.(nid, p),
          } : {}),
          ...(legacyType === 'scriptToStoryboard' ? {
            onSmartMatch: (nid) => handleSmartMatchRef.current?.(nid),
            onGenerateMasterShots: (nid, opts) => handleGenerateMasterShotsRef.current?.(nid, opts),
            onGenerateConnectingShots: (nid) => handleGenerateConnectingShotsRef.current?.(nid),
            onCreateBlankStoryboardShot: (nid, opts) => handleCreateBlankStoryboardShotRef.current?.(nid, opts),
            onSyncShotReferences: (nid) => handleSyncShotReferencesRef.current?.(nid),
            onArrangeStoryboard: (nid) => handleArrangeStoryboardRef.current?.(nid),
            onResumeStoryboardTask: (nid) => handleResumeStoryboardTaskRef.current?.(nid),
          } : {}),
          ...(legacyType === 'motionStoryboard' ? {
            onOpenEditor: (nid) => {
              const node = getNodes().find((n) => n.id === nid);
              if (node) setMotionEditor({ nodeId: nid, nodeData: node.data });
            },
          } : {}),
        },
      };
    });
    const restoredEdges = snapshot.edges.map((e) => {
      const isOutput = (e.data?.connectionType || 'reference') === 'output';
      return {
        ...e,
        source: mapId(e.source),
        target: mapId(e.target),
        style: isOutput ? EDGE_STYLE_OUTPUT : EDGE_STYLE_REFERENCE,
        animated: false,
        deletable: true,
      };
    });
    setNodes([
      ...restoredNodes.filter((n) => n.type === 'groupFrame'),
      ...restoredNodes.filter((n) => n.type !== 'groupFrame'),
    ]);
    setEdges(restoredEdges);
  }, [onDeleteNode, onSaveNode, onSaveConnection, saveQueue, canvasId, setNodes, setEdges, getNodes]);

  const handleUndo = useCallback(() => {
    const current = snapshotGraph(nodesRef.current, edgesRef.current, stripNonSerializable);
    const target = history.undo(current);
    if (target) applySnapshot(target);
  }, [history, applySnapshot]);

  const handleRedo = useCallback(() => {
    const current = snapshotGraph(nodesRef.current, edgesRef.current, stripNonSerializable);
    const target = history.redo(current);
    if (target) applySnapshot(target);
  }, [history, applySnapshot]);

  // 撤销/重做快捷键（输入框聚焦时跳过）
  React.useEffect(() => {
    const onKeyDown = (e) => {
      const target = e.target;
      const isEditable = target && (
        target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable
      );
      if (isEditable) return;
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((key === 'z' && e.shiftKey) || key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleUndo, handleRedo]);
  // ── P2/P3 基础设施结束 ─────────────────────────────────────────

  // 监听空格键和 ESC 键
  React.useEffect(() => {
    const isEditableTarget = (target) => (
      target instanceof HTMLElement
      && (
        target.tagName === 'INPUT'
        || target.tagName === 'TEXTAREA'
        || target.tagName === 'SELECT'
        || target.isContentEditable
      )
    );

    const handleKeyDown = (event) => {
      if (isEditableTarget(event.target)) {
        return;
      }
      if (event.code === 'Space' && !isSpacePressed) {
        event.preventDefault();
        setIsSpacePressed(true);
      }
      if ((event.key === 'Backspace' || event.key === 'Delete') && selectedNodes.length > 0) {
        event.preventDefault();
        setBatchDeleteConfirm({ nodeIds: [...selectedNodes] });
      }
      if (event.key === 'Escape') {
        // 关闭生成类型菜单（如果打开）
        setShowGenerateTypeMenu(null);
        // 关闭右键菜单
        setContextMenu(null);
        // 关闭 Seedance 合并菜单
      }
    };

    const handleKeyUp = (event) => {
      if (event.code === 'Space') {
        event.preventDefault();
        setIsSpacePressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isSpacePressed, selectedNodes]);

  // 监听节点变化：拖拽标脏（防抖保存）、键盘删除同步后端
  const handleNodesChange = useCallback((changes) => {
    // 拖拽开始：整段拖拽只记一步历史
    const hasDragMove = changes.some((c) => c.type === 'position' && c.dragging);
    if (hasDragMove && !dragRecordedRef.current) {
      recordNow();
      dragRecordedRef.current = true;
    }
    // 删除（键盘 Backspace 等）：记录历史
    const removed = changes.filter((c) => c.type === 'remove');
    if (removed.length > 0) {
      recordNow();
    }
    // 片4：被删除的分组框 → 其子节点脱离分组（保留在画布，坐标换算回绝对）
    const removedFrameIds = removed
      .map((c) => nodesRef.current.find((n) => n.id === c.id))
      .filter((n) => (n?.data?.legacyType || n?.type) === 'groupFrame')
      .map((n) => n.id);

    onNodesChange(changes);

    if (removedFrameIds.length > 0) {
      orphanChildrenOf(removedFrameIds, { excludeIds: removed.map((c) => c.id) });
    }

    changes.forEach((change) => {
      if (change.type === 'position') {
        if (change.dragging === false) {
          // 拖拽结束：显式 payload（此刻 getNodes 尚未提交最新位置），立即 flush
          dragRecordedRef.current = false;
          const node = nodesRef.current.find((n) => n.id === change.id);
          if (node && change.position) {
            saveQueue.markDirty(change.id, {
              id: change.id,
              canvasId,
              nodeType: node.data?.legacyType || node.type,
              positionX: change.position.x,
              positionY: change.position.y,
              data: JSON.stringify(stripNonSerializable(node.data)),
            });
          }
          saveQueue.flush();
        } else {
          // 拖拽过程只标脏
          saveQueue.markDirty(change.id);
        }
      }
      if (change.type === 'remove' && !isTempNodeId(change.id)) {
        onDeleteNode(change.id).catch((e) => console.error('[Canvas] 删除节点失败:', e));
      }
    });
  }, [onNodesChange, recordNow, saveQueue, onDeleteNode, canvasId, orphanChildrenOf]);

  // 监听边变化，处理删除时同步后端
  const handleEdgesChangeCustom = useCallback(async (changes) => {
    // 删边属于离散动作：记录历史
    if (changes.some((c) => c.type === 'remove')) {
      recordNow();
    }
    onEdgesChange(changes);

    for (const change of changes) {
      if (change.type === 'remove') {
        // temp 节点（尚未拿到后端真实 id）上的连线只维护本地状态，不同步后端
        const removed = edges.find(e => e.id === change.id);
        if (removed && (isTempNodeId(removed.source) || isTempNodeId(removed.target))) {
          console.warn('[Canvas] 跳过删除 temp 连线（节点尚未保存到后端）:', change.id);
          continue;
        }
        // 本地生成的边 id（非后端数字 id）无需通知后端
        if (!/^\d+$/.test(String(change.id))) {
          continue;
        }
        try {
          await authenticatedFetch(`${API_HOST}/api/canvas/connection/${change.id}`, {
            method: "DELETE",
          });
        } catch (error) {
          console.error("删除连接失败:", error);
        }
      }
    }
  }, [onEdgesChange, edges, recordNow]);

  // 节点连接事件 - 直接创建连接，不弹菜单
  const onConnect = useCallback(
    async (params) => {
      // 如果正在初始化，不处理连接事件（避免加载时重复创建节点）
      if (isInitializing.current) {
        return;
      }

      // 防止完全相同的连线重复创建；同一对节点的不同端口仍允许分别连接。
      const alreadyExists = edges.some(
        (e) => e.source === params.source
          && e.target === params.target
          && (e.sourceHandle || null) === (params.sourceHandle || null)
          && (e.targetHandle || null) === (params.targetHandle || null)
          && (e.data?.connectionType || 'reference') === 'reference'
      );
      if (alreadyExists) return;

      // P2-4 类型化校验（isValidConnection 之外的二次校验，拒绝时给人话提示）
      const sourceNode = nodesRef.current.find((n) => n.id === params.source);
      const targetNode = nodesRef.current.find((n) => n.id === params.target);
      const rejection = getConnectionRejection(sourceNode, targetNode);
      if (rejection) {
        toast.warning(rejection);
        return;
      }

      recordNow();

      // 直接添加连接边（统一样式，方向 source→target = 数据流向）
      const refEdge = {
        ...params,
        data: { connectionType: 'reference' },
        style: { stroke: 'var(--color-text-muted)', strokeWidth: 2, opacity: 0.6 },
        animated: false,
        deletable: true,
      };
      setEdges((eds) => addEdge(refEdge, eds));

      // 保存连接到后端（temp 端走 P2-3 队列，真实 id 回写后自动补存）
      await persistConnection({
        source: params.source,
        target: params.target,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        connectionType: 'reference',
      });
    },
    [setEdges, edges, recordNow, persistConnection, toast]
  );

  // 节点选择变化事件
  const onSelectionChange = useCallback(({ nodes: selectedNodes }) => {
    const selectedIds = selectedNodes.map(n => n.id);
    setSelectedNodes(selectedIds);
  }, []);

  // 连接开始事件
  const onConnectStart = useCallback((event, { nodeId }) => {
    connectingFrom.current = nodeId;
  }, []);

  // 连接结束事件 - 不再处理拖到空白处的情况
  const onConnectEnd = useCallback(() => {
    // 清除连接状态
    connectingFrom.current = null;
  }, []);

  // 创建生成节点（从连接拖拽或节点连接）
  const createGenerateNodeFromConnection = useCallback(async (sourceNodeIds, generateType) => {
    if (!sourceNodeIds || sourceNodeIds.length === 0) return;

    // 如果 generateType 是 'connect'，只保存连接，不创建生成节点
    if (generateType === 'connect') {
      setShowGenerateTypeMenu(null);

      // 如果是两个节点，保存它们之间的连接（手动连接 = reference）
      if (sourceNodeIds.length === 2) {
        const [source, target] = sourceNodeIds;
        const alreadyConnected = edges.some((edge) => (
          edge.source === source
          && edge.target === target
          && !edge.sourceHandle
          && !edge.targetHandle
          && (edge.data?.connectionType || 'reference') === 'reference'
        ));
        if (alreadyConnected) {
          toast.info('这两个节点已经连接');
          return;
        }

        recordNow();
        setEdges((eds) => addEdge({
          source,
          target,
          data: { connectionType: 'reference' },
          style: EDGE_STYLE_REFERENCE,
          animated: false,
        }, eds));

        await persistConnection({
          source,
          target,
          connectionType: 'reference',
        });
      }
      return;
    }

    const sourceNodes = nodes.filter(n => sourceNodeIds.includes(n.id));
    if (sourceNodes.length === 0) return;

    recordNow();

    const position = showGenerateTypeMenu.position;

    const tempNodeId = `node-${nodeIdCounter}`;
    const newNodeData = {
      generateType: generateType,
      prompt: "",
      isGenerating: false,
      result: null,
      ...(generateType === "image" ? { includeProjectStyle: true } : {}),
      referenceNodes: sourceNodeIds,
      referenceUrls: sourceNodes.map(n => n.data.url || n.data.thumbnail).filter(Boolean),
    };

    const newNode = {
      id: tempNodeId,
      type: "atom",
      position,
      data: {
        ...newNodeData,
        projectId,
        episodeId,
        legacyType: "generate",
        normalized: normalizeNodeData(newNodeData, "generate"),
        onImageClick: (url, name, mediaType) => setImagePreview({ url, name, mediaType }),
        onToggleExpand: (nodeId) => toggleNodeExpandRef.current?.(nodeId),
        onUpdate: (nodeId, newData) => handleUpdateNodeRef.current?.(nodeId, newData),
        onGenerate: (nodeId, formData) => handleNodeGenerateRef.current?.(nodeId, formData),
        onExportResult: (nodeId) => handleExportResultRef.current?.(nodeId),
      },
    };

    setNodes((nds) => {
      const next = [...nds, newNode];
      nodesRef.current = next;
      return next;
    });
    setNodeIdCounter((c) => c + 1);
    setShowGenerateTypeMenu(null);

    // 保存新节点到后端
    try {
      const savedNode = await onSaveNode({
        canvasId: canvasId,
        nodeType: "generate",
        positionX: position.x,
        positionY: position.y,
        data: JSON.stringify(newNodeData),
      });

      const savedNodeId = savedNode?.id?.toString() || tempNodeId;

      // 更新节点 ID（并重映射本地/排队连线）
      if (savedNode && savedNode.id) {
        commitTempNodeId(tempNodeId, savedNodeId);
      }

      // 连接所有源节点到新节点（用户选中资源右键生成 = reference）
      const newEdges = sourceNodeIds.map(nodeId => ({
        id: `edge-${nodeId}-${savedNodeId}`,
        source: nodeId,
        target: savedNodeId,
        data: { connectionType: 'reference' },
        style: EDGE_STYLE_REFERENCE,
        animated: false,
      }));

      setEdges((eds) => [...eds, ...newEdges]);

      // 保存所有连接（temp 端走队列）
      for (const nodeId of sourceNodeIds) {
        await persistConnection({
          source: nodeId,
          target: savedNodeId,
          connectionType: 'reference',
        });
      }
    } catch (error) {
      console.error("创建生成节点失败:", error);
    }
  }, [nodes, edges, showGenerateTypeMenu, nodeIdCounter, setNodes, setEdges, onSaveNode, recordNow, persistConnection, commitTempNodeId, canvasId, projectId, episodeId, toast]);

  // 更新节点数据（支持对象或函数式更新）
  const handleUpdateNode = useCallback(async (nodeId, newDataOrFn) => {
    const committedNodeId = committedNodeIdsRef.current.get(String(nodeId));
    const effectiveNodeId = committedNodeId
      && nodesRef.current.some((node) => node.id === committedNodeId)
      ? committedNodeId
      : nodeId;
    // P2-2 历史记录：生成轮询中的进度更新跳过；到达终点（isGenerating→false）记一步
    const prevNode = nodesRef.current.find((n) => n.id === effectiveNodeId);
    if (prevNode) {
      const wasGenerating = !!prevNode.data?.isGenerating;
      const reachesTerminal = typeof newDataOrFn !== 'function' && newDataOrFn?.isGenerating === false;
      if (!wasGenerating || reachesTerminal) recordNow();
    }

    let resolvedData = null;
    let pendingSplit = null;
    let pendingActions = {};
    setNodes((nds) => {
      const next = nds.map((node) => {
        if (node.id !== effectiveNodeId) return node;
        let merged;
        if (typeof newDataOrFn === 'function') {
          merged = newDataOrFn(node.data);
        } else {
          // 过滤掉 undefined 值，防止覆盖已有的函数回调
          const filtered = Object.fromEntries(
            Object.entries(newDataOrFn).filter(([, v]) => v !== undefined)
          );
          merged = { ...node.data, ...filtered };
        }
        // 检查是否有 pendingSplit 请求（GridEditPanel 触发）
        if (merged.pendingSplit) {
          pendingSplit = merged.pendingSplit;
          delete merged.pendingSplit;
        }
        // 检查是否有 pendingRegenerateCell 请求
        if (merged.pendingRegenerateCell) {
          pendingActions.regenerateCell = merged.pendingRegenerateCell;
          delete merged.pendingRegenerateCell;
        }
        // 检查是否有 pendingExportCell 请求
        if (merged.pendingExportCell) {
          pendingActions.exportCell = merged.pendingExportCell;
          delete merged.pendingExportCell;
        }
        // 检查是否有 pendingUpscaleCell 请求
        if (merged.pendingUpscaleCell) {
          pendingActions.upscaleCell = merged.pendingUpscaleCell;
          delete merged.pendingUpscaleCell;
        }
        resolvedData = merged;
        // atom 节点：data 变化后重新计算 normalized
        if (node.type === 'atom' && merged.legacyType) {
          merged = { ...merged, normalized: normalizeNodeData(merged, merged.legacyType) };
        }
        return { ...node, data: merged };
      });
      // 同步 ref，保证紧随其后的动作（如 P2-1 spawn）拿到最新状态
      nodesRef.current = next;
      return next;
    });

    // 如果有 pendingSplit，触发拆分
    if (pendingSplit) {
      handleSplitGridRef.current?.(effectiveNodeId, pendingSplit);
    }
    // 如果有 pendingRegenerateCell，触发单格重新生成
    if (pendingActions.regenerateCell) {
      handleRegenerateCellRef.current?.(effectiveNodeId, pendingActions.regenerateCell);
    }
    // 如果有 pendingExportCell，触发导出单格
    if (pendingActions.exportCell) {
      handleExportCellRef.current?.(effectiveNodeId, pendingActions.exportCell);
    }
    // 如果有 pendingUpscaleCell，触发超分
    if (pendingActions.upscaleCell) {
      handleUpscaleCellRef.current?.(effectiveNodeId, pendingActions.upscaleCell);
    }

    // P2-3 保存走防抖队列（flush 时从节点最新状态构建 payload）
    if (resolvedData) {
      saveQueue.markDirty(effectiveNodeId);
    }
  }, [setNodes, recordNow, saveQueue]);

  // 画布生成 hook（P2-1 就地更新：结果回写 generate 节点，不再自动产出素材节点）
  const { generateImage, generateVideo, resumeGenerationTask } = useCanvasGeneration({
    episodeId,
  });

  // 生成节点的 onGenerate 回调
  const handleNodeGenerate = useCallback(async (nodeId, formData) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    // 查询中断或页面刷新后继续追踪原任务，禁止再次提交昂贵的图片/视频任务。
    if (!formData.upscale && node.data?.activeTaskId && node.data?.activeTaskKind) {
      const resumed = resumeGenerationTask(node, handleUpdateNode);
      if (resumed) {
        toast.info('正在继续查询原生成任务');
        return;
      }
    }

    const incomingRefEdges = edges.filter(
      e => e.target === nodeId && e.data?.connectionType === 'reference'
    );
    const refNodeIds = [...new Set(incomingRefEdges.map(e => e.source))];

    // 高清放大（AtomNode hover 条 upscale 标志）→ 走 SUPER_RESOLUTION 任务
    if (formData.upscale) {
      const sourceUrl = getNodeImageUrl(node.data);
      if (!sourceUrl) { toast.warning('该节点没有可放大的图片'); return; }
      try {
        const { taskId } = await submitTask("SUPER_RESOLUTION", {
          imageUrl: sourceUrl,
          resolution: "4k",
          scale: 50,
        }, { provider: getCurrentImageProvider() });
        toast.info('高清放大任务已提交...');
        startTaskPolling(taskId, {
          onSuccess: (result) => {
            const upscaledUrl = result?.imageUrl;
            if (upscaledUrl) {
              handleUpdateNode(nodeId, (prev) => {
                const isImageGenerator = prev.legacyType === 'generate' && prev.generateType === 'image';
                const historyPatch = isImageGenerator
                  ? createSuccessfulGenerationPatch(prev, clearVideoAssetBinding({
                    ...prev,
                    url: upscaledUrl,
                    mediaType: 'image',
                    taskId,
                    createdAt: new Date().toISOString(),
                    status: 'completed',
                    provider: prev.imageProvider || getCurrentImageProvider(),
                    model: prev.imageModel || null,
                    resolution: '4K',
                    imageQualityTier: '4K',
                    resourceId: null,
                    assetProvider: null,
                    originalVideoUrl: null,
                  }))
                  : { url: upscaledUrl, thumbnail: upscaledUrl };
                const next = { ...prev, ...historyPatch };
                delete next.upscale;
                return next;
              });
              if (node.data?.legacyType === 'generate' && node.data?.generateType === 'image') {
                const historyEntryId = `task:${String(taskId)}`;
                void authenticatedFetch(`${API_HOST}/api/resource/save`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    url: upscaledUrl,
                    projectId: projectId || localStorage.getItem('currentProjectId'),
                    episodeId: episodeId || null,
                    name: `AI高清-${(node.data?.rawPrompt || node.data?.prompt || '未命名').substring(0, 20)}`,
                    typeCode: 'agent_image',
                    source: 'canvas_generate_upscale',
                  }),
                }).then((saved) => {
                  if (!saved?.id) return;
                  handleUpdateNode(nodeId, (previousData) => ({
                    ...previousData,
                    ...backfillGenerationHistoryEntry(
                      previousData,
                      historyEntryId,
                      { resourceId: saved.id },
                    ),
                  }));
                }).catch((error) => {
                  console.warn('[Canvas] 高清结果写入资源中心失败:', error);
                  toast.warning('高清图已生成，但写入资源中心失败');
                });
              }
              toast.success('高清放大完成');
            }
          },
          onError: (errMsg) => {
            toast.error('高清放大失败: ' + errMsg);
          },
        });
      } catch (e) {
        console.error('[Upscale] 提交失败:', e);
        toast.error('高清放大提交失败: ' + e.message);
      }
      return;
    }

    // 分镜生成节点 → 调用 generateIndustrialGrid
    if (formData.nodeKind === "storyboardGrid") {
      const prompts = (formData.prompts || []).filter(p => p.trim());
      if (prompts.length === 0) { toast.warning("请至少填写一格分镜描述"); return; }

      handleUpdateNode(nodeId, { ...formData, isGenerating: true, generatingStatus: '分镜生成中...' });

      try {
        // 收集连入的参考图 base64
        const refImages = [];
        for (const rid of refNodeIds) {
          const refNode = nodes.find(n => n.id === rid);
          const imageUrl = getNodeImageUrl(refNode?.data);
          if (imageUrl) {
            refImages.push({ name: refNode.data.name || '参考图', base64: imageUrl });
          }
        }
        const imageProvider = getCurrentImageProvider();
        assertWanxImageInputLimit(refImages, imageProvider);

        const payload = {
          prompts: formData.prompts, // 传完整数组（含空格，后端会填 placeholder）
          rows: formData.rows || 2,
          cols: formData.cols || 2,
          aspectRatio: formData.aspectRatio || "16:9",
          overallPrompt: formData.imagePrompt || "",
          characterRefs: [],
          sceneRefs: refImages,
          propRefs: [],
          imageQualityTier: normalizeWanxImageQualityTier(
            formData.imageQualityTier || '2K',
            imageProvider,
          ),
          projectId: projectId || localStorage.getItem('currentProjectId') || null,
          episodeId: episodeId || null,
        };

        const imageUrl = await runImageTaskToUrl("GENERATE_INDUSTRIAL_GRID", payload, {
          styleAnchor: currentStyle || "universal",
          projectId: payload.projectId,
          episodeId: payload.episodeId,
          source: "canvas_industrial_grid",
        });

        if (!imageUrl || typeof imageUrl !== 'string') throw new Error("生成失败，未返回结果");

        // 生成完成，存到节点并切换到 generated 模式（用户手动拆分）
        handleUpdateNode(nodeId, {
          ...formData,
          isGenerating: false,
          generatingStatus: null,
          gridImageUrl: imageUrl,
          splitCellImages: [],
          viewMode: "generated",
          lastGeneratedAt: new Date().toISOString(),
        });

        toast.success("分镜生成成功！");
      } catch (error) {
        console.error("分镜生成失败:", error);
        toast.error(`分镜生成失败: ${error.message || "请重试"}`);
        handleUpdateNode(nodeId, { ...formData, isGenerating: false, generatingStatus: null });
      }
      return;
    }

    // 分镜导入节点 → 用 imagePrompt 调用 generateImage
    if (formData.nodeKind === "storyboardShot") {
      if (!formData.imagePrompt) { toast.warning("请输入生图提示词"); return; }
      handleUpdateNode(nodeId, { ...formData, isGenerating: true, generatingStatus: '生图中...' });

      try {
        const refs = [];
        for (const rid of refNodeIds) {
          const refNode = nodes.find(n => n.id === rid);
          const refUrl = getNodeImageUrl(refNode?.data);
          if (refUrl) {
            refs.push({ name: refNode.data.name || '参考图', base64: refUrl });
          }
        }
        const imageProvider = getCurrentImageProvider();
        assertWanxImageInputLimit(refs, imageProvider);

        const payload = {
          prompt: formData.imagePrompt,
          aspectRatio: formData.aspectRatio || "16:9",
          projectId: formData.projectId || localStorage.getItem('currentProjectId') || null,
          episodeId: episodeId || null,
          imageQualityTier: normalizeWanxImageQualityTier(
            formData.imageQualityTier || '2K',
            imageProvider,
          ),
          // 画布模式：只用显式连线的参考图，不注入项目级角色/剧本上下文
          skipStoryboardContext: true,
        };
        if (refs.length > 0) {
          payload.characterRefs = [];
          payload.sceneRefs = refs;
          payload.propRefs = [];
        }

        const result = await runImageTaskToUrl("TEXT_AND_REFERENCE_TO_IMAGE", payload, {
          styleAnchor: currentStyle || "universal",
          projectId: payload.projectId,
          episodeId: payload.episodeId,
          source: "canvas_storyboard_shot",
        });

        if (!result) throw new Error("生成失败，未返回结果");

        // P3-1：分镜生图结果自动入资源中心（失败不阻塞生图，resourceId 写到 shot 节点 data）
        let shotResourceId = null;
        try {
          const saved = await authenticatedFetch(`${API_HOST}/api/resource/save`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              url: result,
              projectId: projectId || localStorage.getItem('currentProjectId'),
              episodeId: episodeId || null,
              name: `分镜#${formData.shotNumber}-生图`,
              typeCode: 'agent_image',
              source: 'canvas_generate',
            }),
          });
          shotResourceId = saved?.id || null;
        } catch (e) {
          console.warn('[Canvas] 分镜生图写入资源中心失败:', e);
          toast.warning('已生图，但写入资源中心失败');
        }

        // 就地更新：只回写 generatedImage（不再自动产出素材节点，保持与分镜系统兼容）
        handleUpdateNode(nodeId, {
          ...formData,
          generatedImage: result,
          isGenerating: false,
          generatingStatus: null,
          ...(shotResourceId ? { resourceId: shotResourceId } : {}),
        });

        toast.success(`分镜 #${formData.shotNumber} 生图完成`);
      } catch (error) {
        console.error("分镜生图失败:", error);
        toast.error(`生图失败: ${error.message || "请重试"}`);
        handleUpdateNode(nodeId, { ...formData, isGenerating: false, generatingStatus: null });
      }
      return;
    }

    // Seedance 片段合并节点 → 调用 generateSeedancePrompt
    if (formData.nodeKind === "seedanceMerge") {
      if (!formData.sourceShots || formData.sourceShots.length === 0) {
        toast.warning("没有来源分镜数据"); return;
      }
      handleUpdateNode(nodeId, { ...formData, isGenerating: true, generatingStatus: '生成视频提示词中...' });

      try {
        const response = await runTaskToCompletion("GENERATE_SEEDANCE_PROMPT", {
          shots: formData.sourceShots,
          style: formData.style,
          duration: formData.duration,
          aspectRatio: formData.aspectRatio,
          projectId: projectId || localStorage.getItem('currentProjectId') || null,
          episodeId: episodeId || null,
        }, {
          projectId: projectId || localStorage.getItem('currentProjectId') || null,
          episodeId: episodeId || null,
          source: "canvas_seedance_prompt",
        });

        const videoPrompt = typeof response.result === 'string' ? response.result : (response.result?.value || response.result?.result || "");
        if (!videoPrompt) throw new Error("生成失败，未返回结果");

        handleUpdateNode(nodeId, {
          ...formData,
          isGenerating: false,
          generatingStatus: null,
          videoPrompt,
        });

        toast.success("视频提示词生成成功！");
      } catch (error) {
        console.error("视频提示词生成失败:", error);
        toast.error(`生成失败: ${error.message || "请重试"}`);
        handleUpdateNode(nodeId, { ...formData, isGenerating: false, generatingStatus: null });
      }
      return;
    }

    // 整集分镜批量生图（使用任务队列系统）
    if (formData.nodeKind === "storyboardEpisode") {
      const shotsToGenerate = (formData.shots || []).filter(s => s.imagePrompt && !s.generatedImage);
      if (shotsToGenerate.length === 0) {
        toast.warning("没有需要生图的分镜（全部已生图或无提示词）");
        return;
      }

      handleUpdateNode(nodeId, { ...formData, isGenerating: true, generatingStatus: `准备提交任务...` });

      const connectedImgs = collectConnectedImages(nodeId, edges, nodes);
      const imageProvider = getCurrentImageProvider();
      const resolvedProjectId = projectId || localStorage.getItem('currentProjectId') || null;
      let completed = 0;
      let succeeded = 0;
      const total = shotsToGenerate.length;

      // 并发提交所有任务
      const taskPromises = shotsToGenerate.map(async (shot) => {
        try {
          // 收集该分镜的参考图（角色、场景、道具）- 支持多选
          const characterRefs = [];
          const sceneRefs = [];
          const propRefs = [];

          // 角色参考图（多张）
          if (shot.refCharacterIndexes && Array.isArray(shot.refCharacterIndexes)) {
            for (const idx of shot.refCharacterIndexes) {
              if (connectedImgs[idx]?.url) {
                const refImg = connectedImgs[idx];
                characterRefs.push({ name: refImg.name || '角色', base64: refImg.url });
              }
            }
          }

          // 场景参考图（多张）
          if (shot.refSceneIndexes && Array.isArray(shot.refSceneIndexes)) {
            for (const idx of shot.refSceneIndexes) {
              if (connectedImgs[idx]?.url) {
                const refImg = connectedImgs[idx];
                sceneRefs.push({ name: refImg.name || '场景', base64: refImg.url });
              }
            }
          }

          // 道具参考图（多张）
          if (shot.refPropIndexes && Array.isArray(shot.refPropIndexes)) {
            for (const idx of shot.refPropIndexes) {
              if (connectedImgs[idx]?.url) {
                const refImg = connectedImgs[idx];
                propRefs.push({ name: refImg.name || '道具', base64: refImg.url });
              }
            }
          }
          assertWanxImageInputLimit(
            [...characterRefs, ...sceneRefs, ...propRefs],
            imageProvider,
          );

          const payload = {
            prompt: shot.imagePrompt,
            aspectRatio: formData.aspectRatio || "16:9",
            characterRefs,
            sceneRefs,
            propRefs,
            imageQualityTier: normalizeWanxImageQualityTier(
              formData.imageQualityTier || '2K',
              imageProvider,
            ),
            // 画布模式：只用显式连线的参考图，不注入项目级角色/剧本上下文
            skipStoryboardContext: true,
            projectId: resolvedProjectId,
            episodeId: episodeId || null,
          };

          // 复用全局任务协议：统一使用 /api/task/status?taskId=... 并
          // 兼容 success/failed 等持久化任务状态，避免画布再维护一套轮询实现。
          const imageUrl = await runImageTaskToUrl("TEXT_AND_REFERENCE_TO_IMAGE", payload, {
            styleAnchor: currentStyle || "universal",
            projectId: resolvedProjectId,
            episodeId: episodeId || null,
            source: "canvas_storyboard_episode",
          });
          if (!imageUrl || typeof imageUrl !== 'string') {
            throw new Error('任务完成但没有返回图片地址');
          }

          succeeded++;
          // 更新该分镜的图片。节点整体 spinner 等全部任务终态后统一清理。
          handleUpdateNode(nodeId, (prevData) => {
            const updatedShots = prevData.shots.map(s =>
              s.shotId === shot.shotId ? { ...s, generatedImage: imageUrl } : s
            );
            return {
              ...prevData,
              shots: updatedShots,
            };
          });
          return imageUrl;
        } catch (error) {
          console.error(`分镜 #${shot.shotNumber} 生图任务失败:`, error);
          toast.error(`分镜 #${shot.shotNumber} 生图失败: ${error.message || '请重试'}`);
          throw error;
        } finally {
          completed++;
          handleUpdateNode(nodeId, (prevData) => ({
            ...prevData,
            isGenerating: true,
            generatingStatus: `生成中 ${completed}/${total}`,
          }));
        }
      });

      // 等待所有任务完成
      try {
        const settledResults = await Promise.allSettled(taskPromises);
        const failed = settledResults.filter((result) => result.status === 'rejected').length;
        if (failed === 0) {
          toast.success(`批量生图完成！成功 ${succeeded}/${total}`);
        } else if (succeeded === 0) {
          toast.error(`批量生图失败，${failed}/${total} 个任务未完成`);
        } else {
          toast.warning(`批量生图部分完成：成功 ${succeeded}/${total}，失败 ${failed}`);
        }
      } finally {
        handleUpdateNode(nodeId, (prevData) => ({
          ...prevData,
          isGenerating: false,
          generatingStatus: null,
        }));
      }

      return;
    }

    // 普通生成节点
    // 防重触发：该节点已有生成在飞（双击穿透/重复回调），直接忽略
    if (node.data?.isGenerating) return;
    if (formData.generateType === "video") {
      generateVideo(node, formData, refNodeIds, nodes, handleUpdateNode);
    } else {
      generateImage(node, formData, refNodeIds, nodes, handleUpdateNode);
    }
  }, [
    nodes,
    edges,
    generateImage,
    generateVideo,
    handleUpdateNode,
    resumeGenerationTask,
    toast,
    projectId,
    episodeId,
    currentStyle,
  ]);

  // 重新生成宫格提示词
  const handleRegeneratePrompts = useCallback(async (nodeId, formData) => {
    handleUpdateNode(nodeId, { ...formData, isGeneratingPrompts: true });

    try {
      const { taskId } = await authenticatedFetch(`${API_HOST}/api/task/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          taskType: 'REGENERATE_GRID_PROMPTS',
          payload: {
            imagePrompt: formData.imagePrompt || '',
            visualContent: formData.visualContent || '',
            content: formData.content || '',
            action: formData.action || '',
            gridCount: formData.gridCount || 9,
          },
          metadata: {},
        })
      });

      // 轮询结果
      let pollCount = 0;
      const MAX_POLLS = 90; // 最多轮询 3 分钟（90 × 2s）
      const interval = setInterval(async () => {
        pollCount++;
        if (pollCount > MAX_POLLS) {
          clearInterval(interval);
          handleUpdateNode(nodeId, (prev) => ({ ...prev, isGeneratingPrompts: false }));
          toast.error('重新生成提示词超时');
          return;
        }
        try {
          const status = await authenticatedFetch(`${API_HOST}/api/task/status?taskId=${taskId}`);
          if (status.status === 'success' || status.status === 'COMPLETED') {
            clearInterval(interval);
            const result = typeof status.result === 'string' ? JSON.parse(status.result) : status.result;
            handleUpdateNode(nodeId, (prev) => ({
              ...prev,
              prompts: result.prompts || prev.prompts,
              isGeneratingPrompts: false,
            }));
            toast.success('宫格提示词已重新生成');
          } else if (status.status === 'FAILED' || status.status === 'failed' || status.status === 'not_found') {
            clearInterval(interval);
            handleUpdateNode(nodeId, (prev) => ({ ...prev, isGeneratingPrompts: false }));
            toast.error('重新生成失败: ' + (status.errorMessage || '未知错误'));
          }
        } catch {
          // 网络错误继续轮询
        }
      }, 2000);
    } catch (error) {
      handleUpdateNode(nodeId, (prev) => ({ ...prev, isGeneratingPrompts: false }));
      toast.error('提交任务失败: ' + error.message);
    }
  }, [handleUpdateNode, toast]);

  // 手动拆分宫格图
  const handleSplitGrid = useCallback(async (nodeId, { gridImageUrl, rows, cols }) => {
    handleUpdateNode(nodeId, (prev) => ({ ...prev, isSplitting: true }));
    try {
      const splitData = await authenticatedFetch(`${API_HOST}/api/video/split-image-only`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ gridImageUrl, rows, cols }),
      });
      const splitCellImages = (splitData?.images || []).map((url, idx) => ({ index: idx, url }));
      handleUpdateNode(nodeId, (prev) => ({
        ...prev,
        isSplitting: false,
        splitCellImages,
        splitRows: rows,
        splitCols: cols,
        viewMode: splitCellImages.length > 0 ? "preview" : "generated",
      }));
      if (splitCellImages.length > 0) {
        toast.success("宫格拆分完成");
      } else {
        toast.warning("拆分未返回结果");
      }
    } catch (err) {
      console.error('宫格拆分失败:', err);
      handleUpdateNode(nodeId, (prev) => ({ ...prev, isSplitting: false }));
      toast.error('宫格拆分失败: ' + (err.message || '请重试'));
    }
  }, [handleUpdateNode, toast]);
  const handleSplitGridRef = useRef(null);

  // 重新生成单格图片
  const handleRegenerateCell = useCallback(async (nodeId, { cellIndex, prompt, aspectRatio }) => {
    // 标记该格正在生成
    handleUpdateNode(nodeId, (prev) => {
      const regen = new Set(prev.regeneratingCells || []);
      regen.add(cellIndex);
      return { ...prev, regeneratingCells: regen };
    });

    try {
      // 收集连入的参考图
      const incomingRefEdges = edges.filter(
        e => e.target === nodeId && e.data?.connectionType === 'reference'
      );
      const refNodeIds = [...new Set(incomingRefEdges.map(e => e.source))];
      const refs = [];
      for (const rid of refNodeIds) {
        const refNode = nodes.find(n => n.id === rid);
        const refUrl = getNodeImageUrl(refNode?.data);
        if (refUrl) refs.push({ name: refNode.data.name || '参考图', base64: refUrl });
      }
      const imageProvider = getCurrentImageProvider();
      assertWanxImageInputLimit(refs, imageProvider);

      const payload = {
        prompt,
        aspectRatio: aspectRatio || "16:9",
        projectId: nodes.find(n => n.id === nodeId)?.data?.projectId || localStorage.getItem('currentProjectId') || null,
        episodeId: episodeId || null,
        imageQualityTier: normalizeWanxImageQualityTier(
          nodes.find(n => n.id === nodeId)?.data?.imageQualityTier || '2K',
          imageProvider,
        ),
      };
      if (refs.length > 0) {
        payload.characterRefs = [];
        payload.sceneRefs = refs;
        payload.propRefs = [];
      }

      const result = await runImageTaskToUrl("TEXT_AND_REFERENCE_TO_IMAGE", payload, {
        styleAnchor: currentStyle || "universal",
        projectId: payload.projectId,
        episodeId: payload.episodeId,
        source: "canvas_cell_regenerate",
      });

      if (!result || typeof result !== 'string') throw new Error("生成失败");

      // 保存生成的图片到本地文件系统
      let imageUrl = result;
      try {
        const savedUrl = await authenticatedFetch(`${API_HOST}/api/resource/save`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            url: result,
            projectId: projectId || localStorage.getItem('currentProjectId'),
            name: `宫格${cellIndex + 1}-重新生成`,
            typeCode: 'agent_image',
            source: 'canvas',
          }),
        });
        if (savedUrl?.url) imageUrl = savedUrl.url;
      } catch { /* 保存失败不影响主流程 */ }

      // 更新该格图片
      handleUpdateNode(nodeId, (prev) => {
        const updated = (prev.splitCellImages || []).map(c =>
          c.index === cellIndex ? { ...c, url: imageUrl } : c
        );
        const regen = new Set(prev.regeneratingCells || []);
        regen.delete(cellIndex);
        return { ...prev, splitCellImages: updated, regeneratingCells: regen };
      });
      toast.success(`格${cellIndex + 1} 重新生成完成`);
    } catch (err) {
      console.error('单格重新生成失败:', err);
      handleUpdateNode(nodeId, (prev) => {
        const regen = new Set(prev.regeneratingCells || []);
        regen.delete(cellIndex);
        return { ...prev, regeneratingCells: regen };
      });
      toast.error('重新生成失败: ' + (err.message || '请重试'));
    }
  }, [edges, nodes, handleUpdateNode, toast, projectId, episodeId, currentStyle]);
  const handleRegenerateCellRef = useRef(null);

  // 导出单格为独立 resource 节点
  const handleExportCell = useCallback(async (nodeId, { cellIndex, url, name }) => {
    const sourceNode = getNodes().find(n => n.id === nodeId);
    if (!sourceNode) return;

    recordNow(); // 导出（节点+边）算一步

    const position = {
      x: sourceNode.position.x + (sourceNode.measured?.width || 500) + 50,
      y: sourceNode.position.y + cellIndex * 120,
    };

    const nodeData = {
      resourceType: "image",
      name: name || `格${cellIndex + 1}`,
      url,
      thumbnail: url,
      source: 'grid-export',
    };
    const tempId = `node-${nodeIdCounter}`;
    const newNode = {
      id: tempId,
      type: "atom",
      position,
      data: {
        ...nodeData,
        legacyType: "resource",
        normalized: normalizeNodeData(nodeData, "resource"),
        onImageClick: (u, n, mediaType) => setImagePreview({ url: u, name: n, mediaType }),
        onToggleExpand: (nodeId2) => toggleNodeExpandRef.current?.(nodeId2),
        onUpdate: (nodeId2, newData) => handleUpdateNodeRef.current?.(nodeId2, newData),
        onGenerate: (nodeId2, formData) => handleNodeGenerateRef.current?.(nodeId2, formData),
        onExportResult: (nodeId2) => handleExportResultRef.current?.(nodeId2),
      },
    };

    setNodes((nds) => {
      const next = [...nds, newNode];
      nodesRef.current = next;
      return next;
    });
    setNodeIdCounter((c) => c + 1);

    try {
      const savedNode = await onSaveNode({
        canvasId,
        nodeType: "resource",
        positionX: position.x,
        positionY: position.y,
        data: JSON.stringify({ resourceType: "image", name: name || `格${cellIndex + 1}`, url, thumbnail: url, source: 'grid-export' }),
      });

      let realId = tempId;
      if (savedNode?.id) {
        realId = savedNode.id.toString();
        commitTempNodeId(tempId, realId);
      }

      // 创建 output 连线
      const edge = {
        id: `e-${nodeId}-${realId}`,
        source: nodeId,
        target: realId,
        data: { connectionType: 'output' },
        style: EDGE_STYLE_OUTPUT,
        animated: false,
        deletable: true,
      };
      setEdges((eds) => [...eds, edge]);

      await persistConnection({
        source: nodeId,
        target: realId,
        connectionType: 'output',
      });

      toast.success(`格${cellIndex + 1} 已导出为独立节点`);
    } catch (err) {
      console.error('导出节点失败:', err);
      toast.error('导出失败: ' + (err.message || '请重试'));
    }
  }, [getNodes, nodeIdCounter, setNodes, setNodeIdCounter, setEdges, onSaveNode, recordNow, persistConnection, commitTempNodeId, canvasId, toast]);
  const handleExportCellRef = useRef(null);

  // 超分单格图片（走后端任务队列）
  const handleUpscaleCell = useCallback(async (nodeId, { cellIndex, url }) => {
    // 标记超分中
    setNodes((nds) => nds.map((n) => {
      if (n.id !== nodeId) return n;
      const prev = n.data.upscalingCells || new Set();
      return { ...n, data: { ...n.data, upscalingCells: new Set([...prev, cellIndex]) } };
    }));

    try {
      // 提交到后端任务队列
      const { taskId } = await submitTask("SUPER_RESOLUTION", {
        imageUrl: url,
        resolution: "4k",
        scale: 50,
      }, { provider: getCurrentImageProvider() });

      // 轮询任务状态
      startTaskPolling(taskId, {
        onSuccess: (result) => {
          const upscaledUrl = result?.imageUrl;
          if (upscaledUrl) {
            setNodes((nds) => nds.map((n) => {
              if (n.id !== nodeId) return n;
              const updatedCells = (n.data.splitCellImages || []).map(c =>
                c.index === cellIndex ? { ...c, url: upscaledUrl } : c
              );
              const upscaling = new Set(n.data.upscalingCells || []);
              upscaling.delete(cellIndex);
              return { ...n, data: { ...n.data, splitCellImages: updatedCells, upscalingCells: upscaling } };
            }));
            toast.success(`格${cellIndex + 1} 超分完成`);
          }
        },
        onError: (errMsg) => {
          toast.error('超分失败: ' + errMsg);
          setNodes((nds) => nds.map((n) => {
            if (n.id !== nodeId) return n;
            const upscaling = new Set(n.data.upscalingCells || []);
            upscaling.delete(cellIndex);
            return { ...n, data: { ...n.data, upscalingCells: upscaling } };
          }));
        },
      });
    } catch (e) {
      console.error('[Upscale] 提交失败:', e);
      toast.error('超分提交失败: ' + e.message);
      setNodes((nds) => nds.map((n) => {
        if (n.id !== nodeId) return n;
        const upscaling = new Set(n.data.upscalingCells || []);
        upscaling.delete(cellIndex);
        return { ...n, data: { ...n.data, upscalingCells: upscaling } };
      }));
    }
  }, [setNodes, toast]);
  const handleUpscaleCellRef = useRef(null);

  // 保持 ref 始终指向最新的回调
  handleUpdateNodeRef.current = handleUpdateNode;
  handleNodeGenerateRef.current = handleNodeGenerate;
  handleRegeneratePromptsRef.current = handleRegeneratePrompts;

  // P2-1 就地更新模型：generate 节点的“导出为素材节点”——按需产出独立素材节点 + output 边
  // （spawnGeneratedResource 内已有 sourceNodeId+url 幂等查重，重复点击不会产生重复节点）
  handleExportResultRef.current = (nodeId) => {
    const node = nodesRef.current.find((n) => n.id === nodeId);
    const d = node?.data || {};
    const url = getCurrentGenerationHistoryEntry(d)?.url || d.lastResultUrl;
    if (!url) return;
    const isVideoGen = d.generateType === 'video';
    spawnGeneratedResource(nodeId, {
      resourceType: isVideoGen ? 'video' : 'image',
      url,
      thumbnail: url,
      ...(isVideoGen && d.videoDuration != null ? { duration: d.videoDuration } : {}),
      name: `AI生成-${(d.prompt || '').substring(0, 20) || '未命名'}`,
      extraData: isVideoGen ? {
        prompt: d.prompt,
        generatedAt: d.lastGeneratedAt,
        originalVideoUrl: d.originalVideoUrl,
        videoProvider: d.videoProvider,
      } : { prompt: d.prompt },
    });
  };
  handleSplitGridRef.current = handleSplitGrid;
  handleRegenerateCellRef.current = handleRegenerateCell;
  handleExportCellRef.current = handleExportCell;
  handleUpscaleCellRef.current = handleUpscaleCell;

  // 删除节点
  const handleDeleteNode = useCallback(async (nodeId) => {
    recordNow();
    // 片4：删除分组框时子节点脱离分组（保留在画布）
    const target = nodesRef.current.find((n) => n.id === nodeId);
    if ((target?.data?.legacyType || target?.type) === 'groupFrame') {
      orphanChildrenOf([nodeId]);
    }
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    // temp 节点（尚未保存到后端）只清理本地状态
    if (isTempNodeId(nodeId)) {
      console.warn('[Canvas] 跳过删除 temp 节点（尚未保存到后端）:', nodeId);
      return;
    }
    try {
      await onDeleteNode(nodeId);
    } catch (e) {
      console.error('[Canvas] 删除节点失败:', e);
    }
  }, [setNodes, setEdges, onDeleteNode, recordNow, orphanChildrenOf]);

  // 批量删除节点
  const handleBatchDeleteNodes = useCallback(async (nodeIds) => {
    recordNow();
    // 片4：批量删除含分组框时，幸存子节点脱离分组（被一并删除的子节点除外）
    const removedFrameIds = nodesRef.current
      .filter((n) => nodeIds.includes(n.id) && (n.data?.legacyType || n.type) === 'groupFrame')
      .map((n) => n.id);
    if (removedFrameIds.length > 0) {
      orphanChildrenOf(removedFrameIds, { excludeIds: nodeIds });
    }
    setNodes((nds) => nds.filter((n) => !nodeIds.includes(n.id)));
    setEdges((eds) => eds.filter((e) => !nodeIds.includes(e.source) && !nodeIds.includes(e.target)));
    for (const nid of nodeIds) {
      // temp 节点（尚未保存到后端）只清理本地状态
      if (isTempNodeId(nid)) continue;
      try { await onDeleteNode(nid); } catch (e) { console.error('[Canvas] 批量删除节点失败:', e); }
    }
    setSelectedNodes([]);
    toast.success(`已删除 ${nodeIds.length} 个节点`);
  }, [setNodes, setEdges, onDeleteNode, toast, recordNow, orphanChildrenOf]);

  // 显示九宫格模板选择菜单

  // 右键菜单
  const onPaneContextMenu = useCallback((event) => {
    event.preventDefault();

    const bounds = reactFlowWrapper.current.getBoundingClientRect();
    const menuX = event.clientX - bounds.left;
    const menuY = event.clientY - bounds.top;

    // 检查是否有选中的资源节点或已完成的生成节点
    const selectedResourceNodes = selectedNodes.filter(id => {
      const node = nodes.find(n => n.id === id);
      // 资源节点 或 已完成的生成节点（P2-1 后结果存 lastResultUrl，旧数据兼容 result）
      return node && (
        isNodeType(node, "resource") ||
        (isNodeType(node, "generate") && (node.data?.lastResultUrl || node.data?.result))
      );
    });

    // 如果有选中的资源节点，显示生成菜单
    if (selectedResourceNodes.length > 0) {
      const canvasPosition = screenToFlowPosition({
        x: menuX,
        y: menuY,
      });

      setShowGenerateTypeMenu({
        x: event.clientX,
        y: event.clientY,
        sourceNodeIds: selectedResourceNodes,
        allSelectedNodeIds: [...selectedNodes],
        position: canvasPosition,
      });
    } else {
      // 否则显示创建节点菜单（wrapper 相对坐标，按画布可视区钳位）
      setContextMenu({
        x: Math.max(4, Math.min(menuX, bounds.width - 200)),
        y: Math.max(4, Math.min(menuY, bounds.height - 320)),
      });
    }
  }, [selectedNodes, nodes, screenToFlowPosition]);

  // 框选后右键菜单（ReactFlow 框选区域的右键事件不走 onPaneContextMenu）
  const onSelectionContextMenu = useCallback((event) => {
    event.preventDefault();

    if (selectedNodes.length === 0) return;

    // 检查是否有选中的分镜节点（用于 Seedance 合并）
    const selectedShotNodes = selectedNodes.filter(id => {
      const node = nodes.find(n => n.id === id);
      return node && isNodeType(node, "storyboardShot");
    });

    if (selectedShotNodes.length >= 2) {
      return;
    }

    const selectedResourceNodes = selectedNodes.filter(id => {
      const node = nodes.find(n => n.id === id);
      return node && (
        isNodeType(node, "resource") ||
        (isNodeType(node, "generate") && (node.data?.lastResultUrl || node.data?.result))
      );
    });

    const bounds = reactFlowWrapper.current.getBoundingClientRect();
    const canvasPosition = screenToFlowPosition({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    });

    // 收集有图片的节点
    const imagesForRoughCut = [];
    selectedNodes.forEach(id => {
      const node = nodes.find(n => n.id === id);
      if (!node) return;
      if (isNodeType(node, "resource") && node.data?.url && node.data?.resourceType === "image") {
        imagesForRoughCut.push({ url: node.data.url, name: node.data.name || "资源" });
      } else if (isNodeType(node, "generate") && (node.data?.lastResultUrl || node.data?.result) && node.data?.generateType === "image") {
        imagesForRoughCut.push({ url: node.data.lastResultUrl || node.data.result, name: node.data.prompt || "AI生成" });
      } else if (isNodeType(node, "storyboardShot") && node.data?.generatedImage) {
        imagesForRoughCut.push({ url: node.data.generatedImage, name: `分镜#${node.data.shotNumber}` });
      }
    });

    setShowGenerateTypeMenu({
      x: event.clientX,
      y: event.clientY,
      sourceNodeIds: selectedResourceNodes,
      allSelectedNodeIds: [...selectedNodes],
      position: canvasPosition,
      imagesForRoughCut,
    });
  }, [selectedNodes, nodes, screenToFlowPosition]);

  // 关闭右键菜单
  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
    setHoveredSubmenu(null);
  }, []);

  // 关闭节点右键菜单
  const closeNodeContextMenu = useCallback(() => {
    setNodeContextMenu(null);
  }, []);

  // 加入粗剪台（单个）— 粗剪台是按集的功能，项目级画布（无 episodeId）下不可用
  const handleAddToRoughCut = useCallback(async (url, name) => {
    if (!episodeId) { toast.warning('项目画布暂不支持粗剪台，请在单集工作台中操作'); return; }
    if (!url) { toast.error('该节点没有可用的图片'); return; }
    try {
      const project = await authenticatedFetch(`${API_HOST}/api/rough-cut/episode/${episodeId}`);
      const imageItems = (project.items || []).filter(it => it.trackType === 'image');
      const lastEnd = imageItems.reduce((m, it) => Math.max(m, (it.startTime || 0) + (it.duration || 0)), 0);
      await authenticatedFetch(`${API_HOST}/api/rough-cut/item`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: project.id,
          trackType: 'image',
          trackIndex: 0,
          startTime: lastEnd,
          duration: 3,
          resourceUrl: url,
          resourceName: name || '画布素材',
          sortOrder: 0,
        }),
      });
      toast.success('已加入粗剪台');
    } catch (e) {
      toast.error('加入粗剪台失败: ' + (e.message || ''));
    }
  }, [episodeId, toast]);

  // 批量加入粗剪台（框选）— 同上，项目级画布下不可用
  const handleBatchAddToRoughCut = useCallback(async (images) => {
    if (!episodeId) { toast.warning('项目画布暂不支持粗剪台，请在单集工作台中操作'); return; }
    if (!images || images.length === 0) return;
    try {
      const project = await authenticatedFetch(`${API_HOST}/api/rough-cut/episode/${episodeId}`);
      const existingItems = (project.items || []).filter(it => it.trackType === 'image');
      let currentEnd = existingItems.reduce((m, it) => Math.max(m, (it.startTime || 0) + (it.duration || 0)), 0);
      for (const img of images) {
        await authenticatedFetch(`${API_HOST}/api/rough-cut/item`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            projectId: project.id,
            trackType: 'image',
            trackIndex: 0,
            startTime: currentEnd,
            duration: 3,
            resourceUrl: img.url,
            resourceName: img.name || '画布素材',
            sortOrder: 0,
          }),
        });
        currentEnd += 3;
      }
      toast.success(`已将 ${images.length} 张图片加入粗剪台`);
    } catch (e) {
      toast.error('加入粗剪台失败: ' + (e.message || ''));
    }
  }, [episodeId, toast]);

  // 节点右键菜单处理
  const onNodeContextMenu = useCallback((event, node) => {
    event.preventDefault();
    event.stopPropagation();

    const { x, y } = clampMenuToViewport(event.clientX, event.clientY, 210, 340);
    setNodeContextMenu({
      x,
      y,
      node: node,
    });
  }, []);

  // 创建新节点
  const createNode = useCallback(async (type, data, customPosition = null) => {
    recordNow();
    let position;

    if (customPosition) {
      // 使用自定义位置
      position = customPosition;
    } else if (contextMenu) {
      // 使用右键菜单位置
      position = screenToFlowPosition({
        x: contextMenu.x,
        y: contextMenu.y,
      });
    } else {
      // 使用画布中心位置
      const bounds = reactFlowWrapper.current?.getBoundingClientRect();
      if (bounds) {
        position = screenToFlowPosition({
          x: bounds.width / 2,
          y: bounds.height / 2,
        });
      } else {
        position = { x: 100, y: 100 }; // 默认位置
      }
    }

    // resource / generate / storyboard* / scriptToStoryboard / aiDirector 走 atom 渲染
    const ATOM_TYPES = new Set(["resource", "generate", "storyboardShot", "seedanceMerge", "motionStoryboard", "storyboardGrid", "scriptToStoryboard", "aiDirector"]);
    const useAtomType = ATOM_TYPES.has(type);
    const renderType = useAtomType ? "atom" : type;
    const normalized = useAtomType ? normalizeNodeData(data, type) : null;

    const newNode = {
      id: `node-${nodeIdCounter}`,
      type: renderType,
      position,
      data: {
        ...data,
        normalized,
        legacyType: type,
        onImageClick: (url, name, mediaType) => setImagePreview({ url, name, mediaType }),
        onToggleExpand: (nodeId) => toggleNodeExpandRef.current?.(nodeId),
        onUpdate: (nodeId, newData) => handleUpdateNodeRef.current?.(nodeId, newData),
        onGenerate: (nodeId, formData) => handleNodeGenerateRef.current?.(nodeId, formData),
        onExportResult: (nodeId) => handleExportResultRef.current?.(nodeId),
        // 容器节点专用
        ...((type === "storyboardGrid" || type === "storyboardShot" || type === "seedanceMerge" || type === "storyboardEpisode") ? {
          onSplitGrid: (nodeId, params) => handleSplitGridRef.current?.(nodeId, params),
        } : {}),
        // 剧本转分镜节点专用回调
        ...(type === "scriptToStoryboard" ? {
          onSmartMatch: (nodeId) => handleSmartMatchRef.current?.(nodeId),
          onGenerateMasterShots: (nodeId, opts) => handleGenerateMasterShotsRef.current?.(nodeId, opts),
          onGenerateConnectingShots: (nodeId) => handleGenerateConnectingShotsRef.current?.(nodeId),
          onCreateBlankStoryboardShot: (nodeId, opts) => handleCreateBlankStoryboardShotRef.current?.(nodeId, opts),
          onSyncShotReferences: (nodeId) => handleSyncShotReferencesRef.current?.(nodeId),
          onArrangeStoryboard: (nodeId) => handleArrangeStoryboardRef.current?.(nodeId),
          onResumeStoryboardTask: (nodeId) => handleResumeStoryboardTaskRef.current?.(nodeId),
        } : {}),
        // motionStoryboard 节点需要 onOpenEditor 回调
        ...(type === "motionStoryboard" ? {
          onOpenEditor: (nodeId) => {
            const node = getNodes().find(n => n.id === nodeId);
            if (node) setMotionEditor({ nodeId, nodeData: node.data });
          },
          onUpdate: (nodeId, newData) => handleUpdateNodeRef.current?.(nodeId, newData),
        } : {}),
      },
    };

    setNodes((nds) => [...nds, newNode]);
    setNodeIdCounter((c) => c + 1);
    closeContextMenu();

    // 保存到后端
    try {
      const savedNode = await onSaveNode({
        canvasId: canvasId,
        nodeType: type,
        positionX: position.x,
        positionY: position.y,
        data: JSON.stringify(data),
      });

      // 更新节点 ID 为后端返回的 ID（并重映射本地/排队连线）
      if (savedNode && savedNode.id) {
        commitTempNodeId(newNode.id, savedNode.id.toString());
      }
    } catch (error) {
      console.error("保存节点失败:", error);
    }
  }, [contextMenu, nodeIdCounter, screenToFlowPosition, setNodes, closeContextMenu, onSaveNode, recordNow, commitTempNodeId, canvasId, reactFlowWrapper, getNodes]);

  // ── 片4：分组框（groupFrame）──────────────────────────────────────
  // 创建分组框：有选中节点时按选中集包围盒创建并自动归入；否则在菜单/画布中心建空框
  const createGroupFrame = useCallback(async () => {
    recordNow();
    // 可归入成员：选中的顶层节点（分组框本身与已在组内的子节点除外）
    const members = nodesRef.current.filter((n) =>
      selectedNodes.includes(n.id) && n.type !== 'groupFrame' && !n.parentId
    );

    let bounds;
    if (members.length > 0) {
      const PAD_X = 24, PAD_TOP = 44, PAD_BOTTOM = 24;
      const minX = Math.min(...members.map((n) => n.position.x));
      const minY = Math.min(...members.map((n) => n.position.y));
      const maxX = Math.max(...members.map((n) => n.position.x + (n.measured?.width || 280)));
      const maxY = Math.max(...members.map((n) => n.position.y + (n.measured?.height || 200)));
      bounds = {
        x: minX - PAD_X,
        y: minY - PAD_TOP,
        width: (maxX - minX) + PAD_X * 2,
        height: (maxY - minY) + PAD_TOP + PAD_BOTTOM,
      };
    } else {
      const pos = contextMenu
        ? screenToFlowPosition({ x: contextMenu.x, y: contextMenu.y })
        : (() => {
          const b = reactFlowWrapper.current?.getBoundingClientRect();
          return b ? screenToFlowPosition({ x: b.width / 2, y: b.height / 2 }) : { x: 100, y: 100 };
        })();
      bounds = { x: pos.x, y: pos.y, width: 480, height: 320 };
    }

    const tempId = `node-${nodeIdCounter}`;
    setNodeIdCounter((c) => c + 1);
    const frameData = {
      name: '分组',
      groupColor: 'blue',
      frameWidth: bounds.width,
      frameHeight: bounds.height,
      legacyType: 'groupFrame',
      onUpdate: (nid, nd) => handleUpdateNodeRef.current?.(nid, nd),
    };
    const memberIds = new Set(members.map((m) => m.id));

    // 成员归入：parentId + extent:'parent'，position 转为相对框坐标；框置顶数组首位（父先子后）
    setNodes((nds) => {
      const reassigned = nds.map((n) => (memberIds.has(n.id) ? {
        ...n,
        parentId: tempId,
        extent: 'parent',
        position: { x: n.position.x - bounds.x, y: n.position.y - bounds.y },
        data: { ...n.data, parentId: tempId },
      } : n));
      const frameNode = {
        id: tempId,
        type: 'groupFrame',
        position: { x: bounds.x, y: bounds.y },
        zIndex: -1,
        style: { width: bounds.width, height: bounds.height },
        data: frameData,
      };
      return [frameNode, ...reassigned];
    });
    closeContextMenu();

    // 持久化框；真实 id 回写时 commitTempNodeId 会重映射子节点 parentId 并标脏补存
    try {
      const saved = await onSaveNode({
        canvasId,
        nodeType: 'groupFrame',
        positionX: bounds.x,
        positionY: bounds.y,
        data: JSON.stringify(stripNonSerializable(frameData)),
      });
      if (saved?.id) {
        commitTempNodeId(tempId, saved.id.toString());
      }
    } catch (e) {
      console.error('[Canvas] 保存分组框失败:', e);
    }
  }, [selectedNodes, contextMenu, nodeIdCounter, screenToFlowPosition, setNodes, closeContextMenu, onSaveNode, recordNow, commitTempNodeId, canvasId]);

  // 片4：移出分组（节点右键项）— 坐标换算回绝对，parentId 清除并标脏补存
  const handleRemoveFromGroup = useCallback((node) => {
    const fid = node.parentId || node.data?.parentId;
    if (!fid) return;
    recordNow();
    const frame = nodesRef.current.find((n) => n.id === fid);
    setNodes((nds) => nds.map((n) => {
      if (n.id !== node.id) return n;
      const absPos = frame
        ? { x: frame.position.x + n.position.x, y: frame.position.y + n.position.y }
        : n.position;
      const data = { ...n.data };
      delete data.parentId;
      return { ...n, parentId: undefined, extent: undefined, position: absPos, data };
    }));
    saveQueue.markDirty(node.id);
    saveQueue.flush();
  }, [recordNow, setNodes, saveQueue]);

  // 片4：拖拽落点命中分组框 → 自动归入（子节点 extent:'parent' 钳制在框内，移出走右键"移出分组"）
  const handleNodeDragStop = useCallback((event, node, draggedNodes) => {
    const dragged = (Array.isArray(draggedNodes) && draggedNodes.length > 0 ? draggedNodes : [node])
      .filter((n) => n && n.type !== 'groupFrame');
    if (dragged.length === 0) return;
    const frames = nodesRef.current.filter((n) => n.type === 'groupFrame');
    if (frames.length === 0) return;

    const assignments = [];
    for (const dn of dragged) {
      // 子节点 position 相对父节点：先换算绝对坐标做命中测试
      const parent = dn.parentId ? nodesRef.current.find((n) => n.id === dn.parentId) : null;
      const absX = (parent?.position.x || 0) + dn.position.x;
      const absY = (parent?.position.y || 0) + dn.position.y;
      const w = dn.measured?.width || 280;
      const h = dn.measured?.height || 200;
      const cx = absX + w / 2;
      const cy = absY + h / 2;
      const hit = frames.find((f) => {
        if (f.id === dn.id) return false;
        const fw = f.measured?.width || f.data?.frameWidth || 480;
        const fh = f.measured?.height || f.data?.frameHeight || 360;
        return cx >= f.position.x && cx <= f.position.x + fw && cy >= f.position.y && cy <= f.position.y + fh;
      });
      if (hit && dn.parentId !== hit.id) {
        assignments.push({ id: dn.id, frameId: hit.id, absX, absY });
      }
    }
    if (assignments.length === 0) return;

    setNodes((nds) => {
      const next = nds.map((n) => {
        const a = assignments.find((x) => x.id === n.id);
        if (!a) return n;
        const frame = frames.find((f) => f.id === a.frameId);
        return {
          ...n,
          parentId: a.frameId,
          extent: 'parent',
          position: { x: a.absX - frame.position.x, y: a.absY - frame.position.y },
          data: { ...n.data, parentId: a.frameId },
        };
      });
      // 父节点需排在子节点之前
      return [
        ...next.filter((n) => n.type === 'groupFrame'),
        ...next.filter((n) => n.type !== 'groupFrame'),
      ];
    });
    assignments.forEach((a) => saveQueue.markDirty(a.id));
    saveQueue.flush();
  }, [setNodes, saveQueue]);
  // ── 片4 分组框结束 ────────────────────────────────────────────────

  // 从资源库选择
  const handleSelectFromLibrary = useCallback((resource) => {
    // 将后端的 typeCode 转换为前端的 resourceType（typeCode 为小写码，统一小写匹配；
    // 音频族：audio/bgm/music/sound/dialogue/narration）
    const code = (resource.typeCode || "").toLowerCase();
    let resourceType = "image";
    if (code.includes("video")) {
      resourceType = "video";
    } else if (code.includes("audio") || code.includes("bgm") || code.includes("music") || code.includes("sound") || code.includes("dialogue") || code.includes("narration")) {
      resourceType = "audio";
    } else if (code === "text") {
      resourceType = "text";
    }

    createNode("resource", {
      resourceType: resourceType,
      name: resource.name,
      thumbnail: resource.thumbnailUrl,
      duration: resource.duration,
      resourceId: resource.id,
      url: resource.url,
      assetType: resource.source, // 保存资产类型：character/scene/prop
      // 入库状态随资源带入，用于节点绿框/已入库标志与视频生成时的 assetId
      guaVideoAssetId: resource.guaVideoAssetId || '',
      guaVideoAssetStatus: resource.guaVideoAssetStatus || '',
      engGuaVideoAssetId: resource.engGuaVideoAssetId || '',
      engGuaVideoAssetStatus: resource.engGuaVideoAssetStatus || '',
      guaOminiAssetId: resource.guaOminiAssetId || '',
      guaOminiAssetStatus: resource.guaOminiAssetStatus || '',
      dreaminaProxyAssetId: resource.dreaminaProxyAssetId || '',
      dreaminaProxyAssetStatus: resource.dreaminaProxyAssetStatus || '',
      assetProvider: (() => {
        const pid = projectId || localStorage.getItem('currentProjectId');
        return pid ? getProjectVideoProvider(pid) : '';
      })(),
    });
    setShowResourceLibrary(false);
  }, [createNode, projectId]);

  // 处理本地文件上传（即时占位：每个文件一个 loading 占位节点，成功就地转正，失败移除）
  const handleFileUpload = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    // 立即清空文件输入，允许重复上传同一文件
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (isUploadingRef.current) return; // flag 守卫：上传期间禁止重复选择
    isUploadingRef.current = true;
    recordNow(); // 整批上传算一步

    try {
      // 占位节点起始位置：画布中心，逐文件斜向级联
      const bounds = reactFlowWrapper.current?.getBoundingClientRect();
      const basePos = bounds
        ? screenToFlowPosition({ x: bounds.width / 2, y: bounds.height / 2 })
        : { x: 100, y: 100 };

      let counter = nodeIdCounter;
      const placeholders = files.map((file, i) => {
        // 判断文件类型
        let resourceType = "image";
        if (file.type.startsWith("video/")) {
          resourceType = "video";
        } else if (file.type.startsWith("audio/")) {
          resourceType = "audio";
        }
        const placeholderData = { resourceType, name: file.name, uploading: true, source: 'canvas-upload' };
        const tempId = `node-${counter++}`;
        return {
          file,
          resourceType,
          tempId,
          position: { x: basePos.x + i * 40, y: basePos.y + i * 60 },
          node: {
            id: tempId,
            type: "atom",
            position: { x: basePos.x + i * 40, y: basePos.y + i * 60 },
            data: {
              ...placeholderData,
              legacyType: "resource",
              normalized: normalizeNodeData(placeholderData, "resource"),
              onImageClick: (url, name, mediaType) => setImagePreview({ url, name, mediaType }),
              onToggleExpand: (nid) => toggleNodeExpandRef.current?.(nid),
              onUpdate: (nid, nd) => handleUpdateNodeRef.current?.(nid, nd),
              onGenerate: (nid, fd) => handleNodeGenerateRef.current?.(nid, fd),
              onExportResult: (nid) => handleExportResultRef.current?.(nid),
            },
          },
        };
      });
      setNodeIdCounter(counter);
      setNodes((nds) => [...nds, ...placeholders.map((p) => p.node)]);

      // 并发上传：成功 → URL 就地转正 + 走正常保存；失败 → 移除占位节点
      await Promise.all(placeholders.map(async (p) => {
        try {
          // 创建 FormData 上传文件到 OSS
          const formData = new FormData();
          formData.append("file", p.file);

          const fileUrl = await authenticatedFetch(`${API_HOST}/api/upload/asset`, {
            method: "POST",
            body: formData,
          });

          if (!fileUrl || typeof fileUrl !== 'string') {
            throw new Error("上传失败，未返回文件地址");
          }

          // 用返回 URL 更新占位节点（去掉 uploading）
          const nextData = {
            resourceType: p.resourceType,
            name: p.file.name,
            url: fileUrl,
            thumbnail: fileUrl,
            source: 'canvas-upload',
          };
          setNodes((nds) => nds.map((n) => (n.id === p.tempId ? {
            ...n,
            data: {
              ...n.data,
              ...nextData,
              uploading: false,
              normalized: normalizeNodeData(nextData, "resource"),
            },
          } : n)));

          // 走正常保存：拿到真实 id 后重映射（与 createNode 一致）
          const savedNode = await onSaveNode({
            canvasId: canvasId,
            nodeType: "resource",
            positionX: p.position.x,
            positionY: p.position.y,
            data: JSON.stringify(nextData),
          });
          if (savedNode && savedNode.id) {
            commitTempNodeId(p.tempId, savedNode.id.toString());
          }
        } catch (error) {
          console.error("文件上传失败:", error);
          toast.error(`「${p.file.name}」上传失败，请重试`, 0);
          // 失败：移除占位节点
          setNodes((nds) => nds.filter((n) => n.id !== p.tempId));
        }
      }));
    } finally {
      isUploadingRef.current = false;
    }
  }, [canvasId, commitTempNodeId, nodeIdCounter, onSaveNode, reactFlowWrapper, recordNow, screenToFlowPosition, setNodes, toast]);

  // 上传触发守卫：上传期间禁止重复选择
  const triggerFileSelect = useCallback(() => {
    if (isUploadingRef.current) {
      toast.info('文件上传中，请稍候...');
      return;
    }
    fileInputRef.current?.click();
  }, [toast]);

  // 处理 URL 链接
  const handleUrlSubmit = useCallback(async (url) => {
    if (!url) return;

    try {
      // 直接用 URL 保存到资源库
      const uploadedResource = await authenticatedFetch(`${API_HOST}/api/resource/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url,
          projectId,
          episodeId: episodeId || null,
          name: url.split('/').pop()?.split('?')[0] || 'URL资源',
          // 片3：按扩展名识别音频，落入 agent_audio（否则误归 agent_image）
          typeCode: url.match(/\.(mp4|mov|avi|webm|m4v)(\?|$)/i)
            ? 'agent_video'
            : url.match(/\.(mp3|wav|m4a|aac|ogg|flac)(\?|$)/i)
              ? 'agent_audio'
              : 'agent_image',
          source: 'canvas_url',
        }),
      });

      // 判断资源类型（typeCode 为小写码，统一小写匹配）
      const code = (uploadedResource.typeCode || "").toLowerCase();
      let resourceType = "image";
      if (code.includes("video")) {
        resourceType = "video";
      } else if (code.includes("audio")) {
        resourceType = "audio";
      }

      // 创建资源节点
      createNode("resource", {
        resourceType: resourceType,
        name: uploadedResource.name || "URL 资源",
        thumbnail: uploadedResource.thumbnailUrl,
        duration: uploadedResource.duration,
        resourceId: uploadedResource.id,
        url: uploadedResource.url,
      });

      setShowUrlDialog(false);
    } catch (error) {
      console.error("URL 资源获取失败:", error);
      toast.error("URL 资源获取失败，请检查链接是否有效", 0);
      setShowUrlDialog(false);
    }
  }, [createNode, projectId, episodeId, toast]);

  // 已导入的分镜 shotId 集合（用于导入面板标记已导入状态）
  const existingShotIds = React.useMemo(() => {
    const ids = new Set();
    nodes.forEach(n => {
      if (n.data?.nodeKind === 'storyboardShot' && n.data?.shotId) {
        ids.add(n.data.shotId.toString());
      }
    });
    return ids;
  }, [nodes]);

  // 导入分镜到画布
  const handleImportShots = useCallback(async (shots) => {
    recordNow(); // 整批导入算一步
    const COLS = 4;
    const SPACING_X = 260;
    const SPACING_Y = 300;
    // 起始位置：画布中心偏左上
    const bounds = reactFlowWrapper.current?.getBoundingClientRect();
    const startPos = bounds
      ? screenToFlowPosition({ x: bounds.width * 0.1, y: bounds.height * 0.1 })
      : { x: 50, y: 50 };

    let counter = nodeIdCounter;

    for (let i = 0; i < shots.length; i++) {
      const shot = shots[i];
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const position = {
        x: startPos.x + col * SPACING_X,
        y: startPos.y + row * SPACING_Y,
      };

      const nodeData = {
        nodeKind: "storyboardShot",
        shotId: shot.id,
        shotNumber: shot.shot_number,
        shotType: shot.shot,
        imagePrompt: shot.image_prompt,
        videoPrompt: shot.video_prompt,
        content: shot.content,
        generatedImage: shot.generated_image,
        generatedVideo: shot.generated_video,
        // 小故事板/导演故事台导入时透传时长（分镜导入无此字段，不影响旧行为）
        ...(shot.duration_seconds != null ? { durationSeconds: shot.duration_seconds } : {}),
      };

      const tempId = `node-${counter}`;
      counter++;

      // 与 convertToReactFlowNodes 的推断逻辑对齐：统一用 atom 渲染，
      // 真实类型放在 data.nodeKind / data.legacyType（nodeTypes 只注册了 atom / storyboardEpisode）
      const newNode = {
        id: tempId,
        type: "atom",
        position,
        data: {
          ...nodeData,
          legacyType: "storyboardShot",
          normalized: normalizeNodeData(nodeData, "storyboardShot"),
          onImageClick: (url, name, mediaType) => setImagePreview({ url, name, mediaType }),
          onToggleExpand: (nodeId) => toggleNodeExpandRef.current?.(nodeId),
          onUpdate: (nodeId, newData) => handleUpdateNodeRef.current?.(nodeId, newData),
          onGenerate: (nodeId, formData) => handleNodeGenerateRef.current?.(nodeId, formData),
          onExportResult: (nodeId) => handleExportResultRef.current?.(nodeId),
          onSplitGrid: (nodeId, params) => handleSplitGridRef.current?.(nodeId, params),
        },
      };

      setNodes(nds => [...nds, newNode]);

      // 保存到后端
      try {
        const saved = await onSaveNode({
          canvasId,
          nodeType: "storyboardShot",
          positionX: position.x,
          positionY: position.y,
          data: JSON.stringify(nodeData),
        });
        if (saved?.id) {
          const savedId = saved.id.toString();
          commitTempNodeId(tempId, savedId);
        }
      } catch (e) {
        console.error('保存分镜节点失败:', e);
      }
    }

    setNodeIdCounter(counter);
    toast.success(`已导入 ${shots.length} 个分镜到画布`);
  }, [nodeIdCounter, setNodes, onSaveNode, recordNow, commitTempNodeId, canvasId, screenToFlowPosition, toast]);

  // ── 剧本转分镜（scriptToStoryboard）──────────────────────────────────

  // 创建 atom 节点并立即持久化（返回真实 id；保存失败保留本地 temp 节点并返回 tempId）
  const createPersistedAtomNode = useCallback(async (nodeType, nodeData, position, tempId) => {
    const newNode = {
      id: tempId,
      type: "atom",
      position,
      data: {
        ...nodeData,
        legacyType: nodeType,
        normalized: normalizeNodeData(nodeData, nodeType),
        onImageClick: (url, name, mediaType) => setImagePreview({ url, name, mediaType }),
        onToggleExpand: (nid) => toggleNodeExpandRef.current?.(nid),
        onUpdate: (nid, nd) => handleUpdateNodeRef.current?.(nid, nd),
        onGenerate: (nid, fd) => handleNodeGenerateRef.current?.(nid, fd),
        onExportResult: (nid) => handleExportResultRef.current?.(nid),
        ...(nodeType === "storyboardShot" ? {
          onRegeneratePrompts: (nid, fd) => handleRegeneratePromptsRef.current?.(nid, fd),
          onSplitGrid: (nid, p) => handleSplitGridRef.current?.(nid, p),
        } : {}),
        ...(nodeType === "scriptToStoryboard" ? {
          onSmartMatch: (nid) => handleSmartMatchRef.current?.(nid),
          onGenerateMasterShots: (nid, opts) => handleGenerateMasterShotsRef.current?.(nid, opts),
          onGenerateConnectingShots: (nid) => handleGenerateConnectingShotsRef.current?.(nid),
          onCreateBlankStoryboardShot: (nid, opts) => handleCreateBlankStoryboardShotRef.current?.(nid, opts),
          onSyncShotReferences: (nid) => handleSyncShotReferencesRef.current?.(nid),
          onArrangeStoryboard: (nid) => handleArrangeStoryboardRef.current?.(nid),
          onResumeStoryboardTask: (nid) => handleResumeStoryboardTaskRef.current?.(nid),
        } : {}),
      },
    };
    setNodes((nds) => [...nds, newNode]);
    try {
      const saved = await onSaveNode({
        canvasId,
        nodeType,
        positionX: position.x,
        positionY: position.y,
        data: JSON.stringify(nodeData),
      });
      if (saved?.id) {
        const realId = saved.id.toString();
        commitTempNodeId(tempId, realId);
        return realId;
      }
    } catch (e) {
      console.error(`[Canvas] 保存 ${nodeType} 节点失败:`, e);
    }
    return tempId;
  }, [canvasId, onSaveNode, commitTempNodeId, setNodes]);

  // 建 reference 边并持久化（temp 端走保存队列）；真实 id 落库后边 id 替换为后端 id，保证后续可删除重连
  const addReferenceEdge = useCallback(async (source, target) => {
    if (!source || !target || source === target) return null;
    const dup = edgesRef.current.some((e) => e.source === source && e.target === target);
    if (dup) return null;
    const localId = `e-${source}-${target}-${Date.now()}`;
    setEdges((eds) => {
      const next = addEdge({
        id: localId,
        source,
        target,
        data: { connectionType: 'reference' },
        style: EDGE_STYLE_REFERENCE,
        animated: false,
        deletable: true,
      }, eds);
      edgesRef.current = next;
      return next;
    });
    if (isTempNodeId(source) || isTempNodeId(target)) {
      saveQueue.queueConnection({ source, target, sourceHandle: null, targetHandle: null, connectionType: 'reference' });
      return localId;
    }
    try {
      const saved = await onSaveConnection({
        canvasId,
        sourceNodeId: source,
        targetNodeId: target,
        sourcePort: "default",
        targetPort: "default",
        connectionType: "reference",
      });
      if (saved?.id) {
        const realEdgeId = saved.id.toString();
        setEdges((eds) => {
          const next = eds.map((e) => (e.id === localId ? { ...e, id: realEdgeId } : e));
          edgesRef.current = next;
          return next;
        });
        return realEdgeId;
      }
    } catch (e) {
      console.error("[Canvas] 保存连线失败:", e);
    }
    return localId;
  }, [canvasId, onSaveConnection, saveQueue, setEdges]);

  // 删除 reference 边（本地移除 + 后端 id 为数字时同步删除）
  const removeReferenceEdge = useCallback(async (edge) => {
    if (!edge) return;
    setEdges((eds) => {
      const next = eds.filter((e) => e.id !== edge.id);
      edgesRef.current = next;
      return next;
    });
    if (/^\d+$/.test(String(edge.id))) {
      try {
        await authenticatedFetch(`${API_HOST}/api/canvas/connection/${edge.id}`, { method: "DELETE" });
      } catch (e) {
        console.error("[Canvas] 删除连线失败:", e);
      }
    }
  }, [setEdges]);

  // 缩略图上的取消参考：只删除该来源资源到当前节点的 reference 边，不删除资源节点。
  const handleRemoveConnectedReference = useCallback(async (targetNodeId, reference) => {
    const targetId = String(targetNodeId);
    const sourceId = reference?.sourceNodeId != null ? String(reference.sourceNodeId) : null;
    const requestedEdgeIds = new Set((reference?.edgeIds || []).map((edgeId) => String(edgeId)));
    const matchedEdges = edgesRef.current.filter((edge) => {
      if (edge.target !== targetId || edge.data?.connectionType !== 'reference') return false;
      if (requestedEdgeIds.size > 0) return requestedEdgeIds.has(String(edge.id));
      return sourceId != null && edge.source === sourceId;
    });
    if (matchedEdges.length === 0) return;

    recordNow();
    const matchedIds = new Set(matchedEdges.map((edge) => String(edge.id)));
    setEdges((currentEdges) => {
      const next = currentEdges.filter((edge) => !matchedIds.has(String(edge.id)));
      edgesRef.current = next;
      return next;
    });

    const persistedEdges = matchedEdges.filter((edge) => /^\d+$/.test(String(edge.id)));
    const results = await Promise.allSettled(
      persistedEdges.map((edge) => authenticatedFetch(
        `${API_HOST}/api/canvas/connection/${edge.id}`,
        { method: "DELETE" }
      ))
    );
    if (results.some((result) => result.status === 'rejected')) {
      console.error("[Canvas] 部分参考连线删除失败:", results);
      toast.error("参考已从当前画布移除，但部分连线同步失败，请刷新确认");
      return;
    }
    toast.success("已取消参考");
  }, [recordNow, setEdges, toast]);

  handleRemoveReferenceRef.current = handleRemoveConnectedReference;

  const getIncomingAssetReferenceNodes = useCallback((targetNodeId) => {
    const targetId = String(targetNodeId);
    return uniqueNodesById(
      edgesRef.current
        .filter((edge) =>
          String(edge.target) === targetId
          && edge.data?.connectionType === 'reference'
        )
        .map((edge) => nodesRef.current.find((node) => String(node.id) === String(edge.source)))
        .filter((node) => node && STORYBOARD_ASSET_TYPES.has(node.data?.assetType))
    );
  }, []);

  const getScriptReferenceAssetNodes = useCallback(
    (scriptNodeId) => getIncomingAssetReferenceNodes(scriptNodeId),
    [getIncomingAssetReferenceNodes],
  );

  const resolveShotAssetReferenceNodes = useCallback((scriptNodeId, shot = {}, {
    allowLegacyTextFallback = false,
  } = {}) => {
    const assetPool = getScriptReferenceAssetNodes(scriptNodeId);
    const shotText = [
      shot.content,
      shot.imagePrompt,
      shot.storyPurpose,
      shot.subjectPositions,
      shot.composition,
      shot.notes,
    ].filter(Boolean).join('\n');
    const requestedNames = {
      character: normalizeShotNameList(shot.characters ?? shot.refCharacterNames),
      scene: normalizeShotNameList(shot.scenes ?? shot.refSceneNames),
      prop: normalizeShotNameList(shot.props ?? shot.refPropNames),
    };
    const hasStructuredField = {
      character: Object.prototype.hasOwnProperty.call(shot, 'characters')
        || Object.prototype.hasOwnProperty.call(shot, 'refCharacterNames'),
      scene: Object.prototype.hasOwnProperty.call(shot, 'scenes')
        || Object.prototype.hasOwnProperty.call(shot, 'refSceneNames'),
      prop: Object.prototype.hasOwnProperty.call(shot, 'props')
        || Object.prototype.hasOwnProperty.call(shot, 'refPropNames'),
    };

    return uniqueNodesById(assetPool.filter((assetNode) => {
      const assetType = assetNode.data?.assetType;
      const assetName = String(assetNode.data?.name || '').trim();
      if (!assetName || !requestedNames[assetType]) return false;
      const normalizedAssetName = normalizeAssetIdentity(assetName);
      const explicitlyRequested = requestedNames[assetType]
        .some((name) => normalizeAssetIdentity(name) === normalizedAssetName);
      if (explicitlyRequested) return true;
      return allowLegacyTextFallback
        && !hasStructuredField[assetType]
        && assetNameAppearsInText(assetName, shotText);
    }));
  }, [getScriptReferenceAssetNodes]);

  const buildShotReferencePatch = useCallback((assetNodes) => {
    const refs = uniqueNodesById(assetNodes);
    const byType = (assetType) => refs.filter((node) => node.data?.assetType === assetType);
    const characters = byType('character');
    const scenes = byType('scene');
    const props = byType('prop');
    return {
      refCharacterIds: characters.map((node) => node.data?.assetId).filter(Boolean),
      refSceneIds: scenes.map((node) => node.data?.assetId).filter(Boolean),
      refPropIds: props.map((node) => node.data?.assetId).filter(Boolean),
      refCharacterNames: characters.map((node) => node.data?.name).filter(Boolean),
      refSceneNames: scenes.map((node) => node.data?.name).filter(Boolean),
      refPropNames: props.map((node) => node.data?.name).filter(Boolean),
    };
  }, []);

  const getSharedAssetReferenceNodes = useCallback((leftNodeId, rightNodeId) => {
    if (!leftNodeId || !rightNodeId) return [];
    const left = getIncomingAssetReferenceNodes(leftNodeId);
    const rightIds = new Set(getIncomingAssetReferenceNodes(rightNodeId).map((node) => String(node.id)));
    return left.filter((node) => rightIds.has(String(node.id)));
  }, [getIncomingAssetReferenceNodes]);

  // 智能匹配：拉角色/场景/道具工坊列表 → 剧本正文精确包含匹配（大小写/空白归一）→ 左侧建参考节点并连线
  const handleScriptSmartMatch = useCallback(async (nodeId) => {
    const node = nodesRef.current.find((n) => n.id === nodeId);
    if (!node) return;
    const script = node.data?.scriptText || '';
    if (!script.trim()) { toast.warning('请先填写剧本正文'); return; }
    if (node.data?.smartMatching) return; // 幂等：匹配在飞
    if (isTempNodeId(nodeId)) { toast.warning('节点保存中，请稍候再试'); return; }
    if (!projectId) { toast.warning('缺少项目上下文，无法智能匹配'); return; }

    setNodes((nds) => nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, smartMatching: true } } : n)));
    try {
      const [characters, scenes, props] = await Promise.all([
        authenticatedFetch(`${API_HOST}/api/characters?projectId=${projectId}`),
        authenticatedFetch(`${API_HOST}/api/scenes?projectId=${projectId}`),
        authenticatedFetch(`${API_HOST}/api/props?projectId=${projectId}`),
      ]);
      const roster = [
        ...(Array.isArray(characters) ? characters : []).map((a) => ({ ...a, assetType: 'character' })),
        ...(Array.isArray(scenes) ? scenes : []).map((a) => ({ ...a, assetType: 'scene' })),
        ...(Array.isArray(props) ? props : []).map((a) => ({ ...a, assetType: 'prop' })),
      ];
      const hits = roster.filter((asset) => asset?.name && assetNameAppearsInText(asset.name, script));
      const missed = roster.filter((asset) => asset?.name && !assetNameAppearsInText(asset.name, script));

      // 已连入的同名同类参考不重复创建
      const incoming = edgesRef.current.filter((e) => e.target === nodeId && e.data?.connectionType === 'reference');
      const existingKeys = new Set(incoming.map((e) => {
        const src = nodesRef.current.find((n) => n.id === e.source);
        return src ? `${src.data?.assetType || ''}:${normalizeMatchText(src.data?.name)}` : '';
      }));
      const toCreate = hits.filter((a) => !existingKeys.has(`${a.assetType}:${normalizeMatchText(a.name)}`));
      if (toCreate.length > 0) recordNow(); // 整批匹配算一步

      // 布局：节点左侧，角色/场景/道具各一列
      const basePos = node.position;
      const colX = { character: basePos.x - 340, scene: basePos.x - 680, prop: basePos.x - 1020 };
      const colCount = { character: 0, scene: 0, prop: 0 };
      const counts = { character: 0, scene: 0, prop: 0 };
      let counter = nodeIdCounter;

      for (const asset of toCreate) {
        const idx = colCount[asset.assetType]++;
        const position = { x: colX[asset.assetType], y: basePos.y + idx * 300 };
        const nodeData = {
          resourceType: 'image',
          name: asset.name,
          url: asset.imageUrl || '',
          thumbnail: asset.imageUrl || '',
          description: asset.description || '',
          assetType: asset.assetType,
          assetId: asset.id,
          source: 'canvas-smart-match',
        };
        const tempId = `node-${counter++}`;
        const realId = await createPersistedAtomNode('resource', nodeData, position, tempId);
        await addReferenceEdge(realId, nodeId);
        counts[asset.assetType]++;
      }
      setNodeIdCounter(counter);

      const parts = [];
      if (counts.character) parts.push(`角色×${counts.character}`);
      if (counts.scene) parts.push(`场景×${counts.scene}`);
      if (counts.prop) parts.push(`道具×${counts.prop}`);
      const skipped = hits.length - toCreate.length;
      let msg = parts.length ? `匹配到 ${parts.join(' ')}` : '没有新的匹配项';
      if (skipped > 0) msg += `（${skipped} 个已存在）`;
      if (missed.length > 0) {
        msg += `；未命中：${missed.slice(0, 6).map((a) => a.name).join('、')}${missed.length > 6 ? ` 等${missed.length}个` : ''}`;
      }
      if (parts.length) toast.success(msg); else toast.info(msg);
    } catch (e) {
      console.error('[Canvas] 智能匹配失败:', e);
      toast.error('智能匹配失败: ' + (e.message || '请重试'));
    } finally {
      setNodes((nds) => nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, smartMatching: false } } : n)));
    }
  }, [projectId, nodeIdCounter, toast, recordNow, createPersistedAtomNode, addReferenceEdge, setNodes, setNodeIdCounter]);

  // 分镜序列按叙事顺序纵向放入一个可整体移动的 frame；镜头仍可通过右键移出分组自由编排。
  const arrangeStoryboardShots = useCallback(async (
    scriptNodeId,
    { recordHistory = true, silent = false } = {},
  ) => {
    const scriptNode = nodesRef.current.find((node) => String(node.id) === String(scriptNodeId));
    if (!scriptNode) return 0;

    const shots = nodesRef.current
      .filter((node) =>
        node.data?.nodeKind === 'storyboardShot'
        && String(node.data?.sourceScriptNodeId) === String(scriptNodeId)
      )
      .sort((left, right) => {
        const numberDiff = Number(left.data?.shotNumber || 0) - Number(right.data?.shotNumber || 0);
        return numberDiff || String(left.id).localeCompare(String(right.id));
      });
    if (shots.length === 0) {
      if (!silent) toast.info('当前还没有可整理的故事板镜头');
      return 0;
    }

    if (recordHistory) recordNow();
    const existingFrame = nodesRef.current.find((node) =>
      node.type === 'groupFrame'
      && node.data?.groupRole === STORYBOARD_GROUP_ROLE
      && String(node.data?.sourceScriptNodeId) === String(scriptNodeId)
    );
    const frameId = existingFrame?.id || `node-storyboard-group-${Date.now()}`;
    const framePosition = existingFrame?.position || {
      x: scriptNode.position.x + 420,
      y: scriptNode.position.y - 40,
    };
    const frameWidth = Math.max(
      STORYBOARD_GROUP_WIDTH,
      Number(existingFrame?.data?.frameWidth || 0),
    );

    let resolvedFrameId = frameId;
    let cursorY = STORYBOARD_GROUP_PADDING_TOP;
    const arrangedShotById = new Map();
    shots.forEach((shot) => {
      const measuredHeight = Number(shot.measured?.height || shot.height || STORYBOARD_SHOT_FALLBACK_HEIGHT);
      const shotHeight = Math.max(260, Math.min(measuredHeight, 620));
      arrangedShotById.set(shot.id, {
        ...shot,
        parentId: frameId,
        extent: 'parent',
        position: { x: STORYBOARD_GROUP_PADDING_X, y: cursorY },
        data: { ...shot.data, parentId: frameId },
      });
      cursorY += shotHeight + STORYBOARD_SHOT_GAP;
    });
    const frameHeight = cursorY - STORYBOARD_SHOT_GAP + STORYBOARD_GROUP_PADDING_BOTTOM;
    const existingFrameName = String(existingFrame?.data?.name || '').trim();
    const frameName = !existingFrameName || /^分镜序列 · \d+镜$/.test(existingFrameName)
      ? `分镜序列 · ${shots.length}镜`
      : existingFrameName;
    const frameData = {
      ...(existingFrame?.data || {}),
      name: frameName,
      groupColor: existingFrame?.data?.groupColor || 'orange',
      groupRole: STORYBOARD_GROUP_ROLE,
      layoutDirection: 'vertical',
      shotCount: shots.length,
      sourceScriptNodeId: scriptNodeId,
      frameWidth,
      frameHeight,
      legacyType: 'groupFrame',
      onUpdate: (nodeId, nextData) => handleUpdateNodeRef.current?.(nodeId, nextData),
    };
    const frameNode = {
      ...(existingFrame || {}),
      id: frameId,
      type: 'groupFrame',
      position: framePosition,
      zIndex: -1,
      style: { ...(existingFrame?.style || {}), width: frameWidth, height: frameHeight },
      data: frameData,
    };

    let arrangedNodes = [];
    setNodes((current) => {
      const rest = current
        .filter((node) => node.id !== frameId)
        .map((node) => arrangedShotById.get(node.id) || node);
      arrangedNodes = [frameNode, ...rest];
      nodesRef.current = arrangedNodes;
      return arrangedNodes;
    });

    if (existingFrame) {
      saveQueue.markDirty(frameId, {
        id: frameId,
        canvasId,
        nodeType: 'groupFrame',
        positionX: framePosition.x,
        positionY: framePosition.y,
        data: JSON.stringify(stripNonSerializable(frameData)),
      });
      arrangedShotById.forEach((shotNode, shotId) => {
        saveQueue.markDirty(shotId, {
          id: shotId,
          canvasId,
          nodeType: shotNode.data?.legacyType || shotNode.type,
          positionX: shotNode.position.x,
          positionY: shotNode.position.y,
          data: JSON.stringify(stripNonSerializable(shotNode.data)),
        });
      });
      saveQueue.flush();
    } else {
      try {
        const saved = await onSaveNode({
          canvasId,
          nodeType: 'groupFrame',
          positionX: framePosition.x,
          positionY: framePosition.y,
          data: JSON.stringify(stripNonSerializable(frameData)),
        });
        if (saved?.id) {
          resolvedFrameId = saved.id.toString();
          commitTempNodeId(frameId, resolvedFrameId);
        }
      } catch (error) {
        console.error('[Canvas] 保存分镜序列组失败:', error);
        if (!silent) toast.error('分镜已排列，但序列组保存失败');
      }
    }

    setTimeout(() => {
      updateNodeInternals(resolvedFrameId);
      shots.forEach((shot) => updateNodeInternals(shot.id));
    }, 50);
    if (!silent) toast.success(`已将 ${shots.length} 个镜头整理为纵向分镜序列`);
    return shots.length;
  }, [
    canvasId,
    commitTempNodeId,
    onSaveNode,
    recordNow,
    saveQueue,
    setNodes,
    toast,
    updateNodeInternals,
  ]);

  // 主镜落画布：先按纵向顺序创建，再归入分镜序列组；同时建立链式与资产参考边。
  const materializeMasterShots = useCallback(async (scriptNodeId, shots, aspectRatio) => {
    const scriptNode = nodesRef.current.find((n) => n.id === scriptNodeId);
    if (!scriptNode) return;
    recordNow(); // 整批主镜创建算一步

    const base = scriptNode.position;
    const startX = base.x + 420;
    const startY = base.y - 40;

    let counter = nodeIdCounter;
    const created = [];
    for (let i = 0; i < shots.length; i++) {
      const shot = shots[i];
      const position = {
        x: startX,
        y: startY + i * (STORYBOARD_SHOT_FALLBACK_HEIGHT + STORYBOARD_SHOT_GAP),
      };
      const assetRefNodes = resolveShotAssetReferenceNodes(scriptNodeId, shot);
      const referencePatch = buildShotReferencePatch(assetRefNodes);
      const nodeData = {
        nodeKind: 'storyboardShot',
        name: `主镜#${shot.shotNumber ?? i + 1}`,
        shotNumber: shot.shotNumber ?? i + 1,
        masterType: shot.masterType || '戏点',
        shotSize: shot.shotSize || '',
        pov: shot.pov || '',
        reason: shot.reason || '',
        content: shot.content || '',
        imagePrompt: shot.imagePrompt || shot.content || '',
        ...buildStoryboardDraftData(shot),
        aspectRatio,
        sourceScriptNodeId: scriptNodeId,
        ...referencePatch,
      };
      const tempId = `node-${counter++}`;
      const realId = await createPersistedAtomNode('storyboardShot', nodeData, position, tempId);
      created.push({ realId, assetRefNodes });
    }
    setNodeIdCounter(counter);

    // 链式 reference 边：剧本节点 → 主镜1 → 主镜2 …
    let prev = scriptNodeId;
    for (const c of created) {
      await addReferenceEdge(prev, c.realId);
      prev = c.realId;
    }
    // 资产参考边：涉及资产节点 → 对应主镜节点（storyboardShot 生图链路按连线收集参考图）
    for (const c of created) {
      for (const assetNode of c.assetRefNodes) {
        await addReferenceEdge(assetNode.id, c.realId);
      }
    }

    // 回写剧本节点：主镜节点 id 列表（面板"生成连接镜"可用态；后续由同步 effect 自动维护）
    handleUpdateNode(scriptNodeId, (prevData) => ({
      ...prevData,
      masterShotNodeIds: created.map((c) => c.realId),
    }));
    await arrangeStoryboardShots(scriptNodeId, { recordHistory: false, silent: true });
  }, [
    nodeIdCounter,
    recordNow,
    resolveShotAssetReferenceNodes,
    buildShotReferencePatch,
    createPersistedAtomNode,
    addReferenceEdge,
    handleUpdateNode,
    arrangeStoryboardShots,
    setNodeIdCounter,
  ]);

  // 连接镜落画布：插入对应主镜之后（位置插排 + 边重连 A→B 变 A→C→B）
  const materializeConnectingShots = useCallback(async (scriptNodeId, shots, aspectRatio) => {
    if (!shots.length) return 0;
    recordNow(); // 整批连接镜算一步

    // 按 afterMasterShotNumber 分组（组内保持 LLM 返回顺序）
    const groups = new Map();
    for (const s of shots) {
      const k = Number(s.afterMasterShotNumber);
      if (!Number.isFinite(k) || k <= 0) continue;
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(s);
    }
    if (groups.size === 0) return 0;

    let counter = nodeIdCounter;
    let createdCount = 0;

    for (const [afterNum, groupShots] of [...groups.entries()].sort((a, b) => a[0] - b[0])) {
      const masterNode = nodesRef.current.find((n) =>
        n.data?.sourceScriptNodeId === scriptNodeId && n.data?.masterType && Number(n.data?.shotNumber) === afterNum
      );
      if (!masterNode) continue;

      // 幂等：同主镜后同 purpose+content 的连接镜不重复创建
      const existing = nodesRef.current.filter((n) =>
        n.data?.sourceScriptNodeId === scriptNodeId && !n.data?.masterType &&
        Number(n.data?.afterMasterShotNumber) === afterNum
      );

      // 链后继：主镜出边中落在本组链路上的下一个 storyboardShot（主镜或此前插入的连接镜）
      const chainOut = edgesRef.current.find((e) => {
        if (e.source !== masterNode.id || e.data?.connectionType !== 'reference') return false;
        const tgt = nodesRef.current.find((n) => n.id === e.target);
        return tgt && tgt.data?.sourceScriptNodeId === scriptNodeId && tgt.data?.nodeKind === 'storyboardShot';
      });
      const succNode = chainOut ? nodesRef.current.find((n) => n.id === chainOut.target) : null;
      const basePos = succNode
        ? { x: (masterNode.position.x + succNode.position.x) / 2, y: (masterNode.position.y + succNode.position.y) / 2 }
        : { x: masterNode.position.x + 330, y: masterNode.position.y };

      // 尾插跟踪：tail=当前链尾节点，tailEdge=链尾→后继的边（首次为主镜→后继的原始边）
      let tailId = masterNode.id;
      let tailEdge = chainOut || null;
      let insertIndex = 0;

      for (const s of groupShots) {
        const purpose = s.purpose || '连接';
        const dup = existing.some((n) => n.data?.purpose === purpose && (n.data?.content || '') === (s.content || ''));
        if (dup) continue;
        insertIndex++;

        const position = { x: basePos.x, y: basePos.y + (insertIndex - 1) * 110 };
        // 新连接镜只绑定 AI 明确返回的本镜资产；与前镜的画面连续性由链式参考边负责。
        const assetRefNodes = resolveShotAssetReferenceNodes(scriptNodeId, s);
        const nodeData = {
          nodeKind: 'storyboardShot',
          name: `连接镜·${purpose}`,
          shotNumber: afterNum + insertIndex / 10,
          purpose,
          afterMasterShotNumber: afterNum,
          shotSize: s.shotSize || '',
          reason: s.reason || '',
          content: s.content || '',
          imagePrompt: s.imagePrompt || s.content || '',
          ...buildStoryboardDraftData(s),
          aspectRatio,
          sourceScriptNodeId: scriptNodeId,
          ...buildShotReferencePatch(assetRefNodes),
        };
        const tempId = `node-${counter++}`;
        const realId = await createPersistedAtomNode('storyboardShot', nodeData, position, tempId);
        createdCount++;

        // 边重连：拆掉 链尾→后继，改 链尾→C→后继
        if (tailEdge) await removeReferenceEdge(tailEdge);
        await addReferenceEdge(tailId, realId);
        for (const assetNode of assetRefNodes) {
          await addReferenceEdge(assetNode.id, realId);
        }
        let nextTailEdge = null;
        if (succNode) {
          const e2 = await addReferenceEdge(realId, succNode.id);
          nextTailEdge = e2 ? { id: e2 } : null;
        }
        tailId = realId;
        tailEdge = nextTailEdge;
      }
    }
    setNodeIdCounter(counter);
    if (createdCount > 0) {
      await arrangeStoryboardShots(scriptNodeId, { recordHistory: false, silent: true });
    }
    return createdCount;
  }, [
    nodeIdCounter,
    recordNow,
    resolveShotAssetReferenceNodes,
    buildShotReferencePatch,
    createPersistedAtomNode,
    addReferenceEdge,
    removeReferenceEdge,
    arrangeStoryboardShots,
    setNodeIdCounter,
  ]);

  // 为旧画布或人工修改后的镜头补齐资产参考。只新增缺失连线，不删除手工参考。
  const handleSyncShotReferences = useCallback(async (scriptNodeId) => {
    const scriptNode = nodesRef.current.find((node) => String(node.id) === String(scriptNodeId));
    if (!scriptNode) return;
    if (scriptNode.data?.syncingShotReferences) return;

    const shotNodes = nodesRef.current
      .filter((node) =>
        String(node.data?.sourceScriptNodeId) === String(scriptNodeId)
        && node.data?.nodeKind === 'storyboardShot'
      )
      .sort((left, right) => Number(left.data?.shotNumber || 0) - Number(right.data?.shotNumber || 0));
    if (shotNodes.length === 0) {
      toast.warning('当前还没有故事板镜头');
      return;
    }

    const masterNodes = shotNodes.filter((node) => !!node.data?.masterType);
    const existingPairs = new Set(
      edgesRef.current
        .filter((edge) => edge.data?.connectionType === 'reference')
        .map((edge) => `${String(edge.source)}=>${String(edge.target)}`)
    );
    let addedCount = 0;

    setNodes((current) => current.map((node) => (
      String(node.id) === String(scriptNodeId)
        ? { ...node, data: { ...node.data, syncingShotReferences: true } }
        : node
    )));
    recordNow();

    try {
      for (const shotNode of shotNodes) {
        let computedRefs = resolveShotAssetReferenceNodes(scriptNodeId, shotNode.data, {
          allowLegacyTextFallback: true,
        });
        if (!shotNode.data?.masterType && !hasStructuredShotReferences(shotNode.data)) {
          const afterNumber = Number(shotNode.data?.afterMasterShotNumber);
          const previousMaster = masterNodes.find(
            (node) => Number(node.data?.shotNumber) === afterNumber
          );
          const nextMaster = masterNodes.find(
            (node) => Number(node.data?.shotNumber) > afterNumber
          );
          computedRefs = uniqueNodesById([
            ...computedRefs,
            ...getSharedAssetReferenceNodes(previousMaster?.id, nextMaster?.id),
          ]);
        }

        const manualRefs = getIncomingAssetReferenceNodes(shotNode.id);
        const allRefs = uniqueNodesById([...manualRefs, ...computedRefs]);
        for (const assetNode of allRefs) {
          const pairKey = `${String(assetNode.id)}=>${String(shotNode.id)}`;
          if (!existingPairs.has(pairKey)) {
            await addReferenceEdge(assetNode.id, shotNode.id);
            existingPairs.add(pairKey);
            addedCount++;
          }
        }
        await handleUpdateNode(shotNode.id, (previousData) => ({
          ...previousData,
          ...buildShotReferencePatch(allRefs),
        }));
      }
      if (addedCount > 0) {
        toast.success(`已为镜头补齐 ${addedCount} 条参考连线`);
      } else {
        toast.info('镜头参考已经是最新状态');
      }
    } catch (error) {
      console.error('[Canvas] 同步镜头参考失败:', error);
      toast.error('同步镜头参考失败: ' + (error.message || '请重试'));
    } finally {
      setNodes((current) => current.map((node) => (
        String(node.id) === String(scriptNodeId)
          ? { ...node, data: { ...node.data, syncingShotReferences: false } }
          : node
      )));
    }
  }, [
    toast,
    recordNow,
    setNodes,
    resolveShotAssetReferenceNodes,
    getSharedAssetReferenceNodes,
    getIncomingAssetReferenceNodes,
    addReferenceEdge,
    handleUpdateNode,
    buildShotReferencePatch,
  ]);

  // AI 粗稿之外允许直接补空白镜头，仍复用 storyboardShot 的保存、连线和生图能力。
  const handleCreateBlankStoryboardShot = useCallback(async (scriptNodeId, { aspectRatio = '16:9' } = {}) => {
    const scriptNode = nodesRef.current.find((n) => n.id === scriptNodeId);
    if (!scriptNode) return;
    if (isTempNodeId(scriptNodeId)) {
      toast.warning('节点保存中，请稍候再试');
      return;
    }

    const existingShots = nodesRef.current
      .filter((n) => n.data?.sourceScriptNodeId === scriptNodeId && n.data?.nodeKind === 'storyboardShot')
      .sort((a, b) => (Number(a.data?.shotNumber) || 0) - (Number(b.data?.shotNumber) || 0));
    const tailNode = existingShots[existingShots.length - 1] || null;
    const shotNumber = existingShots.reduce(
      (max, current) => Math.max(max, Math.ceil(Number(current.data?.shotNumber) || 0)),
      0,
    ) + 1;
    const position = tailNode
      ? {
        x: tailNode.position.x,
        y: tailNode.position.y + STORYBOARD_SHOT_FALLBACK_HEIGHT + STORYBOARD_SHOT_GAP,
      }
      : { x: scriptNode.position.x + 420, y: scriptNode.position.y - 40 };
    const nodeData = {
      nodeKind: 'storyboardShot',
      name: `空白镜头#${shotNumber}`,
      shotNumber,
      masterType: '戏点',
      shotSize: '中景',
      pov: '',
      reason: '',
      content: '',
      imagePrompt: '',
      ...buildStoryboardDraftData(),
      aspectRatio: aspectRatio || scriptNode.data?.aspectRatio || '16:9',
      sourceScriptNodeId: scriptNodeId,
      refCharacterIds: [],
      refSceneIds: [],
      refPropIds: [],
      refCharacterNames: [],
      refSceneNames: [],
      refPropNames: [],
    };

    recordNow();
    const tempId = `node-${nodeIdCounter}`;
    setNodeIdCounter((current) => Math.max(current, nodeIdCounter + 1));
    try {
      const realId = await createPersistedAtomNode('storyboardShot', nodeData, position, tempId);
      await addReferenceEdge(tailNode?.id || scriptNodeId, realId);
      handleUpdateNode(scriptNodeId, (prevData) => ({
        ...prevData,
        masterShotNodeIds: [...new Set([...(prevData.masterShotNodeIds || []), realId])],
      }));
      await arrangeStoryboardShots(scriptNodeId, { recordHistory: false, silent: true });
      toast.success('已新建空白故事板镜头');
    } catch (error) {
      console.error('[Canvas] 新建空白故事板镜头失败:', error);
      toast.error('新建空白镜头失败: ' + (error.message || '请重试'));
    }
  }, [
    nodeIdCounter,
    recordNow,
    createPersistedAtomNode,
    addReferenceEdge,
    handleUpdateNode,
    arrangeStoryboardShots,
    setNodeIdCounter,
    toast,
  ]);

  // 分镜文本任务共用同一套可恢复轮询。taskId 持久化在剧本节点中，刷新后只续查，不重新提交。
  const startStoryboardTaskPolling = useCallback((
    nodeId,
    taskId,
    taskType,
    aspectRatio = '16:9',
  ) => {
    const key = String(nodeId);
    if (!taskId || storyboardPollingStopsRef.current.has(key)) return;

    const clearPollingHandle = () => {
      storyboardPollingStopsRef.current.delete(key);
    };
    const finishTask = (patch = {}) => {
      handleUpdateNode(nodeId, (previousData) => ({
        ...previousData,
        isGenerating: false,
        activeTaskId: null,
        activeTaskType: null,
        activeTaskAspectRatio: null,
        generationStartedAt: null,
        generatingStatus: null,
        ...patch,
      }));
    };

    const stop = startTaskPolling(taskId, {
      onProgress: ({ currentStageText, progress }) => {
        handleUpdateNode(nodeId, (previousData) => ({
          ...previousData,
          isGenerating: true,
          activeTaskId: taskId,
          activeTaskType: taskType,
          activeTaskAspectRatio: aspectRatio,
          generatingStatus: currentStageText || (
            taskType === 'connecting' ? '连接镜评估中...' : '故事板粗稿生成中...'
          ),
          generateProgress: typeof progress === 'number'
            ? progress
            : previousData.generateProgress,
          generationError: null,
        }));
      },
      onSuccess: async (result) => {
        clearPollingHandle();
        try {
          const shots = parseShotArrayResult(result);
          if (taskType === 'connecting') {
            const createdCount = await materializeConnectingShots(
              nodeId,
              shots,
              aspectRatio,
            );
            finishTask({ generateProgress: 100, generationError: null });
            if (createdCount > 0) {
              toast.success(`连接镜生成完成，新增 ${createdCount} 个`);
            } else {
              toast.info('导演判断：当前主镜衔接足够，无需新增连接镜');
            }
            return;
          }

          if (!shots.length) throw new Error('AI 未返回故事板粗稿');
          const existingMasterShots = nodesRef.current.filter((node) =>
            String(node.data?.sourceScriptNodeId) === String(nodeId)
            && node.data?.masterType
          );
          if (existingMasterShots.length > 0) {
            finishTask({ generateProgress: 100, generationError: null });
            toast.info(`任务已完成，画布中已有 ${existingMasterShots.length} 个故事板粗稿`);
            return;
          }
          await materializeMasterShots(nodeId, shots, aspectRatio);
          finishTask({ generateProgress: 100, generationError: null });
          toast.success(`故事板粗稿完成，共 ${shots.length} 个`);
        } catch (error) {
          console.error('[Canvas] 分镜任务结果落画布失败:', error);
          finishTask({ generationError: error.message || '结果落画布失败' });
          toast.error('分镜结果落画布失败: ' + (error.message || '请重试'));
        }
      },
      onError: (message) => {
        clearPollingHandle();
        finishTask({ generationError: message || '任务执行失败' });
        toast.error(
          taskType === 'connecting'
            ? `连接镜生成失败: ${message || '请重试'}`
            : `故事板粗稿生成失败: ${message || '请重试'}`,
        );
      },
      onPollingInterrupted: (message) => {
        clearPollingHandle();
        handleUpdateNode(nodeId, (previousData) => ({
          ...previousData,
          isGenerating: false,
          activeTaskId: taskId,
          activeTaskType: taskType,
          activeTaskAspectRatio: aspectRatio,
          generatingStatus: '任务仍在后台运行，可继续查询',
          generationError: message || '查询暂时中断',
        }));
      },
    });
    storyboardPollingStopsRef.current.set(key, stop);
  }, [
    handleUpdateNode,
    materializeConnectingShots,
    materializeMasterShots,
    toast,
  ]);

  const handleResumeStoryboardTask = useCallback((nodeId) => {
    const node = nodesRef.current.find((item) => String(item.id) === String(nodeId));
    const taskId = node?.data?.activeTaskId;
    if (!taskId) {
      toast.info('当前没有可恢复的分镜任务');
      return;
    }
    handleUpdateNode(nodeId, (previousData) => ({
      ...previousData,
      isGenerating: true,
      generatingStatus: '正在恢复任务查询...',
      generationError: null,
    }));
    startStoryboardTaskPolling(
      nodeId,
      taskId,
      node.data?.activeTaskType || 'master',
      node.data?.activeTaskAspectRatio || node.data?.aspectRatio || '16:9',
    );
  }, [handleUpdateNode, startStoryboardTaskPolling, toast]);

  // 生成主镜：提交 SCRIPT_TO_MASTER_SHOTS 任务 → 轮询（进度上屏）→ 成功落画布
  const handleGenerateMasterShots = useCallback(async (nodeId, { aspectRatio = '16:9' } = {}) => {
    const node = nodesRef.current.find((n) => n.id === nodeId);
    if (!node) return;
    const script = node.data?.scriptText || '';
    if (!script.trim()) { toast.warning('请先填写剧本正文'); return; }
    if (node.data?.activeTaskId) {
      handleResumeStoryboardTask(nodeId);
      return;
    }
    if (storyboardSubmittingRef.current.has(String(nodeId))) return;
    if (isTempNodeId(nodeId)) { toast.warning('节点保存中，请稍候再试'); return; }

    // 已生成过主镜则不重复（用户删除后可再生成）
    const existingMasters = nodesRef.current.filter((n) => n.data?.sourceScriptNodeId === nodeId && n.data?.masterType);
    if (existingMasters.length > 0) {
      toast.warning(`已存在 ${existingMasters.length} 个故事板粗稿，如需重新生成请先删除`);
      return;
    }

    // 收集连入参考的角色/场景/道具摘要（name/description 即可）
    const incoming = edgesRef.current.filter((e) => e.target === nodeId && e.data?.connectionType === 'reference');
    const bucket = { character: [], scene: [], prop: [] };
    for (const e of incoming) {
      const rn = nodesRef.current.find((n) => n.id === e.source);
      const t = rn?.data?.assetType;
      if (!rn || !bucket[t]) continue;
      bucket[t].push({ name: rn.data?.name || '', description: rn.data?.description || '' });
    }

    storyboardSubmittingRef.current.add(String(nodeId));
    handleUpdateNode(nodeId, (prevData) => ({
      ...prevData,
      isGenerating: true,
      generatingStatus: '提交故事板粗稿任务...',
      generateProgress: 5,
      generationError: null,
      aspectRatio,
    }));

    try {
      const { taskId } = await submitTask('SCRIPT_TO_MASTER_SHOTS', {
        script,
        characters: bucket.character,
        scenes: bucket.scene,
        props: bucket.prop,
        aspectRatio,
        projectId: projectId || null,
      }, { projectId: projectId || undefined });
      handleUpdateNode(nodeId, (previousData) => ({
        ...previousData,
        isGenerating: true,
        activeTaskId: taskId,
        activeTaskType: 'master',
        activeTaskAspectRatio: aspectRatio,
        generationStartedAt: Date.now(),
        generatingStatus: '故事板粗稿生成中...',
      }));
      startStoryboardTaskPolling(nodeId, taskId, 'master', aspectRatio);
    } catch (e) {
      console.error('[Canvas] 提交故事板粗稿任务失败:', e);
      handleUpdateNode(nodeId, (prevData) => ({
        ...prevData,
        isGenerating: false,
        activeTaskId: null,
        activeTaskType: null,
        activeTaskAspectRatio: null,
        generationStartedAt: null,
        generatingStatus: null,
        generationError: e.message || '提交任务失败',
      }));
      toast.error('提交故事板粗稿任务失败: ' + (e.message || '请重试'));
    } finally {
      storyboardSubmittingRef.current.delete(String(nodeId));
    }
  }, [
    projectId,
    toast,
    handleUpdateNode,
    handleResumeStoryboardTask,
    startStoryboardTaskPolling,
  ]);

  // 生成连接镜：提交 SCRIPT_TO_CONNECTING_SHOTS 任务（带当前画布主镜摘要）→ 成功插入主镜之后
  const handleGenerateConnectingShots = useCallback(async (nodeId) => {
    const node = nodesRef.current.find((n) => n.id === nodeId);
    if (!node) return;
    const script = node.data?.scriptText || '';
    if (!script.trim()) { toast.warning('请先填写剧本正文'); return; }
    if (node.data?.activeTaskId) {
      handleResumeStoryboardTask(nodeId);
      return;
    }
    if (storyboardSubmittingRef.current.has(String(nodeId))) return;
    if (isTempNodeId(nodeId)) { toast.warning('节点保存中，请稍候再试'); return; }

    const masterNodes = nodesRef.current
      .filter((n) => n.data?.sourceScriptNodeId === nodeId && n.data?.masterType)
      .sort((a, b) => (a.data?.shotNumber || 0) - (b.data?.shotNumber || 0));
    if (masterNodes.length === 0) { toast.warning('请先生成故事板粗稿'); return; }
    if (masterNodes.some((n) => isTempNodeId(n.id))) { toast.warning('故事板粗稿保存中，请稍候再试'); return; }

    const masterShots = masterNodes.map((n) => ({
      shotNumber: n.data.shotNumber,
      masterType: n.data.masterType,
      content: n.data.content || '',
      shotSize: n.data.shotSize || '',
      storyPurpose: n.data.storyPurpose || '',
      cameraAngle: n.data.cameraAngle || '',
      cameraMovement: n.data.cameraMovement || '',
      composition: n.data.composition || '',
      subjectPositions: n.data.subjectPositions || '',
      axisType: n.data.axisType || '',
      axisSide: n.data.axisSide || '',
      screenDirection: n.data.screenDirection || '',
      lookDirection: n.data.lookDirection || '',
      axisStatus: n.data.axisStatus || '',
      axisReason: n.data.axisReason || '',
      informationControl: n.data.informationControl || '',
      sound: n.data.sound || '',
      continuity: n.data.continuity || '',
      notes: n.data.notes || '',
      characters: n.data.refCharacterNames || [],
      scenes: n.data.refSceneNames || [],
      props: n.data.refPropNames || [],
    }));
    const aspectRatio = node.data?.aspectRatio || '16:9';
    const bucket = { character: [], scene: [], prop: [] };
    for (const assetNode of getScriptReferenceAssetNodes(nodeId)) {
      const assetType = assetNode.data?.assetType;
      if (!bucket[assetType]) continue;
      bucket[assetType].push({
        name: assetNode.data?.name || '',
        description: assetNode.data?.description || '',
      });
    }

    storyboardSubmittingRef.current.add(String(nodeId));
    handleUpdateNode(nodeId, (prevData) => ({
      ...prevData,
      isGenerating: true,
      generatingStatus: '提交连接镜任务...',
      generateProgress: 5,
      generationError: null,
    }));

    try {
      const { taskId } = await submitTask('SCRIPT_TO_CONNECTING_SHOTS', {
        script,
        masterShots,
        characters: bucket.character,
        scenes: bucket.scene,
        props: bucket.prop,
        aspectRatio,
        projectId: projectId || null,
      }, { projectId: projectId || undefined });
      handleUpdateNode(nodeId, (previousData) => ({
        ...previousData,
        isGenerating: true,
        activeTaskId: taskId,
        activeTaskType: 'connecting',
        activeTaskAspectRatio: aspectRatio,
        generationStartedAt: Date.now(),
        generatingStatus: '连接镜评估中...',
      }));
      startStoryboardTaskPolling(nodeId, taskId, 'connecting', aspectRatio);
    } catch (e) {
      console.error('[Canvas] 提交连接镜任务失败:', e);
      handleUpdateNode(nodeId, (prevData) => ({
        ...prevData,
        isGenerating: false,
        activeTaskId: null,
        activeTaskType: null,
        activeTaskAspectRatio: null,
        generationStartedAt: null,
        generatingStatus: null,
        generationError: e.message || '提交任务失败',
      }));
      toast.error('提交连接镜任务失败: ' + (e.message || '请重试'));
    } finally {
      storyboardSubmittingRef.current.delete(String(nodeId));
    }
  }, [
    getScriptReferenceAssetNodes,
    handleResumeStoryboardTask,
    handleUpdateNode,
    projectId,
    startStoryboardTaskPolling,
    toast,
  ]);

  handleSmartMatchRef.current = handleScriptSmartMatch;
  handleGenerateMasterShotsRef.current = handleGenerateMasterShots;
  handleGenerateConnectingShotsRef.current = handleGenerateConnectingShots;
  handleCreateBlankStoryboardShotRef.current = handleCreateBlankStoryboardShot;
  handleSyncShotReferencesRef.current = handleSyncShotReferences;
  handleArrangeStoryboardRef.current = arrangeStoryboardShots;
  handleResumeStoryboardTaskRef.current = handleResumeStoryboardTask;

  React.useEffect(() => {
    storyboardRecoveryDoneRef.current = false;
    canvasGenerationRecoveryDoneRef.current = false;
    storyboardPollingStopsRef.current.forEach((stop) => stop?.());
    storyboardPollingStopsRef.current.clear();
  }, [canvasId]);

  // 画布恢复后续查同一个后台任务；旧数据只有 loading 没有 taskId 时清理脏状态，避免永久转圈。
  React.useEffect(() => {
    if (storyboardRecoveryDoneRef.current || nodes.length === 0) return;
    const scriptNodes = nodesRef.current.filter(
      (node) => node.data?.nodeKind === 'scriptToStoryboard',
    );
    if (scriptNodes.length === 0) return;
    storyboardRecoveryDoneRef.current = true;

    scriptNodes.forEach((node) => {
      const taskId = node.data?.activeTaskId;
      if (taskId) {
        handleUpdateNode(node.id, (previousData) => ({
          ...previousData,
          isGenerating: true,
          generatingStatus: '正在恢复任务查询...',
          generationError: null,
        }));
        startStoryboardTaskPolling(
          node.id,
          taskId,
          node.data?.activeTaskType || 'master',
          node.data?.activeTaskAspectRatio || node.data?.aspectRatio || '16:9',
        );
        return;
      }
      if (node.data?.isGenerating) {
        handleUpdateNode(node.id, (previousData) => ({
          ...previousData,
          isGenerating: false,
          generatingStatus: null,
          generationError: '上次任务缺少可恢复的任务编号，请重新生成',
        }));
      }
    });
  }, [
    handleUpdateNode,
    nodes.length,
    startStoryboardTaskPolling,
  ]);

  // 普通图片/视频节点恢复：有 taskId 时续查同一任务；旧版本仅留下 loading 时结束脏状态。
  React.useEffect(() => {
    if (canvasGenerationRecoveryDoneRef.current || nodes.length === 0) return undefined;
    const timer = window.setTimeout(() => {
      if (canvasGenerationRecoveryDoneRef.current) return;
      canvasGenerationRecoveryDoneRef.current = true;

      nodesRef.current.forEach((node) => {
        if (node.data?.nodeKind === 'scriptToStoryboard') return;

        if (
          node.data?.legacyType === 'generate'
          && node.data?.activeTaskId
          && node.data?.activeTaskKind
        ) {
          resumeGenerationTask(node, handleUpdateNode);
          return;
        }

        if (node.data?.isGenerating || node.data?.isGeneratingPrompts) {
          handleUpdateNode(node.id, (previousData) => ({
            ...previousData,
            isGenerating: false,
            isGeneratingPrompts: false,
            generatingStatus: previousData?.generatingStatus === 'completed'
              ? 'completed'
              : 'interrupted',
            errorMessage: previousData?.errorMessage || '上次任务缺少可恢复的任务编号，请重新生成',
          }));
        }
      });
    }, 500);

    return () => window.clearTimeout(timer);
  }, [
    handleUpdateNode,
    nodes.length,
    resumeGenerationTask,
  ]);

  React.useEffect(() => () => {
    storyboardPollingStopsRef.current.forEach((stop) => stop?.());
    storyboardPollingStopsRef.current.clear();
  }, []);

  // 检测是否有正在进行的批量扩展任务（后端存库，前端只轮询状态）
  React.useEffect(() => {
    const pendingTaskId = localStorage.getItem('batchExpandTaskId');
    const pendingEpisodeId = localStorage.getItem('batchExpandEpisodeId');
    if (!pendingTaskId || !pendingEpisodeId || String(pendingEpisodeId) !== String(episodeId)) return;

    // 有正在进行的任务，显示蒙层并轮询
    const interval = setInterval(async () => {
      try {
        const status = await authenticatedFetch(`${API_HOST}/api/task/status?taskId=${pendingTaskId}`);
        if (status.status === 'COMPLETED' || status.status === 'success') {
          clearInterval(interval);
          localStorage.removeItem('batchExpandTaskId');
          localStorage.removeItem('batchExpandEpisodeId');
          toast.success('9宫格生成完成，正在刷新画布...');
          setTimeout(() => window.location.reload(), 500);
        } else if (status.status === 'FAILED' || status.status === 'failed' || status.status === 'not_found') {
          clearInterval(interval);
          localStorage.removeItem('batchExpandTaskId');
          localStorage.removeItem('batchExpandEpisodeId');
          if (status.status === 'not_found') {
            toast.warning('任务已过期，请重新操作');
          } else {
            toast.error('9宫格生成失败: ' + (status.errorMessage || '未知错误'));
          }
        }
      } catch (e) {
        console.error('轮询任务状态失败:', e);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [episodeId, toast]);

  // 一键整理画布
  const handleAutoLayout = useCallback(async () => {
    recordNow(); // 自动布局算一步
    const currentNodes = nodesRef.current;
    const resourceNodes = [];
    const gridNodes = [];
    const otherNodes = [];

    currentNodes.forEach(n => {
      // 片4：分组内子节点跟随分组框移动，不参与单独整理（分组框本身按 other 排布）
      if (n.parentId) return;
      const kind = n.data?.nodeKind;
      if (isNodeType(n, 'resource') || kind === 'resource') {
        resourceNodes.push(n);
      } else if (isNodeType(n, 'storyboardGrid') || kind === 'storyboardGrid') {
        gridNodes.push(n);
      } else {
        otherNodes.push(n);
      }
    });

    // 资产排序：角色→场景→道具
    const assetOrder = { character: 0, scene: 1, prop: 2 };
    resourceNodes.sort((a, b) => {
      const oa = assetOrder[a.data?.assetType] ?? 9;
      const ob = assetOrder[b.data?.assetType] ?? 9;
      return oa - ob;
    });

    // 九宫格按 shotNumber 排序
    gridNodes.sort((a, b) => (a.data?.shotNumber || 0) - (b.data?.shotNumber || 0));

    const updates = [];

    // 资产节点：左侧单列
    resourceNodes.forEach((n, i) => {
      updates.push({ id: n.id, position: { x: 50, y: 100 + i * 450 } });
    });

    // 九宫格节点：右侧网格，每行3个
    const GRID_COLS = 3;
    gridNodes.forEach((n, i) => {
      const col = i % GRID_COLS;
      const row = Math.floor(i / GRID_COLS);
      updates.push({ id: n.id, position: { x: 500 + col * 560, y: 100 + row * 900 } });
    });

    // 其他节点：九宫格下方
    const gridRows = Math.ceil(gridNodes.length / GRID_COLS);
    const otherStartY = 100 + gridRows * 900 + 100;
    otherNodes.forEach((n, i) => {
      const col = i % 4;
      const row = Math.floor(i / 4);
      updates.push({ id: n.id, position: { x: 500 + col * 520, y: otherStartY + row * 400 } });
    });

    // 批量更新位置
    const updateById = new Map(updates.map((update) => [String(update.id), update.position]));
    setNodes((current) => {
      let changed = false;
      const next = current.map((node) => {
        const position = updateById.get(String(node.id));
        if (!position) return node;
        if (node.position.x === position.x && node.position.y === position.y) return node;
        changed = true;
        return { ...node, position };
      });
      if (!changed) return current;
      nodesRef.current = next;
      return next;
    });

    // 持久化到后端（显式 payload：此刻 getNodes 尚未提交最新位置）
    const nodeById = new Map(currentNodes.map((node) => [String(node.id), node]));
    updates.forEach((u) => {
      const node = nodeById.get(String(u.id));
      if (!node) return;
      saveQueue.markDirty(u.id, {
        id: u.id,
        canvasId,
        nodeType: node.data?.legacyType || node.type,
        positionX: u.position.x,
        positionY: u.position.y,
        data: JSON.stringify(stripNonSerializable(node.data)),
      });
    });
    saveQueue.flush();

    // 缩放到全览
    setTimeout(() => fitView({ padding: 0.1, duration: 300 }), 100);
    toast.success('画布已整理');
  }, [setNodes, recordNow, saveQueue, canvasId, fitView, toast]);

  // 新建文本节点（resource 节点的 text 类型，AtomEditPanel 可编辑 data.text）
  const handleAddTextNode = useCallback(() => {
    createNode("resource", {
      resourceType: "text",
      name: "文本",
      text: "",
    });
  }, [createNode]);

  // 片4 新建便签（stickyNote 独立节点类型，双击编辑文字，无连接端口）
  const handleAddStickyNote = useCallback(() => {
    createNode("stickyNote", {
      name: "便签",
      text: "",
      noteColor: "yellow",
    });
  }, [createNode]);

  // 片5 新建 AI 导演对话节点（atom + legacyType:'aiDirector'，连入节点作为对话上下文）
  const handleAddAiDirector = useCallback(() => {
    createNode("aiDirector", {
      nodeKind: "aiDirector",
      name: "AI 导演",
      messages: [],
    });
  }, [createNode]);

  // 工具栏动作（全部复用现有 handler）
  const toolbarActions = {
    onAddText: handleAddTextNode,
    onAddStickyNote: handleAddStickyNote,
    onAddAiDirector: handleAddAiDirector,
    onAddGroupFrame: createGroupFrame,
    onOpenLibrary: () => setShowResourceLibrary(true),
    onUpload: triggerFileSelect,
    onOpenUrl: () => setShowUrlDialog(true),
    onAddGenerateImage: () => createNode("generate", {
      generateType: "image",
      prompt: "",
      includeProjectStyle: true,
      isGenerating: false,
      result: null,
    }),
    onAddGenerateVideo: () => createNode("generate", {
      generateType: "video",
      prompt: "",
      projectId: projectId || null,
      aspectRatio: getProjectDefaultVideoRatio(projectId),
      isGenerating: false,
      result: null,
    }),
    onAddScriptToStoryboard: () => createNode("scriptToStoryboard", {
      nodeKind: "scriptToStoryboard",
      name: "剧本转分镜",
      scriptText: "",
      scriptSource: "input",
      aspectRatio: getProjectDefaultVideoRatio(projectId),
      projectId: projectId || null,
    }),
    onImportShots: () => setImportPanelSource('storyboard'),
    onImportMiniStoryboard: () => setImportPanelSource('mini'),
    onImportStoryboardBoard: () => setImportPanelSource('board'),
    onAutoLayout: handleAutoLayout,
    onUndo: handleUndo,
    onRedo: handleRedo,
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    onToggleNodeList: () => setShowNodeList((open) => !open),
    nodeListOpen: showNodeList,
  };

  // 菜单项 — 简化为 4 原子类型 + 基础操作
  const menuItems = [
    {
      icon: <FolderOpen size={16} />,
      label: "从资源库选择",
      action: () => {
        setShowResourceLibrary(true);
        closeContextMenu();
      },
    },
    {
      icon: <Upload size={16} />,
      label: "本地上传",
      action: () => {
        closeContextMenu();
        triggerFileSelect();
      },
    },
    {
      icon: <Link size={16} />,
      label: "URL 链接",
      action: () => {
        closeContextMenu();
        setShowUrlDialog(true);
      },
    },
    {
      icon: <StickyNote size={16} />,
      label: "新建便签",
      action: () => {
        handleAddStickyNote();
      },
    },
    {
      icon: <Bot size={16} />,
      label: "新建 AI 导演",
      action: () => {
        handleAddAiDirector();
      },
    },
    {
      icon: <Frame size={16} />,
      label: selectedNodes.length > 0 ? `新建分组框（归入选中 ${selectedNodes.length} 项）` : "新建分组框",
      action: () => {
        createGroupFrame();
      },
    },
    {
      icon: <Wand2 size={16} />,
      label: "新建生成",
      submenu: [
        {
          icon: <Image size={14} />,
          label: "生成图片",
          action: () => {
            createNode("generate", {
              generateType: "image",
              prompt: "",
              includeProjectStyle: true,
              isGenerating: false,
              result: null,
            });
          },
        },
        {
          icon: <Video size={14} />,
          label: "生成视频",
          action: () => {
            createNode("generate", {
              generateType: "video",
              prompt: "",
              projectId: projectId || null,
              aspectRatio: getProjectDefaultVideoRatio(projectId),
              isGenerating: false,
              result: null,
            });
          },
        },
      ],
    },
    {
      icon: <Film size={16} />,
      label: "导入分镜",
      action: () => {
        closeContextMenu();
        setImportPanelSource('storyboard');
      },
    },
    {
      icon: <ScrollText size={16} />,
      label: "剧本转分镜",
      action: () => {
        createNode("scriptToStoryboard", {
          nodeKind: "scriptToStoryboard",
          name: "剧本转分镜",
          scriptText: "",
          scriptSource: "input",
          aspectRatio: getProjectDefaultVideoRatio(projectId),
          projectId: projectId || null,
        });
      },
    },
    {
      icon: <Layers size={16} />,
      label: "导入小故事板",
      action: () => {
        closeContextMenu();
        setImportPanelSource('mini');
      },
    },
    {
      icon: <Clapperboard size={16} />,
      label: "导入导演故事台",
      action: () => {
        closeContextMenu();
        setImportPanelSource('board');
      },
    },
    {
      icon: <LayoutGrid size={16} />,
      label: "整理画布",
      action: () => {
        closeContextMenu();
        handleAutoLayout();
      },
    },
  ];

  return (
    <div className="w-full h-full relative" ref={reactFlowWrapper}>

      {/* 画布节点交互样式 */}
      <style>{`
        /* 节点容器允许 overflow，防止 Handle 被裁成半圆 */
        .react-flow__node {
          overflow: visible !important;
        }
        /* 拖拽中：阴影提升 */
        .react-flow__node.dragging > div {
          box-shadow: 0 12px 32px rgba(0,0,0,0.25);
        }
        /* 节点内按钮 hover */
        .react-flow__node button:not(:disabled) {
          transition: filter 0.15s ease;
        }
        .react-flow__node button:not(:disabled):hover {
          filter: brightness(1.15);
        }
        .react-flow__node button:not(:disabled):active {
          filter: brightness(0.9);
        }
        /* 连接线 hover */
        .react-flow__edge:hover .react-flow__edge-path {
          stroke-width: 3;
          filter: drop-shadow(0 0 4px var(--color-accent-glow));
        }
        /* Handle hover — 用伪元素做放大光晕，不改 Handle 自身尺寸/transform */
        .react-flow__handle {
          overflow: visible !important;
          z-index: 10;
          transition: background-color 0.15s ease;
        }
        .react-flow__handle::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 14px;
          height: 14px;
          transform: translate(-50%, -50%);
          border-radius: 50%;
          background: transparent;
          box-shadow: none;
          transition: width 0.15s ease, height 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
          pointer-events: none;
        }
        .react-flow__handle:hover {
          background-color: var(--color-accent-primary) !important;
        }
        .react-flow__handle:hover::after {
          width: 24px;
          height: 24px;
          background: var(--color-accent-light);
          box-shadow: 0 0 0 3px var(--color-accent-glow), 0 0 12px var(--color-accent-light);
        }
        /* P2-4 连接过程中：通过类型校验的合法目标 Handle 高亮 */
        .react-flow__handle.valid {
          background: var(--color-accent-primary) !important;
          border-color: var(--color-accent-primary) !important;
        }
        .react-flow__handle.valid::after {
          width: 24px;
          height: 24px;
          background: var(--color-accent-light);
          box-shadow: 0 0 0 3px var(--color-accent-glow);
        }

        /* ── P4 悬浮舞台视觉（纯 CSS，主题变量驱动） ── */
        /* 柔和径向光：accent 5% alpha 居中 */
        .canvas-stage-glow {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 1;
          background: radial-gradient(ellipse 55% 45% at 50% 42%, color-mix(in srgb, var(--color-accent-primary) 5%, transparent) 0%, transparent 70%);
        }
        /* 四角极轻 vignette */
        .canvas-stage-vignette {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 1;
          box-shadow: inset 0 0 140px rgba(0, 0, 0, 0.12), inset 0 0 36px rgba(0, 0, 0, 0.05);
        }
        /* 节点悬浮：挂在 .react-flow__node 上，避免逐组件改 transform */
        .react-flow__node > div {
          transition: transform 200ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        .react-flow__node:hover > div,
        .react-flow__node.dragging > div {
          transform: translateY(-4px) scale(1.02);
        }
        /* 生成中节点：accent 呼吸光环（动画只动 box-shadow） */
        @keyframes canvasNodeBreathing {
          0%, 100% {
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 12px 24px -8px rgba(0,0,0,0.5), 0 32px 64px -24px rgba(0,0,0,0.35), 0 0 0 0 var(--color-accent-glow);
          }
          50% {
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 12px 24px -8px rgba(0,0,0,0.5), 0 32px 64px -24px rgba(0,0,0,0.35), 0 0 0 5px var(--color-accent-glow), 0 0 28px 2px var(--color-accent-light);
          }
        }
        .canvas-node-card--generating {
          animation: canvasNodeBreathing 2s ease-in-out infinite;
        }
        /* 连线 hover 加粗过渡 */
        .react-flow__edge .react-flow__edge-path {
          transition: stroke-width 0.15s ease;
        }
        /* MiniMap / Controls 同款悬浮阴影 */
        .react-flow__minimap,
        .react-flow__controls {
          box-shadow: 0 12px 24px -8px rgba(0,0,0,0.5), 0 32px 64px -24px rgba(0,0,0,0.35) !important;
        }
        /* 生成设置弹框 details 展开时箭头旋转 */
        details[open] > summary .details-chevron {
          transform: rotate(180deg);
        }
        /* ── 片3 音频卡片：原生播放条尺寸收敛 ── */
        .atom-audio-player {
          width: 100%;
          height: 30px;
          display: block;
          border-radius: 8px;
          outline: none;
        }
        /* ── 片5 语义缩放（AtomNode / StoryboardEpisodeNode） ── */
        /* 简卡（zoom < 0.45）：CSS 隐藏编辑区/hover 条/标签行（不卸载 DOM，媒体元素不解码重建） */
        .atom-node .atom-detail {
          transition: opacity 150ms ease, max-height 150ms ease;
        }
        .atom-zoom-simple .atom-detail {
          opacity: 0;
          visibility: hidden;
          max-height: 0 !important;
          overflow: hidden !important;
          margin-top: 0 !important;
          margin-bottom: 0 !important;
          padding-top: 0 !important;
          padding-bottom: 0 !important;
          border-width: 0 !important;
          pointer-events: none;
        }
        .atom-zoom-simple .atom-media {
          min-height: 56px !important;
          max-height: 88px;
          overflow: hidden;
          transition: max-height 150ms ease;
        }
        .atom-zoom-simple .atom-media video {
          min-height: 0 !important;
          max-height: 88px;
        }
        /* 类型小徽章：仅简卡模式显示 */
        .atom-node .atom-simple-badge {
          display: none;
        }
        .atom-zoom-simple .atom-simple-badge {
          display: inline-flex;
        }
        /* 简卡保留连线能力：Handle 视觉弱化但不卸载 */
        .atom-zoom-simple .react-flow__handle,
        .episode-zoom-simple .react-flow__handle {
          opacity: 0.35;
        }
        /* 整集分镜节点：简卡只留标题 + 计数 */
        .episode-zoom-simple .episode-detail {
          display: none;
        }
        /* prefers-reduced-motion 降级：停动画，保留静态光环 */
        @media (prefers-reduced-motion: reduce) {
          .canvas-node-card--generating {
            animation: none;
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 12px 24px -8px rgba(0,0,0,0.5), 0 32px 64px -24px rgba(0,0,0,0.35), 0 0 0 3px var(--color-accent-glow) !important;
          }
          .react-flow__node > div {
            transition: none;
          }
          .react-flow__node:hover > div,
          .react-flow__node.dragging > div {
            transform: none;
          }
        }
      `}</style>

      {/* 批量扩展蒙层 */}

      <ReactFlow
        nodes={nodes}
        edges={edges}
        deleteKeyCode={null}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChangeCustom}
        onConnect={onConnect}
        onConnectStart={onConnectStart}
        onConnectEnd={onConnectEnd}
        onNodeDragStop={handleNodeDragStop}
        isValidConnection={isValidConnection}
        onNodeClick={(event, node) => {
          // 点击节点 → 展开该节点功能区，关闭其他（所有节点类型都支持）
          setExpandedNode(node.id, 'toggle');
        }}
        onPaneClick={(e) => {
          closeContextMenu();
          closeNodeContextMenu();
          setEdgeContextMenu(null);
          // 点击画布空白处关闭所有功能区
          setExpandedNode(null);
          if (showGenerateTypeMenu && !e.target.closest('.generate-type-menu')) {
            setShowGenerateTypeMenu(null);
          }
        }}
        onPaneContextMenu={onPaneContextMenu}
        onNodeContextMenu={onNodeContextMenu}
        onEdgeContextMenu={(event, edge) => {
          event.preventDefault();
          setEdgeContextMenu({ ...clampMenuToViewport(event.clientX, event.clientY, 150, 80), edge });
        }}
        onSelectionChange={onSelectionChange}
        onSelectionContextMenu={onSelectionContextMenu}
        nodeTypes={nodeTypes}
        selectionOnDrag={true}
        panOnDrag={isSpacePressed ? [0] : [1, 2]}
        panOnScroll={false}
        zoomOnScroll={true}
        selectionMode="partial"
        selectionKeyCode="Shift"
        multiSelectionKeyCode="Shift"
        nodesDraggable={!isSpacePressed}
        nodesConnectable={true}
        connectionRadius={30}
        elementsSelectable={true}
        onlyRenderVisibleElements={true}
        fitView={false}
        minZoom={0.05}
        maxZoom={2}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        defaultEdgeOptions={{
          type: 'default',
          animated: false,
          style: EDGE_STYLE_REFERENCE,
          deletable: true,
        }}
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          cursor: isSpacePressed ? 'grab' : 'default',
        }}
      >
        <Controls
          style={{
            button: {
              backgroundColor: 'var(--color-bg-secondary)',
              borderColor: 'var(--color-border-primary)',
              color: 'var(--color-text-primary)',
            }
          }}
        />
        <MiniMap
          pannable
          zoomable
          nodeColor={(node) => {
            // 节点分类色：主题变量色板，收敛为 4 色语义（结构/便签/资源/创作）
            if (node.type === 'groupFrame') return 'var(--color-text-muted)';
            if (node.type === 'stickyNote') return 'var(--color-warning)';
            if (isNodeType(node, 'resource')) return 'var(--color-blue-primary)';
            return 'var(--color-accent-primary)';
          }}
          nodeStrokeWidth={2}
          nodeStrokeColor="var(--color-bg-primary)"
          nodeBorderRadius={2}
          maskColor="var(--color-accent-glow)"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
          }}
        />
        {/* P4 悬浮舞台：双层背景 —— 大网格 faint 营造纵深 + 小点阵保持定位参照 */}
        <Background
          id="stage-grid"
          variant="lines"
          gap={120}
          size={1}
          color="var(--color-border-secondary)"
          style={{ opacity: 0.5 }}
        />
        <Background
          id="stage-dots"
          variant="dots"
          gap={12}
          size={1}
          color="var(--color-border-primary)"
        />
      </ReactFlow>

      {/* P4 悬浮舞台：柔和径向光 + 极轻四角 vignette（纯视觉层，不拦截事件） */}
      <div className="canvas-stage-glow" aria-hidden="true" />
      <div className="canvas-stage-vignette" aria-hidden="true" />

      {/* 创建工具栏（左上角悬浮） */}
      <CanvasToolbar actions={toolbarActions} />

      {/* 画布节点导航：覆盖式滑入，不改变 React Flow 视口尺寸。 */}
      <CanvasNodeListPanel
        isOpen={showNodeList}
        onClose={() => setShowNodeList(false)}
        nodes={nodes}
        selectedNodeIds={selectedNodes}
        onLocateNode={locateNodeFromList}
        onOpenNode={openNodeFromList}
      />

      {/* 空状态提示 */}
      {nodes.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center space-y-3 animate-in fade-in duration-300">
            <div className="text-sm font-bold" style={{ color: 'var(--color-text-secondary)' }}>
              画布为空
            </div>
            <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              使用左上角工具栏或右键点击画布创建节点
            </div>
            <button
              className="pointer-events-auto h-8 px-3 rounded-md text-xs font-bold transition-all duration-200 flex items-center gap-1.5 mx-auto"
              style={{ backgroundColor: 'var(--color-accent-primary)', color: 'var(--color-accent-contrast)' }}
              onClick={() => setImportPanelSource('storyboard')}
            >
              <Film size={14} />
              导入分镜
            </button>
          </div>
        </div>
      )}

      {/* 右键菜单 */}
      <PaneContextMenu
        menu={contextMenu}
        items={menuItems}
        hoveredSubmenu={hoveredSubmenu}
        onHoverSubmenu={setHoveredSubmenu}
        onClose={closeContextMenu}
      />

      {/* NodeEditPanel 已移除，编辑功能内联到各节点组件 */}

      {/* 生成类型选择菜单 */}
      {genTypeMenuAnim.mounted && effectiveGenTypeMenu && (
        <>
          <div
            className={`generate-type-menu absolute rounded-lg overflow-hidden ${genTypeMenuAnim.exiting ? "animate-out fade-out zoom-out-95 duration-150" : "animate-in fade-in zoom-in-95 duration-150"}`}
            style={{
              left: Math.max(8, Math.min(effectiveGenTypeMenu.x, window.innerWidth - 178)),
              top: Math.max(8, Math.min(effectiveGenTypeMenu.y, window.innerHeight - 290)),
              backgroundColor: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border-primary)',
              minWidth: '160px',
              zIndex: 99999,
              pointerEvents: 'auto',
              boxShadow: 'var(--shadow-dropdown)',
              position: 'fixed', // 锚定右键点，左上对齐（与其他菜单一致）
            }}
          >
          <div className="p-2">
            <div className="text-label mb-2 px-2">
              {effectiveGenTypeMenu.sourceNodeIds?.length > 1
                ? `使用 ${effectiveGenTypeMenu.sourceNodeIds.length} 个节点生成`
                : '选择操作'}
            </div>
            <button
              className="w-full px-3 py-2 flex items-center gap-2 text-left text-sm transition-all duration-200 rounded hover:bg-opacity-50"
              style={{
                color: 'var(--color-text-primary)',
                backgroundColor: 'transparent',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              onClick={() => createGenerateNodeFromConnection(effectiveGenTypeMenu.sourceNodeIds, 'image')}
            >
              <Image size={16} style={{ color: 'var(--color-blue-primary)' }} />
              <span>生成图片</span>
            </button>
            <button
              className="w-full px-3 py-2 flex items-center gap-2 text-left text-sm transition-all duration-200 rounded hover:bg-opacity-50"
              style={{
                color: 'var(--color-text-primary)',
                backgroundColor: 'transparent',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              onClick={() => createGenerateNodeFromConnection(effectiveGenTypeMenu.sourceNodeIds, 'video')}
            >
              <Video size={16} style={{ color: 'var(--color-green-primary)' }} />
              <span>生成视频</span>
            </button>
            <button
              className="w-full px-3 py-2 flex items-center gap-2 text-left text-sm transition-all duration-200 rounded hover:bg-opacity-50"
              style={{
                color: 'var(--color-text-primary)',
                backgroundColor: 'transparent',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              onClick={async () => {
                const sourceNodeIds = effectiveGenTypeMenu.sourceNodeIds;
                setShowGenerateTypeMenu(null);
                recordNow(); // 宫格节点+连线算一步

                // 计算位置（在选中节点右侧）
                const sourceNodes = nodes.filter(n => sourceNodeIds.includes(n.id));
                const avgY = sourceNodes.reduce((sum, n) => sum + n.position.y, 0) / sourceNodes.length;
                const maxX = Math.max(...sourceNodes.map(n => n.position.x));
                const gridPosition = { x: maxX + 300, y: avgY };

                const gridNodeData = {
                  nodeKind: "storyboardGrid",
                  rows: 2, cols: 2,
                  aspectRatio: "16:9",
                  prompts: [],
                  isGenerating: false,
                };

                const tempId = `node-${nodeIdCounter}`;
                const newNode = {
                  id: tempId,
                  type: "atom",
                  position: gridPosition,
                  data: {
                    ...gridNodeData,
                    legacyType: "storyboardGrid",
                    normalized: normalizeNodeData(gridNodeData, "storyboardGrid"),
                    onImageClick: (url, name, mediaType) => setImagePreview({ url, name, mediaType }),
                    onToggleExpand: (nid) => toggleNodeExpandRef.current?.(nid),
                    onUpdate: (nid, nd) => handleUpdateNodeRef.current?.(nid, nd),
                    onGenerate: (nid, fd) => handleNodeGenerateRef.current?.(nid, fd),
        onExportResult: (nid) => handleExportResultRef.current?.(nid),
                    onSplitGrid: (nid, params) => handleSplitGridRef.current?.(nid, params),
                  },
                };
                setNodes((nds) => [...nds, newNode]);
                setNodeIdCounter((c) => c + 1);

                try {
                  const savedNode = await onSaveNode({
                    canvasId, nodeType: "storyboardGrid",
                    positionX: gridPosition.x, positionY: gridPosition.y,
                    data: JSON.stringify(gridNodeData),
                  });
                  const savedId = savedNode?.id?.toString() || tempId;
                  if (savedNode?.id) {
                    commitTempNodeId(tempId, savedId);
                  }
                  // 自动连线：每个选中的资源节点 → 分镜生成节点（参考输入 = reference）
                  for (const srcId of sourceNodeIds) {
                    setEdges((eds) => addEdge({
                      source: srcId, target: savedId,
                      data: { connectionType: 'reference' },
                      style: EDGE_STYLE_REFERENCE,
                      animated: false,
                    }, eds));
                    await persistConnection({
                      source: srcId, target: savedId,
                      sourceHandle: "right", targetHandle: "left",
                      connectionType: 'reference',
                    });
                  }
                } catch (e) { console.error('创建分镜生成节点失败:', e); }
              }}
            >
              <Grid3x3 size={16} style={{ color: 'var(--color-accent-primary)' }} />
              <span>生成宫格图</span>
            </button>
            {/* 片4：选中节点归为分组（框选右键菜单；框按选中集包围盒创建） */}
            {effectiveGenTypeMenu.allSelectedNodeIds?.length > 0 && (
              <button
                className="w-full px-3 py-2 flex items-center gap-2 text-left text-sm transition-all duration-200 rounded hover:bg-opacity-50"
                style={{
                  color: 'var(--color-text-primary)',
                  backgroundColor: 'transparent',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                onClick={() => {
                  setShowGenerateTypeMenu(null);
                  createGroupFrame();
                }}
              >
                <Frame size={16} style={{ color: 'var(--color-purple-primary)' }} />
                <span>归为分组 ({effectiveGenTypeMenu.allSelectedNodeIds.length})</span>
              </button>
            )}
            {episodeId && effectiveGenTypeMenu.imagesForRoughCut?.length > 0 && (              <>
                <div className="h-px my-1" style={{ backgroundColor: 'var(--color-border-primary)' }} />
                <button
                  className="w-full px-3 py-2 flex items-center gap-2 text-left text-sm transition-all duration-200 rounded hover:bg-opacity-50"
                  style={{ color: 'var(--color-text-primary)', backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  onClick={() => {
                    const images = effectiveGenTypeMenu.imagesForRoughCut;
                    setShowGenerateTypeMenu(null);
                    setRoughCutConfirm({ images });
                  }}
                >
                  <ListVideo size={16} style={{ color: 'var(--color-green-primary)' }} />
                  <span>全部加入粗剪台 ({effectiveGenTypeMenu.imagesForRoughCut.length})</span>
                </button>
              </>
            )}
            <div className="h-px my-1" style={{ backgroundColor: 'var(--color-border-primary)' }} />
            <button
              className="w-full px-3 py-2 flex items-center gap-2 text-left text-sm transition-all duration-200 rounded hover:bg-opacity-50"
              style={{
                color: 'var(--color-error)',
                backgroundColor: 'transparent',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              onClick={() => {
                const nodeIds = effectiveGenTypeMenu.allSelectedNodeIds || effectiveGenTypeMenu.sourceNodeIds;
                setShowGenerateTypeMenu(null);
                setBatchDeleteConfirm({ nodeIds });
              }}
            >
              <Trash2 size={16} />
              <span>全部删除 ({(effectiveGenTypeMenu.allSelectedNodeIds || effectiveGenTypeMenu.sourceNodeIds)?.length})</span>
            </button>
            <button
              className="w-full px-3 py-2 flex items-center gap-2 text-left text-sm transition-all duration-200 rounded hover:bg-opacity-50"
              style={{
                color: 'var(--color-text-muted)',
                backgroundColor: 'transparent',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              onClick={() => setShowGenerateTypeMenu(null)}
            >
              <X size={16} />
              <span>取消</span>
            </button>
          </div>
        </div>
        </>
      )}

      {/* Seedance 合并菜单 */}

      {/* 节点右键菜单 */}
      <NodeContextMenu
        menu={nodeContextMenu}
        onClose={closeNodeContextMenu}
        actions={{
          expandNode,
          handleDeleteNode,
          setImagePreview,
          setShowGenerateTypeMenu,
          handleAddToRoughCut,
          canRoughCut: !!episodeId,
          handleSaveToResourceCenter,
          handleIngestNodeImage,
          handleRemoveFromGroup,
          screenToFlowPosition,
          reactFlowWrapperRef: reactFlowWrapper,
          nodes,
          handleSplitImage: (targetNode) => openSplitGridConfirm(targetNode),
          assetProvider: (() => {
            const pid = projectId || localStorage.getItem('currentProjectId');
            return pid ? getProjectVideoProvider(pid) : '';
          })(),
        }}
      />

      {/* 宫格拆分确认弹窗（行列数在弹窗内选择） */}
      {splitGridConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="fixed inset-0 animate-in fade-in duration-150"
            style={{ backgroundColor: 'var(--overlay-medium)' }}
            onClick={() => !splitGridConfirm.busy && setSplitGridConfirm(null)}
          />
          <div
            className="relative w-full max-w-sm rounded-2xl p-6 animate-in fade-in zoom-in-95 duration-200"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border-primary)',
              boxShadow: 'var(--shadow-modal)',
            }}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 rounded-full shrink-0" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                <Scissors size={18} style={{ color: 'var(--color-accent-primary)' }} />
              </div>
              <div>
                <h3 className="text-sm font-bold mb-1" style={{ color: 'var(--color-text-primary)' }}>确认宫格拆分</h3>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                  将把这张图按宫格拆分为多张图片，请选择行列数。
                </p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 mb-2">
              <label className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                行
                <input
                  type="number"
                  min={1}
                  max={6}
                  value={splitGridConfirm.rows}
                  disabled={splitGridConfirm.busy}
                  onChange={(e) => setSplitGridConfirm((c) => ({ ...c, rows: Math.max(1, Math.min(6, parseInt(e.target.value, 10) || 1)) }))}
                  className="w-16 h-9 rounded-md text-center text-sm outline-none"
                  style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                />
              </label>
              <span style={{ color: 'var(--color-text-muted)' }}>×</span>
              <label className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                列
                <input
                  type="number"
                  min={1}
                  max={6}
                  value={splitGridConfirm.cols}
                  disabled={splitGridConfirm.busy}
                  onChange={(e) => setSplitGridConfirm((c) => ({ ...c, cols: Math.max(1, Math.min(6, parseInt(e.target.value, 10) || 1)) }))}
                  className="w-16 h-9 rounded-md text-center text-sm outline-none"
                  style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                />
              </label>
            </div>
            <p className="text-center text-xs mb-5 font-mono-num" style={{ color: 'var(--color-text-muted)' }}>
              将产出 {splitGridConfirm.rows * splitGridConfirm.cols} 张图片
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={splitGridConfirm.busy}
                onClick={() => setSplitGridConfirm(null)}
                className="px-4 py-2 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
                style={{ color: 'var(--color-text-muted)' }}
              >
                取消
              </button>
              <button
                type="button"
                disabled={splitGridConfirm.busy}
                onClick={() => executeSplitImage(splitGridConfirm.node, splitGridConfirm.rows, splitGridConfirm.cols)}
                className="px-4 py-2 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                style={{ backgroundColor: 'var(--color-accent-primary)', color: 'var(--color-accent-contrast)' }}
              >
                {splitGridConfirm.busy && <Loader2 size={12} className="animate-spin" />}
                确认拆分
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 连线右键菜单 */}
      <EdgeContextMenu
        menu={edgeContextMenu}
        onDelete={async (edge) => {
          recordNow();
          try {
            setEdges((eds) => eds.filter((e) => e.id !== edge.id));
            await authenticatedFetch(`${API_HOST}/api/canvas/connection/${edge.id}`, { method: "DELETE" });
            toast.success("已删除连线");
          } catch (error) {
            console.error("删除连线失败:", error);
            toast.error("删除连线失败");
          }
        }}
        onClose={() => setEdgeContextMenu(null)}
      />

      <ResourceSelector
        isOpen={showResourceLibrary}
        onClose={() => setShowResourceLibrary(false)}
        onSelect={(resource) => {
          handleSelectFromLibrary(resource);
          setShowResourceLibrary(false);
        }}
        projectId={projectId}
        type="all"
        title="从资源库选择"
      />

      {/* 导入面板（分镜 / 小故事板 / 导演故事台，支持选集） */}
      {importPanelAnim.mounted && (
        <StoryboardImportPanel
          projectId={projectId}
          episodeId={episodeId}
          source={effectiveImportPanelSource}
          existingShotIds={existingShotIds}
          onImport={handleImportShots}
          onClose={() => setImportPanelSource(null)}
          exiting={importPanelAnim.exiting}
        />
      )}

      {/* 隐藏的文件输入（多选：每个文件一个占位节点） */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*,audio/*"
        multiple
        className="hidden"
        onChange={handleFileUpload}
      />

      {/* URL 输入对话框 */}
      {urlDialogAnim.mounted && (
        <div
          className={`absolute inset-0 bg-[var(--overlay-medium)] flex items-center justify-center z-50 ${urlDialogAnim.exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-150"}`}
          onClick={() => setShowUrlDialog(false)}
        >
          <div
            className={`rounded-lg p-6 w-96 ${urlDialogAnim.exiting ? "animate-out fade-out zoom-out-95 duration-150" : "animate-in fade-in zoom-in-95 duration-200"}`}
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border-primary)',
              boxShadow: 'var(--shadow-modal)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--color-text-primary)' }}>
              从 URL 添加资源
            </h3>
            <input
              type="text"
              placeholder="输入图片/视频/音频 URL..."
              className="w-full px-3 py-2 rounded text-sm mb-4"
              style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                border: '1px solid var(--color-border-primary)',
                color: 'var(--color-text-primary)',
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleUrlSubmit(e.target.value);
                }
              }}
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowUrlDialog(false)}
                className="px-4 py-2 rounded text-sm"
                style={{
                  backgroundColor: 'var(--color-bg-tertiary)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                取消
              </button>
              <button
                onClick={(e) => {
                  const input = e.target.parentElement.previousElementSibling;
                  handleUrlSubmit(input.value);
                }}
                className="px-4 py-2 rounded text-sm"
                style={{
                  backgroundColor: 'var(--color-blue-primary)',
                  color: 'white',
                }}
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 草图编辑器 */}
      <SketchEditorModal
        isOpen={!!sketchEditor}
        aspectRatio={sketchEditor?.aspectRatio || '16:9'}
        onSave={async (cosUrl) => {
          // 保存草图为资源节点
          const nodeData = {
            resourceType: "image",
            name: "草图",
            url: cosUrl,
            thumbnail: cosUrl,
          };
          await createNode("resource", nodeData);
          setSketchEditor(null);
        }}
        onClose={() => setSketchEditor(null)}
        title="画布草图"
      />

      {/* 动态分镜编辑器 */}
      <MotionStoryboardEditor
        isOpen={!!motionEditor}
        nodeData={motionEditor?.nodeData || {}}
        projectId={projectId}
        episodeId={episodeId}
        onSave={(updatedData) => {
          if (motionEditor?.nodeId) {
            handleUpdateNodeRef.current?.(motionEditor.nodeId, updatedData);
          }
          setMotionEditor(null);
        }}
        onClose={() => setMotionEditor(null)}
      />

      {/* 图片预览模态框 */}
      {imagePreviewAnim.mounted && effectiveImagePreview && (
        <>
          <div
            className={`fixed inset-0 bg-[var(--overlay-heavy)] flex items-center justify-center z-[100] ${imagePreviewAnim.exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-150"}`}
            onClick={() => {
              setImagePreview(null);
            }}
          >
          <div className={`relative max-w-[90vw] max-h-[90vh] flex flex-col ${imagePreviewAnim.exiting ? "animate-out fade-out zoom-out-95 duration-150" : "animate-in fade-in zoom-in-95 duration-200"}`}>
            {/* 关闭按钮 */}
            <button
              className="absolute top-4 right-4 p-2 rounded-lg transition-all duration-200 z-10"
              style={{
                backgroundColor: 'var(--overlay-medium)',
                color: 'white',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setImagePreview(null);
              }}
            >
              <X size={24} />
            </button>

            {/* 图片 / 视频预览（视频带控制条和声音） */}
            {isVideoPreview ? (
              <video
                src={effectiveImagePreview.url}
                controls
                autoPlay
                className="max-w-full max-h-[80vh] rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img
                src={effectiveImagePreview.url}
                alt={effectiveImagePreview.name || '预览'}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            )}

            {/* 底部操作栏 */}
            <div
              className="mt-4 px-6 py-3 rounded-lg flex items-center justify-between"
              style={{
                backgroundColor: 'var(--overlay-heavy)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-sm text-white truncate flex-1">
                {effectiveImagePreview.name || (isVideoPreview ? '视频预览' : '图片预览')}
              </div>
              <button
                className="ml-4 px-4 py-2 rounded-lg text-sm font-bold transition-all duration-200"
                style={{
                  backgroundColor: 'var(--color-blue-primary)',
                  color: 'white',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  downloadFile(
                    effectiveImagePreview.url,
                    effectiveImagePreview.name || (isVideoPreview ? 'video.mp4' : 'image.png')
                  );
                }}
              >
                {isVideoPreview ? '下载视频' : '下载图片'}
              </button>
            </div>
          </div>
        </div>
        </>
      )}

      {/* 批量加入粗剪台确认弹窗 */}
      <ConfirmDialog
        isOpen={!!roughCutConfirm}
        onClose={() => setRoughCutConfirm(null)}
        onConfirm={async () => {
          const images = roughCutConfirm?.images;
          setRoughCutConfirm(null);
          await handleBatchAddToRoughCut(images);
        }}
        title="加入粗剪台"
        message={`确定将选中的 ${roughCutConfirm?.images?.length || 0} 张图片全部加入粗剪台吗？每张默认 3 秒，依次排列在图片轨末尾。`}
        confirmLabel="确定加入"
        cancelLabel="取消"
        variant="info"
      />

      {/* 批量删除确认弹窗 */}
      <ConfirmDialog
        isOpen={!!batchDeleteConfirm}
        onClose={() => setBatchDeleteConfirm(null)}
        onConfirm={async () => {
          const nodeIds = batchDeleteConfirm?.nodeIds;
          setBatchDeleteConfirm(null);
          if (nodeIds?.length) await handleBatchDeleteNodes(nodeIds);
        }}
        title="删除节点"
        message={`确定删除选中的 ${batchDeleteConfirm?.nodeIds?.length || 0} 个节点吗？此操作不可撤销。`}
        confirmLabel="确定删除"
        cancelLabel="取消"
        variant="danger"
      />

      {/* 九宫格模板选择弹窗 */}
    </div>
  );
};

const NodeCanvas = (props) => {
  return (
    <ReactFlowProvider>
      <NodeCanvasInner {...props} />
    </ReactFlowProvider>
  );
};

export default NodeCanvas;
